import { HttpClient, HttpContext, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SKIP_DUPLICATE_CHECK } from '@insite-store360/auth';
import {
  PRODUCT_URL,
  RawData_URL,
  Status,
  SURVEY_URL,
  SurveyLine,
  USER_URL
} from '@insite-store360/shared';
import { BehaviorSubject, catchError, map, Observable } from 'rxjs';
import { AsyncExportService } from './async-export.service';
import type { AsyncExportApiConfig } from './async-export.types';
import { ErrorHandlerService } from './error-handler.service';
import type { ExcelExportProgressSlot } from './excel-export-progress.service';

@Injectable({
  providedIn: 'root',
})
export class SurveyServiceService {
  /** Azure blob container for Store360 store images and videos */
  public static readonly STORE360_BLOB_CONTAINER = 'store360';

  private readonly SURVEY_SERVICE_URL = `${SURVEY_URL}`;
  private readonly USER_URL = `${USER_URL}`;
  private readonly PRODUCT_SERVICE_URL = `${PRODUCT_URL}`;

  private readonly RAWDATA_URL = `${RawData_URL}`;
  private readonly DISPLAY_COMPLIANCE_EXPORT_URL = `${RawData_URL}/displayComplianceExport`;
  private readonly PRICE_PROMOTION_EXPORT_URL = `${RawData_URL}/pricePromotionExport`;
  private readonly PRICE_TRACKING_EXPORT_URL = `${RawData_URL}/priceTrackingExport`;
  private readonly CHECK_IN_OUT_EXPORT_URL = `${RawData_URL}/checkInOutExport`;

  /** Shared async-export API config for Display Compliance (use with AsyncExportService). */
  readonly displayComplianceExportApi: AsyncExportApiConfig = {
    apiBaseUrl: this.DISPLAY_COMPLIANCE_EXPORT_URL,
    exportingLabel: 'Exporting display compliance',
    defaultFileName: 'Display-Compliance-RawData.csv',
  };

  /** Shared async-export API config for Display Matrix (use with AsyncExportService). */
  readonly displayMatrixExportApi: AsyncExportApiConfig = {
    apiBaseUrl: `${this.SURVEY_SERVICE_URL}/displayMatrix`,
    exportingLabel: 'Exporting display matrix',
    defaultFileName: 'display-matrix.xlsx',
  };

  /** Shared async-export API config for Price Promotion (use with AsyncExportService). */
  readonly pricePromotionExportApi: AsyncExportApiConfig = {
    apiBaseUrl: this.PRICE_PROMOTION_EXPORT_URL,
    exportingLabel: 'Exporting price promotion',
    defaultFileName: 'price-promotion.csv',
  };

  /** Shared async-export API config for Price Tracking (use with AsyncExportService). */
  readonly priceTrackingExportApi: AsyncExportApiConfig = {
    apiBaseUrl: this.PRICE_TRACKING_EXPORT_URL,
    exportingLabel: 'Exporting price tracking',
    defaultFileName: 'Price-Tracking.csv',
  };

  readonly checkInOutExportApi: AsyncExportApiConfig = {
    apiBaseUrl: this.CHECK_IN_OUT_EXPORT_URL,
    exportingLabel: 'Exporting check-in/out',
    defaultFileName: 'StoreVisitsAttendance.csv',
  };

  private selectedRowDataSource = new BehaviorSubject<any>(null);
  selectedRowData$ = this.selectedRowDataSource.asObservable();
  constructor(
    private http: HttpClient,
    private errorHandler: ErrorHandlerService,
    private asyncExportService: AsyncExportService,
  ) { }

  setSelectedRow(data: any): void {
    this.selectedRowDataSource.next(data);
  }

  clearSelectedRow(): void {
    this.selectedRowDataSource.next(null); // Clear the data
  }

  public surveyGetAll(
    pageIndex: number,
    pageSize: number,
    searchFilter?: string
  ): Observable<any> {
    if (searchFilter) {
      return this.http
        .get<any>(
          `${this.SURVEY_SERVICE_URL}/survey/getAll?pageIndex=${pageIndex}&pageSize=${pageSize}&searchFilter=${searchFilter}`
        )
        .pipe(catchError(this.errorHandler.handleError));
    } else {
      return this.http
        .get<any>(
          `${this.SURVEY_SERVICE_URL}/survey/getAll?pageIndex=${pageIndex}&pageSize=${pageSize}`
        )
        .pipe(catchError(this.errorHandler.handleError));
    }
  }

  // Update Survey Data:
  // {
  //   "surveyId":6,
  //   "userIds":[184]
  // }

  public updateSurvey(data: SurveyLine): Observable<SurveyLine> {
    return this.http
      .post<SurveyLine>(`${this.SURVEY_SERVICE_URL}/survey/updateSurvey`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  //  Update All json data:
  // {
  //   "id":11,
  //   "name":"Survey 11",
  //   "desc":"Test",
  //   "surveyTypeId":11
  // }
  public updateAll(data: SurveyLine): Observable<SurveyLine> {
    return this.http
      .post<SurveyLine>(`${this.SURVEY_SERVICE_URL}/survey/update`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteSurvey(id: number): Observable<any> {
    return this.http
      .delete<any>(`${this.SURVEY_SERVICE_URL}/survey/delete/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getSurveyByUserId(id: number): Observable<any> {
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/survey/getAllSurveyStatusByUserId`, {
        params: new HttpParams().set('userId', id.toString())
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public fetchAllMarketActivity(params: {
    countryId?: number;
    countryIds?: number[];
    pageIndex?: number;
    pageSize?: number;
    categoryId?: number[];
    subCategoryId?: number[];
    brandId?: number[];
    marketActivityId?: number[];
    channelId?: number[];
    accountId?: number[];
    storeId?: number[];
    startDate?: string;
    endDate?: string;
    search?: string;
    focusStore?: string;
  }): Observable<any> {
    let queryParams = new HttpParams();

    if (typeof params.countryId === 'number' && !Number.isNaN(params.countryId)) {
      queryParams = queryParams.set('countryId', params.countryId.toString());
    }
    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }
    if (typeof params.pageIndex === 'number' && !Number.isNaN(params.pageIndex)) {
      queryParams = queryParams.set('pageIndex', params.pageIndex.toString());
    }
    if (typeof params.pageSize === 'number' && !Number.isNaN(params.pageSize)) {
      queryParams = queryParams.set('pageSize', params.pageSize.toString());
    }
    if (params.categoryId && params.categoryId.length > 0) {
      queryParams = queryParams.set('categoryId', params.categoryId.join(','));
    }
    if (params.subCategoryId && params.subCategoryId.length > 0) {
      queryParams = queryParams.set('subCategoryId', params.subCategoryId.join(','));
    }
    if (params.brandId && params.brandId.length > 0) {
      queryParams = queryParams.set('brandId', params.brandId.join(','));
    }
    if (params.marketActivityId && params.marketActivityId.length > 0) {
      queryParams = queryParams.set('marketActivityId', params.marketActivityId.join(','));
    }
    if (params.channelId && params.channelId.length > 0) {
      queryParams = queryParams.set('channelId', params.channelId.join(','));
    }
    if (params.accountId && params.accountId.length > 0) {
      queryParams = queryParams.set('accountId', params.accountId.join(','));
    }
    if (params.storeId && params.storeId.length > 0) {
      queryParams = queryParams.set('storeId', params.storeId.join(','));
    }
    if (params.startDate) {
      queryParams = queryParams.set('startDate', params.startDate);
    }
    if (params.endDate) {
      queryParams = queryParams.set('endDate', params.endDate);
    }
    if (params.search && params.search.trim() !== '') {
      queryParams = queryParams.set('search', params.search.trim());
    }
    if (params.focusStore) {
      queryParams = queryParams.set('focusStore', params.focusStore);
    }

    return this.http
      .get<any>(`${this.PRODUCT_SERVICE_URL}/market_activities/fetchall-marketactivity`, {
        params: queryParams
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getMarketActivityTypes(): Observable<any> {
    return this.http
      .get<any>(`${this.PRODUCT_SERVICE_URL}/market_activities/activity-types`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Downloads all market activity rows for the current filters as an Excel file (no pagination).
   */
  public exportMarketActivityExcel(params: {
    countryId?: number;
    countryIds?: number[];
    categoryId?: number[];
    subCategoryId?: number[];
    brandId?: number[];
    marketActivityId?: number[];
    channelId?: number[];
    accountId?: number[];
    storeId?: number[];
    startDate?: string;
    endDate?: string;
    search?: string;
    focusStore?: string;
  }): Observable<Blob> {
    let queryParams = new HttpParams();

    if (typeof params.countryId === 'number' && !Number.isNaN(params.countryId)) {
      queryParams = queryParams.set('countryId', params.countryId.toString());
    }
    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }
    if (params.categoryId && params.categoryId.length > 0) {
      queryParams = queryParams.set('categoryId', params.categoryId.join(','));
    }
    if (params.subCategoryId && params.subCategoryId.length > 0) {
      queryParams = queryParams.set('subCategoryId', params.subCategoryId.join(','));
    }
    if (params.brandId && params.brandId.length > 0) {
      queryParams = queryParams.set('brandId', params.brandId.join(','));
    }
    if (params.marketActivityId && params.marketActivityId.length > 0) {
      queryParams = queryParams.set('marketActivityId', params.marketActivityId.join(','));
    }
    if (params.channelId && params.channelId.length > 0) {
      queryParams = queryParams.set('channelId', params.channelId.join(','));
    }
    if (params.accountId && params.accountId.length > 0) {
      queryParams = queryParams.set('accountId', params.accountId.join(','));
    }
    if (params.storeId && params.storeId.length > 0) {
      queryParams = queryParams.set('storeId', params.storeId.join(','));
    }
    if (params.startDate) {
      queryParams = queryParams.set('startDate', params.startDate);
    }
    if (params.endDate) {
      queryParams = queryParams.set('endDate', params.endDate);
    }
    if (params.search && params.search.trim() !== '') {
      queryParams = queryParams.set('search', params.search.trim());
    }
    if (params.focusStore) {
      queryParams = queryParams.set('focusStore', params.focusStore);
    }

    return this.http
      .get(`${this.RAWDATA_URL}/rawData/marketActivity/downloadExcelReport`, {
        params: queryParams,
        responseType: 'blob',
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteMarketActivity(id: number): Observable<any> {
    return this.http
      .delete<any>(`${this.PRODUCT_SERVICE_URL}/market_activities/delete/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Imports a list of regions into the server.
   * @param data - An array of regions objects to be imported.
   * @returns An Observable that emits an array of imported regions objects upon successful import.
   */

  public assignSurvey(data: { userId: number, surveyId: number, status: string }): Observable<any> {
    return this.http
      .post<any>(`${this.SURVEY_SERVICE_URL}/survey/assignSurvey`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getSurveyById(id: number): Observable<any> {
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/survey/getById/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public saveSurvey(data: SurveyLine): Observable<SurveyLine> {
    return this.http.post<SurveyLine>(
      `${this.SURVEY_SERVICE_URL}/survey/save`,
      data
    );
  }

  public toggleStatusSurvey(surveyStatus: Status): Observable<any> {
    return this.http
      .put<any>(
        `${this.SURVEY_SERVICE_URL}/survey/changeStatus/${surveyStatus.id}`,
        surveyStatus
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  // public getAllUserToSendInvitation(){
  //   return this.http.get
  // }

  /**
    * Get users to send invitations with optional filters
    * @param pageIndex Page number for pagination
    * @param pageSize Number of items per page
    * @param filterOptions Optional filter parameters (store, promoter, etc.)
    * @param surveyId
    */
  public getAllUserToSendInvitation(
    pageIndex: number,
    pageSize: number,
    filterOptions: {
      store?: string;
      promoter?: string;
      type?: string;
      category?: string;
      area?: string;
    } = {},
    surveyId?: number
  ): Observable<any> {
    let params = new HttpParams()
      .set('pageIndex', pageIndex.toString())
      .set('pageSize', pageSize.toString());

    Object.entries(filterOptions).forEach(([key, value]) => {
      if (value) {
        params = params.set(key, value);
      }
    });

    if (typeof surveyId === 'number' && !isNaN(surveyId)) {
      params = params.set('surveyId', surveyId.toString());
    }


    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/survey/getMobileUsersToSendInvitation`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllUsersForFilter(searchFilter?: string): Observable<any> {
    if (searchFilter) {
      return this.http
        .get<any>(
          `${this.SURVEY_SERVICE_URL}/survey/getUserForFilter?searchFilter=${searchFilter}`
        )
        .pipe(catchError(this.errorHandler.handleError));
    } else {
      return this.http.get<any>(
        `${this.SURVEY_SERVICE_URL}/survey/getUserForFilter`
      );
    }
  }
  public getAllUserTypeToSendInvitation(
    params: {
      pageIndex: number,
      pageSize: number,
      countryIds?: number[]
    }
  ): Observable<any> {

    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize)
    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','))
    }
    return this.http
      .get<any>(
        `${this.USER_URL}/user/getRoleWithActiveUserCount`, { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }



  public getAllSurveyData(searchFilter?: string): Observable<any> {
    const url = searchFilter
      ? `${this.SURVEY_SERVICE_URL}/survey/getUserForFilter?searchFilter=${searchFilter}`
      : `${this.SURVEY_SERVICE_URL}/survey/getUserForFilter`;

    return this.http
      .get<any>(url)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getUserByIdForSurvey(roleId: number, year: number): Observable<any> {
    return this.http
      .get<any>(
        `${this.SURVEY_SERVICE_URL}/survey/getSurveyMapByRoleId?roleId=${roleId}&year=${year}`
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public assignSurveyForUser(params: {
    surveyId: number;
    year: number;
    month: number;
    roleId: number;
    // countryId: number;
  }): Observable<any> {
    const url = `${this.SURVEY_SERVICE_URL}/survey/surveyMapping`;

    return this.http
      .post<any>(url, params)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllActiveSurveys(): Observable<any> {
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/survey/getAllActive`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
  * Get survey details if user did any changes
  * @param pageIndex Page number for pagination
  * @param pageSize Number of items per page
  * @param dateOfTimeline Optional filter to fetch based on the date
  */
  public getTimeLineDetails(params: {
    pageIndex: number,
    pageSize: number,
    dateOfTimeline?: string,
    surveyId?: number,
    countryId?: number[],
    storeId?: number[],
    userId?: number[],
    accountId?: number[],
    areaId?: number[]
  }): Observable<any> {
    let queryParams = new HttpParams().set('pageIndex', params.pageIndex).set('pageSize', params.pageSize);
    if (params.dateOfTimeline) {
      queryParams = queryParams.set('dateOfTimeline', params.dateOfTimeline)
    }
    if (params.surveyId) {
      queryParams = queryParams.set('surveyId', params.surveyId)
    }
    if (params.countryId && params.countryId.length > 0) {
      queryParams = queryParams.set('countryId', params.countryId.join(','))
    }
    if (params.storeId && params.storeId.length > 0) {
      queryParams = queryParams.set('storeId', params.storeId.join(','))
    }
    if (params.userId && params.userId.length > 0) {
      queryParams = queryParams.set('userId', params.userId.join(','))
    }
    if (params.accountId && params.accountId.length > 0) {
      queryParams = queryParams.set('accountId', params.accountId.join(','))
    }
    if (params.areaId && params.areaId.length > 0) {
      queryParams = queryParams.set('areaId', params.areaId.join(','))
    }

    return this.http.get<any>(`${this.SURVEY_SERVICE_URL}/timeline/getAll`, { params: queryParams }).pipe(catchError(this.errorHandler.handleError));
  }

  /**
* To save the survey comments
* @body we have to pass surveyresponseId and comment
*/
  public saveSurveyComments(data: any): Observable<any> {
    return this.http.post<any>(`${this.SURVEY_SERVICE_URL}/timeline/comment/save`, data).pipe(catchError(this.errorHandler.handleError));
  }

  // Get all active languages
  public getAllActiveLanguages(): Observable<any> {
    return this.http
      .get<any>(`${this.USER_URL}/language/getAllActive`)
      .pipe(catchError(this.errorHandler.handleError));
  }
  /**
   * Get users to send invitations with optional filters
   * @param pageIndex Page number for pagination
   * @param pageSize Number of items per page
   * @param filterOptions Optional filter parameters (survey, username, etc.)
   */
  public getAllInviteHistory(
    pageIndex: number,
    pageSize: number,
    filterOptions?: {
      survey?: string;
      username?: string;
      loginid?: string;
      invitedate?: string;
      usertype?: string;
      province?: string;
    },
    searchFilter?: string
  ): Observable<any> {
    let params = new HttpParams()
      .set('pageIndex', pageIndex.toString())
      .set('pageSize', pageSize.toString());

    if (searchFilter) {
      params = params.set('searchFilter', searchFilter);
    }
    if (filterOptions) {
      Object.entries(filterOptions).forEach(([key, value]) => {
        if (value) {
          params = params.set(key, value);
        }
      });
    }
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/survey/inviteHistory`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getInviteHistoryFilters(): Observable<any> {
    return this.http.get<any>(`${this.SURVEY_SERVICE_URL}/survey/invitedata`)
  }

  /**
   * Saves form field data to the server
   * @param data The form field data to be saved
   * @returns An Observable of the saved form field data
   */
  public saveFormFields(data: any): Observable<any> {
    return this.http
      .post<any>(`${this.SURVEY_SERVICE_URL}/formField/save`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public saveSurveyGeneral(surveyData: any): Observable<any> {
    return this.http.post(`${this.SURVEY_SERVICE_URL}/survey/save`, surveyData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateSurveyGeneral(surveyData: any): Observable<any> {
    return this.http.put(`${this.SURVEY_SERVICE_URL}/survey/update`, surveyData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllFrequencies(): Observable<any> {
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/frequency/getAll`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteSurveyInvite(id: number): Observable<any> {
    return this.http
      .delete<any>(`${this.SURVEY_SERVICE_URL}/survey/inviteDelete/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Deletes form fields for a specific survey and language
   * @param surveyId The ID of the survey
   * @param languageId The ID of the language
   * @returns An Observable of the deletion result
   */
  public deleteFormFieldsByLanguage(surveyId: number, languageId: number): Observable<any> {
    return this.http
      .delete<any>(`${this.SURVEY_SERVICE_URL}/formField/deleteFormFieldBySurveyIdandLangId/${surveyId}/${languageId}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public publishSurvey(surveyId: number): Observable<any> {
    return this.http.put(`${this.SURVEY_SERVICE_URL}/survey/isPublish/${surveyId}`, {});
  }

  /**
   * Gets form fields for a specific survey and language
   * @param surveyId The ID of the survey
   * @param languageId The ID of the language
   * @returns An Observable of the form fields
   */
  public getFormFieldsBySurveyId(surveyId: number, languageId: number): Observable<any> {
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/formField/getBySurveyId/${surveyId}?langId=${languageId}`)
      .pipe(catchError(this.errorHandler.handleError));
  }


  public getTimlineFilterValue(params: { countryId?: number[], storeId?: number[] }): Observable<any> {
    let queryParams = new HttpParams()
    if (params.countryId && params.countryId.length > 0) {
      queryParams = queryParams.set('countryId', params.countryId.join(','))
    }
    if (params.storeId && params.storeId.length > 0) {
      queryParams = queryParams.set('storeId', params.storeId.join(','))
    }
    return this.http.get<any>(`${this.SURVEY_SERVICE_URL}/timeline/getFilterValues`, { params: queryParams }).pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Gets check-in/check-out data with optional filters
   * @param pageIndex Page number for pagination
   * @param pageSize Number of items per page
   * @param filterOptions Optional filter parameters
   * @returns An Observable of the check-in/check-out data
   */
  public getCheckInOut(
    pageIndex: number,
    pageSize: number,
    filterOptions?: {
      roleId?: number;
      countryId?: number[];
      fromDate?: string;
      toDate?: string;
      searchFilter?: string;
      autoCheckOut?: number;
      type?: number;
      focusStore?: string;
      promoterCategoryId?: number;
    }
  ): Observable<any> {
    let params = new HttpParams()
      .set('pageIndex', pageIndex.toString())
      .set('pageSize', pageSize.toString());

    if (filterOptions) {
      Object.entries(filterOptions).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') {
          return;
        }
        if (Array.isArray(value)) {
          if (value.length === 0) return;
          params = params.set(key, value.join(','));
        } else {
          params = params.set(key, String(value));
        }
      });
    }

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/checkInOut/getCheckInOut`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public performManagerialCheckout(checkInOutId: number): Observable<any> {

    return this.http.put(`${this.SURVEY_SERVICE_URL}/checkInOut/managerialCheckOut/${checkInOutId}`, {
      isManualCheckoutByAdmin: true,
    }).pipe(catchError(this.errorHandler.handleError));
  }

  public getUserAttendanceCalendar(params: {
    regionId?: number;
    countryId?: number;
    roleId?: number;
    year: number;
    month: number;
    pageIndex?: number;
    pageSize?: number;
    searchValue?: string;

  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('year', params.year.toString())
      .set('month', params.month.toString())
      .set('pageIndex', (params.pageIndex ?? 1).toString())
      .set('pageSize', (params.pageSize ?? 20).toString());

    if (params.regionId) {
      queryParams = queryParams.set('regionId', params.regionId.toString());
    }
    if (params.countryId) {
      queryParams = queryParams.set('countryId', params.countryId.toString());
    }
    if (params.roleId) {
      queryParams = queryParams.set('roleId', params.roleId.toString());
    }
    if (params.searchValue) {
      queryParams = queryParams.set('searchValue', params.searchValue);
    }


    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/checkInOut/getUserAttendanceCalendar`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getUserAttendanceCalendarExcel(params: {
    regionId?: number;
    countryId?: number;
    roleId?: number;
    year: number;
    month: number;
    searchValue?: string;

  }): Observable<Blob> {
    let queryParams = new HttpParams()
      .set('year', params.year.toString())
      .set('month', params.month.toString());

    if (params.regionId) {
      queryParams = queryParams.set('regionId', params.regionId.toString());
    }
    if (params.countryId) {
      queryParams = queryParams.set('countryId', params.countryId.toString());
    }
    if (params.roleId) {
      queryParams = queryParams.set('roleId', params.roleId.toString());
    }
    if (params.searchValue) {
      queryParams = queryParams.set('searchValue', params.searchValue);
    }


    return this.http
      .get(`${this.SURVEY_SERVICE_URL}/checkInOut/getUserAttendanceCalendarExcel/downloadExcelTemplate`, {
        params: queryParams,
        responseType: 'blob'
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getNoAttendanceCalendar(params: {
    regionId?: number | number[];
    countryId?: number | number[];
    roleId?: number;
    year: number;
    month: number;
    pageIndex?: number;
    pageSize?: number;
    searchValue?: string;
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('year', params.year.toString())
      .set('month', params.month.toString())
      .set('pageIndex', (params.pageIndex ?? 1).toString())
      .set('pageSize', (params.pageSize ?? 20).toString());

    if (Array.isArray(params.regionId)) {
      if (params.regionId.length > 0) {
        queryParams = queryParams.set('regionId', params.regionId.join(','));
      }
    } else if (params.regionId) {
      queryParams = queryParams.set('regionId', params.regionId.toString());
    }
    if (Array.isArray(params.countryId)) {
      if (params.countryId.length > 0) {
        queryParams = queryParams.set('countryId', params.countryId.join(','));
      }
    } else if (params.countryId) {
      queryParams = queryParams.set('countryId', params.countryId.toString());
    }
    if (params.roleId) {
      queryParams = queryParams.set('roleId', params.roleId.toString());
    }
    if (params.searchValue) {
      queryParams = queryParams.set('searchValue', params.searchValue);
    }

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/checkInOut/getNoAttendanceCalendar`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getNoAttendanceCalendarExcel(params: {
    regionId?: number | number[];
    countryId?: number | number[];
    roleId?: number;
    year: number;
    month: number;
    searchValue?: string;
  }): Observable<Blob> {
    let queryParams = new HttpParams()
      .set('year', params.year.toString())
      .set('month', params.month.toString());

    if (Array.isArray(params.regionId)) {
      if (params.regionId.length > 0) {
        queryParams = queryParams.set('regionId', params.regionId.join(','));
      }
    } else if (params.regionId) {
      queryParams = queryParams.set('regionId', params.regionId.toString());
    }
    if (Array.isArray(params.countryId)) {
      if (params.countryId.length > 0) {
        queryParams = queryParams.set('countryId', params.countryId.join(','));
      }
    } else if (params.countryId) {
      queryParams = queryParams.set('countryId', params.countryId.toString());
    }
    if (params.roleId) {
      queryParams = queryParams.set('roleId', params.roleId.toString());
    }
    if (params.searchValue) {
      queryParams = queryParams.set('searchValue', params.searchValue);
    }

    return this.http
      .get(`${this.SURVEY_SERVICE_URL}/checkInOut/getNoAttendanceCalendarExcel/downloadExcelTemplate`, {
        params: queryParams,
        responseType: 'blob'
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Downloads check-in/check-out data in Excel format
   * @param filterOptions Optional filter parameters
   * @returns Observable<Blob> The Excel file as a blob
   */
  public downloadExcelReport(filterOptions?: {
    roleId?: number;
    countryId?: number | number[];
    fromDate?: string;
    toDate?: string;
    searchFilter?: string;
    exportImageType?: number;
    type?: number;
    autoCheckOut?: number;
    focusStore?: string;
    promoterCategoryId?: number;
  }): Observable<Blob> {
    let params = new HttpParams();

    if (filterOptions) {
      Object.entries(filterOptions).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') {
          return;
        }
        if (Array.isArray(value)) {
          if (value.length === 0) return;
          params = params.set(key, value.join(','));
        } else {
          params = params.set(key, String(value));
        }
      });
    }

    return this.http.get(`${this.SURVEY_SERVICE_URL}/checkInOut/downloadExcelReport`, {
      params,
      responseType: 'blob'
    }).pipe(catchError(this.errorHandler.handleError));
  }

  public downloadCheckInOutMediaStream(
    id: string,
    fileIndex: string,
    kind: string
  ): Observable<HttpResponse<Blob>> {
    const params = new HttpParams()
      .set('id', id)
      .set('fileIndex', fileIndex || '0')
      .set('kind', kind || 'checkin-checkout-image');
    const headers = new HttpHeaders().set('isBlob', 'true');
    return this.http
      .get(`${this.SURVEY_SERVICE_URL}/checkInOut/media/file`, {
        params,
        observe: 'response',
        responseType: 'blob',
        headers,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Gets visit summary data with pagination and filters
   * @param filterOptions Optional filter parameters including pagination
   * @returns Observable of visit summary data with pagination
   */
  public getVisitSummary(filterOptions?: {
    userId?: number;
    roleName?: string;
    startDate?: string;
    endDate?: string;
    countryId?: number[];
    type?: number;
    searchFilter?: string;
    page?: number;
    limit?: number;
  }): Observable<{
    status: boolean;
    path: string;
    statusCode: number;
    result: {
      data: Array<{
        userId: number;
        name: string;
        planCount: string;
        unplannedCount: string;
        plannedVisitedCount: string;
        plannedNotVisitedCount: string;
        cancelledCount: string;
      }>;
      pagination: {
        page: number;
        limit: number;
        totalItems: number;
        totalPages: number;
      };
      cardTotals?: {
        plan: number;
        actualVisits: number;
        unplanned: number;
        notVisited: number;
        cancelled: number;
      };
    };
  }> {
    let params = new HttpParams();

    if (filterOptions) {
      Object.entries(filterOptions).forEach(([key, value]) => {
        if (value === undefined || value === null) {
          return;
        }

        if (Array.isArray(value)) {
          if (value.length === 0) {
            return;
          }
          params = params.set(key, value.join(','));
          return;
        }

        const str = value.toString();
        if (str === '') {
          return;
        }
        params = params.set(key, str);
      });
    }

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/journeyPlan/getVisitSummary`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  // Get user list for display matrix filter
  public getUserListDisplaymatrix(countryId?: number[]): Observable<any> {
    let queryParams = new HttpParams();
    if (countryId && countryId.length > 0) {
      queryParams = queryParams.set('countryId', countryId.join(','));
    }
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/displayMatrix/getAllDisplayMatrixDetails/userList`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllDisplayMatrix(params: {
    pageIndex: any,
    pageSize: any,
    searchFilter?: string,
    country?: string[],
    store?: string[],
    product?: string[],
    startDate?: string,
    endDate?: string,
    channel?: string[],
    account?: string[],
    category?: string[],
    subCategory?: string[],
    displayType?: string[],
    segmentId?: number[],
    isPosm?: string,
    isPosmAsPerGuidline?: string,
    areaId?: number[],
    stateId?: number[],
    userId?: string | number | (string | number)[],
    brandId?: number[],
    model?: string[],
    starProduct?: string
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize);
    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }
    if (params.country && params.country.length > 0) {
      queryParams = queryParams.set('country', params.country.join(','));
    }
    if (params.product && params.product.length > 0) {
      queryParams = queryParams.set('product', params.product.join(','));
    }
    if (params.category && params.category.length > 0) {
      queryParams = queryParams.set('category', params.category.join(','));
    }
    if (params.brandId && params.brandId.length > 0) {
      queryParams = queryParams.set('brandId', params.brandId.join(','));
    }
    if (params.model && params.model.length > 0) {
      queryParams = queryParams.set('model', params.model.join(','));
    }
    if (params.subCategory && params.subCategory.length > 0) {
      queryParams = queryParams.set('subCategory', params.subCategory.join(','));
    }
    if (params.startDate) {
      queryParams = queryParams.set('startDate', params.startDate);
    }
    if (params.endDate) {
      queryParams = queryParams.set('endDate', params.endDate);
    }
    if (params.account && params.account.length > 0) {
      queryParams = queryParams.set('account', params.account.join(','));
    }
    if (params.channel && params.channel.length > 0) {
      queryParams = queryParams.set('channel', params.channel.join(','));
    }
    if (params.store && params.store.length > 0) {
      queryParams = queryParams.set('store', params.store.join(','));
    }
    if (params.displayType && params.displayType.length > 0) {
      queryParams = queryParams.set('displayType', params.displayType.join(','));
    }
    if (params.segmentId && params.segmentId.length > 0) {
      queryParams = queryParams.set('segmentId', params.segmentId.join(','));
    }
    if (params.isPosm) {
      queryParams = queryParams.set('isPosm', params.isPosm);
    }
    if (params.isPosmAsPerGuidline) {
      queryParams = queryParams.set('isPosmAsPerGuidline', params.isPosmAsPerGuidline);
    }
    if (params.areaId && params.areaId.length > 0) {
      queryParams = queryParams.set('areaId', params.areaId.join(','));
    }
    if (params.stateId && params.stateId.length > 0) {
      queryParams = queryParams.set('stateId', params.stateId.join(','));
    }
    if (params.userId) {
      let userIdValue = params.userId;
      if (Array.isArray(userIdValue)) {
        userIdValue = userIdValue.join(',');
      }
      queryParams = queryParams.set('userId', userIdValue.toString());
    }
    if (params.starProduct) {
      queryParams = queryParams.set('starProduct', params.starProduct);
    }
    return this.http.get<any>(`${this.SURVEY_SERVICE_URL}/displayMatrix/getAllDisplayMatrixDetails`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError))
  }

  public getDisplayMatrixModels(params: {
    pageIndex: number,
    pageSize: number,
    searchFilter?: string,
    country?: number[],
    startDate: string,
    endDate: string,
    category?: number[],
    subCategory?: number[],
    brandId?: number[],
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize)
      .set('startDate', params.startDate)
      .set('endDate', params.endDate);

    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }
    if (params.country && params.country.length > 0) {
      queryParams = queryParams.set('country', params.country.join(','));
    }
    if (params.category && params.category.length > 0) {
      queryParams = queryParams.set('category', params.category.join(','));
    }
    if (params.subCategory && params.subCategory.length > 0) {
      queryParams = queryParams.set('subCategory', params.subCategory.join(','));
    }
    if (params.brandId && params.brandId.length > 0) {
      queryParams = queryParams.set('brandId', params.brandId.join(','));
    }

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/displayMatrix/getModels`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public exportDisplayMatrix(params: {
    pageIndex: any,
    pageSize: any,
    exportImageType: number,
    searchFilter?: string,
    country?: number[],
    storeName?: string[],
    product?: string[],
    startDate?: string,
    endDate?: string,
    channel?: string[],
    account?: string[],
    category?: string[],
    subCategory?: string[],
    displayType?: number[],
    segmentId?: number[],
    isPosmAsPerGuidline?: string,
    noPosmGuidlinesReasonName?: string,
    isPosm?: string,
    areaId?: number[],
    stateId?: number[],
    userId?: string | number | (string | number)[],
    brandId?: number[],
    model?: string[],
    starProduct?: string
  }): Observable<Blob> {

    const columns: string[] = [
      'Country',
      'Store Code',
      'Store',
      'Focus Store',
      'City',
      'State',
      'Area Group',
      'Channel',
      'Account',
      'Date',
      'Category',
      'Sub Category',
      'Segment',
      'Brand',
      'Project Name',
      'MDA/SDA/O',
      'Model',
      'PNC',
      'Display Type',
      'Quantity',
      'Is POSM available',
      'No Posm Guidlines Reason',
      'Is POSM as per guidline',
      'Reason for not as per guideline',
      'Before Image 1',
      'After Image 1',
      'Uploaded By',
      'Created By',
      'Created Date & Time',
      'Updated Date & Time',
    ];

    //export needed columns
    // const columns:string[] = []; //export all columns
    return this.http
      .post<Blob>(
        `${this.SURVEY_SERVICE_URL}/displayMatrix/downloadExcelReport`,
        { columns, ...params },
        {
          responseType: 'blob' as 'json',
        }
      )
      .pipe(catchError(this.errorHandler.handleError));

  }

  public exportDisplayMatrixAsync(
    params: {
      pageIndex: any,
      pageSize: any,
      exportImageType: number,
      searchFilter?: string,
      country?: number[],
      storeName?: string[],
      product?: string[],
      startDate?: string,
      endDate?: string,
      channel?: string[],
      account?: string[],
      category?: string[],
      subCategory?: string[],
      displayType?: number[],
      segmentId?: number[],
      isPosmAsPerGuidline?: string,
      noPosmGuidlinesReasonName?: string,
      isPosm?: string,
      areaId?: number[],
      stateId?: number[],
      userId?: string | number | (string | number)[],
      brandId?: number[],
      model?: string[],
      starProduct?: string
    },
    progress: ExcelExportProgressSlot,
  ): Observable<void> {
    return this.asyncExportService
      .runAsyncExport(
        this.displayMatrixExportApi,
        params as Record<string, unknown>,
        progress,
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public downloadDisplayMatrixMediaStream(
    id: string,
    fileIndex: string,
    kind: string
  ): Observable<HttpResponse<Blob>> {
    const params = new HttpParams()
      .set('id', id)
      .set('fileIndex', fileIndex || '0')
      .set('kind', kind || 'display-matrix-before');
    const headers = new HttpHeaders().set('isBlob', 'true');
    return this.http
      .get(`${this.SURVEY_SERVICE_URL}/displayMatrix/media/file`, {
        params,
        observe: 'response',
        responseType: 'blob',
        headers,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public downloadAegSurveyMediaStream(
    id: string,
    fileIndex: string,
    kind: string
  ): Observable<HttpResponse<Blob>> {
    const params = new HttpParams()
      .set('id', id)
      .set('fileIndex', fileIndex || '0')
      .set('kind', kind || 'aeg-survey-image');
    const headers = new HttpHeaders().set('isBlob', 'true');
    return this.http
      .get(`${this.SURVEY_SERVICE_URL}/aegSurvey/media/file`, {
        params,
        observe: 'response',
        responseType: 'blob',
        headers,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public downloadSnagReportMediaStream(
    id: string,
    fileIndex: string,
    kind: string,
    source: string,
  ): Observable<HttpResponse<Blob>> {
    const params = new HttpParams()
      .set('id', id)
      .set('fileIndex', fileIndex || '0')
      .set('kind', kind || 'snag-report-image')
      .set('source', source || 'open');
    const headers = new HttpHeaders().set('isBlob', 'true');
    return this.http
      .get(`${this.SURVEY_SERVICE_URL}/snagreport/media/file`, {
        params,
        observe: 'response',
        responseType: 'blob',
        headers,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllSelloutData(params: { type?: string, categoryId?: number, subCategoryId?: number, searchFilter?: string }): Observable<any> {

    let queryParams = new HttpParams()

    if (params.type) {
      queryParams = queryParams.set('type', params.type);
    }

    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }

    if (params.categoryId) {
      queryParams = queryParams.set('category', params.categoryId);
    }
    if (params.subCategoryId) {
      queryParams = queryParams.set('subCategory', params.subCategoryId);
    }

    return this.http.get<any>(`${this.SURVEY_SERVICE_URL}/sellOut/sellout-graph`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError))
  }

  public updateSelloutPriceAndQuantity(payload: any): Observable<any> {
    // Normalize possible id keys and coerce numeric fields to numbers to avoid type issues
    const saleId = payload?.saleId ?? payload?.salesId ?? payload?.id ?? payload?.sales_id ?? payload?.sale_id;
    const invoicePriceUnitRaw = payload?.invoicePriceUnit ?? payload?.invoice_price_unit ?? payload?.invoicePrice ?? payload?.price;
    const quantityRaw = payload?.quantity ?? payload?.qnt ?? payload?.qty ?? payload?.quantityUnit;

    const body = {
      saleId,
      invoicePriceUnit: invoicePriceUnitRaw !== undefined && invoicePriceUnitRaw !== null ? Number(invoicePriceUnitRaw) : invoicePriceUnitRaw,
      quantity: quantityRaw !== undefined && quantityRaw !== null ? Number(quantityRaw) : quantityRaw,
    };

    return this.http
      .put<any>(`${this.SURVEY_SERVICE_URL}/sellOut/updatePriceAndQuantity`, body)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteSellout(id: number): Observable<any> {
    if (id === undefined || id === null) {
      throw new Error('deleteSellout requires a valid id');
    }
    return this.http
      .delete<any>(`${this.SURVEY_SERVICE_URL}/sellOut/delete/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Download a protected Excel template pre-populated with the currently-filtered sellout records.
   * Quantity and Price columns are unlocked; all others are read-only.
   */
  public downloadSelloutTemplate(params: {
    fromDate?: string;
    toDate?: string;
    countryId?: string;
    channelId?: string | number[];
    accountId?: string | number[];
    categoryId?: string | number[];
    subCategoryId?: string | number[];
    storeId?: string | number[];
    productId?: string | number;
    searchFilter?: string;
    userId?: string | number[];
    areaId?: string | number[];
    stateId?: string | number[];
    starProduct?: string | number | number[];
    mdaSda?: string;
    isBundleOffer?: string;
    channelType?: string | number | number[];
  }): Observable<Blob> {
    let queryParams = new HttpParams();
    const addParam = (key: string, val: any) => {
      if (val === undefined || val === null || val === '') return;
      const str = Array.isArray(val) ? val.join(',') : String(val);
      if (str) queryParams = queryParams.set(key, str);
    };
    addParam('fromDate', params.fromDate);
    addParam('toDate', params.toDate);
    addParam('countryId', params.countryId);
    addParam('channelId', params.channelId);
    addParam('accountId', params.accountId);
    addParam('categoryId', params.categoryId);
    addParam('subCategoryId', params.subCategoryId);
    addParam('storeId', params.storeId);
    addParam('productId', params.productId);
    addParam('searchFilter', params.searchFilter);
    addParam('userId', params.userId);
    addParam('areaId', params.areaId);
    addParam('stateId', params.stateId);
    addParam('starProduct', params.starProduct);
    addParam('mdaSda', params.mdaSda);
    addParam('isBundleOffer', params.isBundleOffer);
    addParam('channelType', params.channelType);

    return this.http
      .get(`${this.RAWDATA_URL}/rawData/selloutTemplate/downloadExcelReport`, {
        params: queryParams,
        responseType: 'blob',
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Bulk-update Quantity and Price for multiple sellout records in one request.
   * Returns { updated: number, errors: { saleId, reason }[] }.
   */
  public bulkUpdateSelloutPriceAndQuantity(
    records: { saleId: number; quantity: number; invoicePriceUnit: number }[]
  ): Observable<{ updated: number; errors: { saleId: number | string; reason: string }[] }> {
    return this.http
      .post<{ updated: number; errors: { saleId: number | string; reason: string }[] }>(
        `${this.SURVEY_SERVICE_URL}/sellOut/bulkUpdatePriceAndQuantity`,
        { records }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }



  public fetchCompletionStatus(params: { surveyType?: string, pageIndex?: number, pageSize?: number, month?: number, year?: number, searchFilter?: string, status: string, countryId: number[] | any }): Observable<any> {

    let queryParams = new HttpParams()

    if (params.surveyType) {
      queryParams = queryParams.set('surveyType', params.surveyType);
    }
    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }
    if (params.pageIndex) {
      queryParams = queryParams.set('pageIndex', params.pageIndex);
    }
    if (params.pageSize) {
      queryParams = queryParams.set('pageSize', params.pageSize);
    }
    if (params.month) {
      queryParams = queryParams.set('month', params.month);
    }
    if (params.year) {
      queryParams = queryParams.set('year', params.year);
    }
    if (params.status) {
      queryParams = queryParams.set('status', params.status);
    }
    if (params.countryId && params.countryId.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryId);
    }


    return this.http.get<any>(`${this.SURVEY_SERVICE_URL}/survey/completionDetails`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError))
  }



  public exportCompletionStatus(params: {
    surveyType?: string, pageIndex?: number, pageSize?: number, month?: number, year?: number, searchFilter?: string, status: string, countryId: number[] | any
  }): Observable<Blob> {

    const columns: string[] = [
      'Store ID',
      'Name',
      'City',
      'Address',
      'Account',
      'Channel',
      'TDS'
    ];

    //export needed columns
    // const columns:string[] = []; //export all columns
    return this.http
      .post<Blob>(
        `${this.SURVEY_SERVICE_URL}/survey/completionDetails/downloadExcelReport`,
        { columns, ...params },
        {
          responseType: 'blob' as 'json',
        }
      )
      .pipe(catchError(this.errorHandler.handleError));

  }

  public getAllJourneyPlan(pageIndex: number, pageSize: number, countryId?: number, stateId?: any, userId?: number, roleId?: number, search?: string, promoterCategoryId?: number, month?: number, year?: number, accountId?: string | number, storeId?: string | number): Observable<any> {
    let url = `${this.SURVEY_SERVICE_URL}/journeyPlan/getAllJourneyPlan?pageIndex=${pageIndex}&pageSize=${pageSize}`;
    if (countryId) {
      url += `&countryId=${countryId}`;
    }
    if (stateId) {
      url += `&stateId=${stateId}`;
    }
    if (userId) {
      url += `&userId=${userId}`;
    }
    if (roleId) {
      url += `&roleId=${roleId}`;
    }
    if (search) {
      url += `&search=${search}`;
    }
    if (promoterCategoryId) {
      url += `&promoterCategoryId=${promoterCategoryId}`;
    }
    if (month) {
      url += `&month=${month}`;
    }
    if (year) {
      url += `&year=${year}`;
    }
    if (accountId) {
      url += `&accountId=${accountId}`;
    }
    if (storeId) {
      url += `&storeId=${storeId}`;
    }
    return this.http
      .get<any>(url)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public journeyPlanExcelReport(
    userId?: number,
    countryId?: number,
    stateId?: any,
    roleId?: number,
    month?: number,
    year?: number,
    search?: string,
    promoterCategoryId?: number,
    accountId?: string | number,
    storeId?: string | number
  ): Observable<Blob> {
    let queryParams = new HttpParams()
    if (userId) {
      queryParams = queryParams.set('userId', userId);
    }
    if (countryId) {
      queryParams = queryParams.set('countryId', countryId);
    }
    if (stateId) {
      queryParams = queryParams.set('stateId', stateId);
    }
    if (roleId) {
      queryParams = queryParams.set('roleId', roleId);
    }
    if (month) {
      queryParams = queryParams.set('month', month);
    }
    if (year) {
      queryParams = queryParams.set('year', year);
    }
    if (search) {
      queryParams = queryParams.set('search', search);
    }
    if (promoterCategoryId) {
      queryParams = queryParams.set('promoterCategoryId', promoterCategoryId);
    }
    if (accountId) {
      queryParams = queryParams.set('accountId', accountId);
    }
    if (storeId) {
      queryParams = queryParams.set('storeId', storeId);
    }
    return this.http
      .post<Blob>(`${this.SURVEY_SERVICE_URL}/journeyPlan/getAllJourneyPlan/downloadExcelReport`, {}, { params: queryParams, responseType: 'blob' as 'json' })
      .pipe(catchError(this.errorHandler.handleError));
  }



  public journeyPlanExcelDownloadCalender(userId: number, roleId: number, month: number, year: number, countryId: number, stateId: any): Observable<Blob> {
    let queryParams = new HttpParams()
    if (userId) {
      queryParams = queryParams.set('userId', userId);
    }
    if (month) {
      queryParams = queryParams.set('month', month);
    }
    if (year) {
      queryParams = queryParams.set('year', year);
    }

    if (countryId) {
      queryParams = queryParams.set('countryId', countryId);
    }
    if (stateId) {
      queryParams = queryParams.set('stateId', stateId);
    }
    queryParams = queryParams.set('roleId', roleId);

    return this.http
      .post<Blob>(`${this.RAWDATA_URL}/rawJourneyPlan/calender/downloadExcelTemplate`, {}, { params: queryParams, responseType: 'blob' as 'json' })
      .pipe(catchError(this.errorHandler.handleError));
  }



  public getPromoterShareGraph(params: { country?: string, channel?: string, account?: string, store?: string, promoterType?: string, year?: number, areaId?: string, stateId?: string }): Observable<any> {
    let queryParams = new HttpParams();
    if (params?.country) {
      queryParams = queryParams.set('country', params?.country);
    }
    if (params?.channel) {
      queryParams = queryParams.set('channel', params?.channel);
    }
    if (params?.account) {
      queryParams = queryParams.set('account', params?.account);
    }
    if (params?.store) {
      queryParams = queryParams.set('store', params?.store);
    }
    if (params?.promoterType) {
      queryParams = queryParams.set('promoterType', params?.promoterType);
    }
    if (params?.year) {
      queryParams = queryParams.set('year', params?.year);
    }
    if (params?.areaId) {
      queryParams = queryParams.set('areaId', params.areaId);
    }
    if (params?.stateId) {
      queryParams = queryParams.set('stateId', params.stateId);
    }
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/survey/getPromoterShareGraph`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getPromoterShareTable(params: { country?: string, channel?: string, account?: string, store?: string, promoterType?: string, year?: number, areaId?: string, stateId?: string }): Observable<any> {
    let queryParams = new HttpParams();
    if (params?.country) {
      queryParams = queryParams.set('country', params?.country);
    }
    if (params?.channel) {
      queryParams = queryParams.set('channel', params?.channel);
    }
    if (params?.account) {
      queryParams = queryParams.set('account', params?.account);
    }
    if (params?.store) {
      queryParams = queryParams.set('store', params?.store);
    }
    if (params?.promoterType) {
      queryParams = queryParams.set('promoterType', params?.promoterType);
    }
    if (params?.year) {
      queryParams = queryParams.set('year', params?.year);
    }
    if (params?.areaId) {
      queryParams = queryParams.set('areaId', params.areaId);
    }
    if (params?.stateId) {
      queryParams = queryParams.set('stateId', params.stateId);
    }
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/survey/getPromoterShareTable`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }
  public getTdsPromoterCheckinList(params: { pageIndex: number, pageSize: number, countryId?: number, startDate?: string, endDate?: string, userType?: number, searchValue?: string }): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize);

    if (params.countryId) {
      queryParams = queryParams.set('countryId', params.countryId);
    }

    if (params.startDate) {
      queryParams = queryParams.set('startDate', params.startDate);
    }
    if (params.endDate) {
      queryParams = queryParams.set('endDate', params.endDate);
    }
    if (params.userType) {
      queryParams = queryParams.set('userType', params.userType);
    }
    if (params.searchValue) {
      queryParams = queryParams.set('searchValue', params.searchValue);
    }

    return this.http.get<any>(`${this.SURVEY_SERVICE_URL}/survey/getTdsPromoterCheckinList`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public exportTdsPromoterCheckInList(params: { countryId?: number, startDate?: string, endDate?: string, userType?: number, searchValue?: string }): Observable<any> {
    let queryParams = new HttpParams()
    if (params.countryId) {
      queryParams = queryParams.set('countryId', params.countryId);
    }

    if (params.startDate) {
      queryParams = queryParams.set('startDate', params.startDate);
    }
    if (params.endDate) {
      queryParams = queryParams.set('endDate', params.endDate);
    }
    if (params.userType) {
      queryParams = queryParams.set('userType', params.userType);
    }
    if (params.searchValue) {
      queryParams = queryParams.set('searchValue', params.searchValue);
    }

    return this.http
      .post<Blob>(
        `${this.SURVEY_SERVICE_URL}/survey/downloadExcelReport`,
        {},
        {
          params: queryParams,
          responseType: 'blob' as 'json'
        }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }


  public getCheckInOutByStoreId(params: {
    pageIndex?: any,
    pageSize?: any,
    storeId: number;
  }): Observable<any> {
    let queryParams = new HttpParams()
    if (params.pageIndex) {
      queryParams = queryParams.set('pageIndex', params.pageIndex);
    }
    if (params.pageSize) {
      queryParams = queryParams.set('pageSize', params.pageSize);
    }
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/checkInOut/getCheckInOutByStoreId/${params.storeId}`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Calls the salesDataSurveyDashboardByWeek API in the backend
   */
  public getSalesDataSurveyDashboardByWeek(params: {
    countryId?: number | number[],
    weekFrom?: number,
    weekTo?: number,
    yearFrom?: number,
    yearTo?: number,
    categoryId?: number | number[],
    subCategoryId?: number | number[],
    currencyId?: number,
    productId?: number | number[],
    resultBy?: number
  }): Observable<any> {
    let queryParams = new HttpParams();
    if (params) {
      if (params.countryId !== undefined) {
        if (Array.isArray(params.countryId)) {
          queryParams = queryParams.set('countryId', params.countryId.join(','));
        } else {
          queryParams = queryParams.set('countryId', params.countryId.toString());
        }
      }
      if (params.weekFrom !== undefined) {
        queryParams = queryParams.set('weekFrom', params.weekFrom.toString());
      }
      if (params.weekTo !== undefined) {
        queryParams = queryParams.set('weekTo', params.weekTo.toString());
      }
      queryParams = queryParams.set('yearFrom', (params.yearFrom ?? 0).toString());
      queryParams = queryParams.set('yearTo', (params.yearTo ?? 0).toString());
      if (params.categoryId !== undefined) {
        if (Array.isArray(params.categoryId)) {
          queryParams = queryParams.set('categoryId', params.categoryId.join(','));
        } else {
          queryParams = queryParams.set('categoryId', params.categoryId.toString());
        }
      }
      if (params.subCategoryId !== undefined) {
        if (Array.isArray(params.subCategoryId)) {
          queryParams = queryParams.set('subCategoryId', params.subCategoryId.join(','));
        } else {
          queryParams = queryParams.set('subCategoryId', params.subCategoryId.toString());
        }
      }
      if (params.currencyId !== undefined) {
        queryParams = queryParams.set('currencyId', params.currencyId.toString());
      }
      if (params.productId !== undefined) {
        if (Array.isArray(params.productId)) {
          queryParams = queryParams.set('productId', params.productId.join(','));
        } else {
          queryParams = queryParams.set('productId', params.productId.toString());
        }
      }
      if (params.resultBy !== undefined) {
        queryParams = queryParams.set('resultBy', params.resultBy.toString());
      }
    }
    return this.http.get<any>(`${this.SURVEY_SERVICE_URL}/sellOut/salesDataSurveyDashboardByWeek`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Calls the salesDataSurveyDashboardByDate API in the backend
   */
  public getSalesDataSurveyDashboardByDate(params: {
    countryId?: number | number[],
    dateFrom?: string,
    dateTo?: string,
    categoryId?: number | number[],
    subCategoryId?: number | number[],
    currencyId?: number,
    productId?: number | number[],
    resultBy?: number
  }): Observable<any> {
    let queryParams = new HttpParams();
    if (params) {
      if (params.countryId !== undefined) {
        if (Array.isArray(params.countryId)) {
          queryParams = queryParams.set('countryId', params.countryId.join(','));
        } else {
          queryParams = queryParams.set('countryId', params.countryId.toString());
        }
      }
      if (params.dateFrom !== undefined) {
        queryParams = queryParams.set('dateFrom', params.dateFrom);
      }
      if (params.dateTo !== undefined) {
        queryParams = queryParams.set('dateTo', params.dateTo);
      }
      if (params.categoryId !== undefined) {
        if (Array.isArray(params.categoryId)) {
          queryParams = queryParams.set('categoryId', params.categoryId.join(','));
        } else {
          queryParams = queryParams.set('categoryId', params.categoryId.toString());
        }
      }
      if (params.subCategoryId !== undefined) {
        if (Array.isArray(params.subCategoryId)) {
          queryParams = queryParams.set('subCategoryId', params.subCategoryId.join(','));
        } else {
          queryParams = queryParams.set('subCategoryId', params.subCategoryId.toString());
        }
      }
      if (params.currencyId !== undefined) {
        queryParams = queryParams.set('currencyId', params.currencyId.toString());
      }
      if (params.productId !== undefined) {
        if (Array.isArray(params.productId)) {
          queryParams = queryParams.set('productId', params.productId.join(','));
        } else {
          queryParams = queryParams.set('productId', params.productId.toString());
        }
      }
      if (params.resultBy !== undefined) {
        queryParams = queryParams.set('resultBy', params.resultBy.toString());
      }
    }
    return this.http.get<any>(`${this.SURVEY_SERVICE_URL}/sellOut/salesDataSurveyDashboardByDate`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Calls the salesDataSurveyDashboardByMonth API in the backend
   */
  public getSalesDataSurveyDashboardByMonth(params: {
    countryId?: number | number[],
    monthFrom?: number,
    monthTo?: number,
    yearFrom?: number,
    yearTo?: number,
    categoryId?: number | number[],
    subCategoryId?: number | number[],
    currencyId?: number,
    productId?: number | number[],
    resultBy?: number
  }): Observable<any> {
    let queryParams = new HttpParams();
    if (params) {
      if (params.countryId !== undefined) {
        if (Array.isArray(params.countryId)) {
          queryParams = queryParams.set('countryId', params.countryId.join(','));
        } else {
          queryParams = queryParams.set('countryId', params.countryId.toString());
        }
      }
      if (params.monthFrom !== undefined) {
        queryParams = queryParams.set('monthFrom', params.monthFrom.toString());
      }
      if (params.monthTo !== undefined) {
        queryParams = queryParams.set('monthTo', params.monthTo.toString());
      }
      queryParams = queryParams.set('yearFrom', (params.yearFrom ?? 0).toString());
      queryParams = queryParams.set('yearTo', (params.yearTo ?? 0).toString());
      if (params.categoryId !== undefined) {
        if (Array.isArray(params.categoryId)) {
          queryParams = queryParams.set('categoryId', params.categoryId.join(','));
        } else {
          queryParams = queryParams.set('categoryId', params.categoryId.toString());
        }
      }
      if (params.subCategoryId !== undefined) {
        if (Array.isArray(params.subCategoryId)) {
          queryParams = queryParams.set('subCategoryId', params.subCategoryId.join(','));
        } else {
          queryParams = queryParams.set('subCategoryId', params.subCategoryId.toString());
        }
      }
      if (params.currencyId !== undefined) {
        queryParams = queryParams.set('currencyId', params.currencyId.toString());
      }
      if (params.productId !== undefined) {
        if (Array.isArray(params.productId)) {
          queryParams = queryParams.set('productId', params.productId.join(','));
        } else {
          queryParams = queryParams.set('productId', params.productId.toString());
        }
      }
      if (params.resultBy !== undefined) {
        queryParams = queryParams.set('resultBy', params.resultBy.toString());
      }
    }
    return this.http.get<any>(`${this.SURVEY_SERVICE_URL}/sellOut/salesDataSurveyDashboardByMonth`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Calls the top 10 store survey dashboard by date API in the backend
   */
  public getTop10StoreSurveyDashboardByDate(params: {
    countryId?: number | number[],
    dateFrom?: string,
    dateTo?: string,
    categoryId?: number | number[],
    subCategoryId?: number | number[],
    currencyId?: number,
    productId?: number | number[],
    resultBy?: number
  }): Observable<any> {
    let queryParams = new HttpParams();
    if (params) {
      if (params.countryId !== undefined) {
        if (Array.isArray(params.countryId)) {
          queryParams = queryParams.set('countryId', params.countryId.join(','));
        } else {
          queryParams = queryParams.set('countryId', params.countryId.toString());
        }
      }
      if (params.dateFrom !== undefined) {
        queryParams = queryParams.set('dateFrom', params.dateFrom);
      }
      if (params.dateTo !== undefined) {
        queryParams = queryParams.set('dateTo', params.dateTo);
      }
      if (params.categoryId !== undefined) {
        if (Array.isArray(params.categoryId)) {
          queryParams = queryParams.set('categoryId', params.categoryId.join(','));
        } else {
          queryParams = queryParams.set('categoryId', params.categoryId.toString());
        }
      }
      if (params.subCategoryId !== undefined) {
        if (Array.isArray(params.subCategoryId)) {
          queryParams = queryParams.set('subCategoryId', params.subCategoryId.join(','));
        } else {
          queryParams = queryParams.set('subCategoryId', params.subCategoryId.toString());
        }
      }
      if (params.currencyId !== undefined) {
        queryParams = queryParams.set('currencyId', params.currencyId.toString());
      }
      if (params.productId !== undefined) {
        if (Array.isArray(params.productId)) {
          queryParams = queryParams.set('productId', params.productId.join(','));
        } else {
          queryParams = queryParams.set('productId', params.productId.toString());
        }
      }
      if (params.resultBy !== undefined) {
        queryParams = queryParams.set('resultBy', params.resultBy.toString());
      }
    }
    return this.http.get<any>(`${this.SURVEY_SERVICE_URL}/sellOut/top10StoreSurveyDashboardByDate`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Calls the top 10 store survey dashboard by week API in the backend
   */
  public getTop10StoreSurveyDashboardByWeek(params: {
    countryId?: number | number[],
    weekFrom?: number,
    weekTo?: number,
    yearFrom?: number,
    yearTo?: number,
    categoryId?: number | number[],
    subCategoryId?: number | number[],
    currencyId?: number,
    productId?: number | number[],
    resultBy?: number
  }): Observable<any> {
    let queryParams = new HttpParams();
    if (params) {
      if (params.countryId !== undefined) {
        if (Array.isArray(params.countryId)) {
          queryParams = queryParams.set('countryId', params.countryId.join(','));
        } else {
          queryParams = queryParams.set('countryId', params.countryId.toString());
        }
      }
      if (params.weekFrom !== undefined) {
        queryParams = queryParams.set('weekFrom', params.weekFrom.toString());
      }
      if (params.weekTo !== undefined) {
        queryParams = queryParams.set('weekTo', params.weekTo.toString());
      }
      queryParams = queryParams.set('yearFrom', (params.yearFrom ?? 0).toString());
      queryParams = queryParams.set('yearTo', (params.yearTo ?? 0).toString());
      if (params.categoryId !== undefined) {
        if (Array.isArray(params.categoryId)) {
          queryParams = queryParams.set('categoryId', params.categoryId.join(','));
        } else {
          queryParams = queryParams.set('categoryId', params.categoryId.toString());
        }
      }
      if (params.subCategoryId !== undefined) {
        if (Array.isArray(params.subCategoryId)) {
          queryParams = queryParams.set('subCategoryId', params.subCategoryId.join(','));
        } else {
          queryParams = queryParams.set('subCategoryId', params.subCategoryId.toString());
        }
      }
      if (params.currencyId !== undefined) {
        queryParams = queryParams.set('currencyId', params.currencyId.toString());
      }
      if (params.productId !== undefined) {
        if (Array.isArray(params.productId)) {
          queryParams = queryParams.set('productId', params.productId.join(','));
        } else {
          queryParams = queryParams.set('productId', params.productId.toString());
        }
      }
      if (params.resultBy !== undefined) {
        queryParams = queryParams.set('resultBy', params.resultBy.toString());
      }
    }
    return this.http.get<any>(`${this.SURVEY_SERVICE_URL}/sellOut/top10StoreSurveyDashboardByWeek`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Calls the top 10 store survey dashboard by month API in the backend
   */
  public getTop10StoreSurveyDashboardByMonth(params: {
    countryId?: number | number[],
    monthFrom?: number,
    monthTo?: number,
    yearFrom?: number,
    yearTo?: number,
    categoryId?: number | number[],
    subCategoryId?: number | number[],
    currencyId?: number,
    productId?: number | number[],
    resultBy?: number
  }): Observable<any> {
    let queryParams = new HttpParams();
    if (params) {
      if (params.countryId !== undefined) {
        if (Array.isArray(params.countryId)) {
          queryParams = queryParams.set('countryId', params.countryId.join(','));
        } else {
          queryParams = queryParams.set('countryId', params.countryId.toString());
        }
      }
      if (params.monthFrom !== undefined) {
        queryParams = queryParams.set('monthFrom', params.monthFrom.toString());
      }
      if (params.monthTo !== undefined) {
        queryParams = queryParams.set('monthTo', params.monthTo.toString());
      }
      queryParams = queryParams.set('yearFrom', (params.yearFrom ?? 0).toString());
      queryParams = queryParams.set('yearTo', (params.yearTo ?? 0).toString());
      if (params.categoryId !== undefined) {
        if (Array.isArray(params.categoryId)) {
          queryParams = queryParams.set('categoryId', params.categoryId.join(','));
        } else {
          queryParams = queryParams.set('categoryId', params.categoryId.toString());
        }
      }
      if (params.subCategoryId !== undefined) {
        if (Array.isArray(params.subCategoryId)) {
          queryParams = queryParams.set('subCategoryId', params.subCategoryId.join(','));
        } else {
          queryParams = queryParams.set('subCategoryId', params.subCategoryId.toString());
        }
      }
      if (params.currencyId !== undefined) {
        queryParams = queryParams.set('currencyId', params.currencyId.toString());
      }
      if (params.productId !== undefined) {
        if (Array.isArray(params.productId)) {
          queryParams = queryParams.set('productId', params.productId.join(','));
        } else {
          queryParams = queryParams.set('productId', params.productId.toString());
        }
      }
      if (params.resultBy !== undefined) {
        queryParams = queryParams.set('resultBy', params.resultBy.toString());
      }
    }
    return this.http.get<any>(`${this.SURVEY_SERVICE_URL}/sellOut/top10StoreSurveyDashboardByMonth`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getTop10ProductSurveyDashboardByDate(params: {
    countryId?: number | number[],
    dateFrom?: string,
    dateTo?: string,
    categoryId?: number | number[],
    subCategoryId?: number | number[],
    currencyId?: number,
    productId?: number | number[],
    resultBy?: number
  }): Observable<any> {
    let queryParams = new HttpParams();
    if (params) {
      if (params.countryId !== undefined) {
        if (Array.isArray(params.countryId)) {
          queryParams = queryParams.set('countryId', params.countryId.join(','));
        } else {
          queryParams = queryParams.set('countryId', params.countryId.toString());
        }
      }
      if (params.dateFrom !== undefined) {
        queryParams = queryParams.set('dateFrom', params.dateFrom);
      }
      if (params.dateTo !== undefined) {
        queryParams = queryParams.set('dateTo', params.dateTo);
      }
      if (params.categoryId !== undefined) {
        if (Array.isArray(params.categoryId)) {
          queryParams = queryParams.set('categoryId', params.categoryId.join(','));
        } else {
          queryParams = queryParams.set('categoryId', params.categoryId.toString());
        }
      }
      if (params.subCategoryId !== undefined) {
        if (Array.isArray(params.subCategoryId)) {
          queryParams = queryParams.set('subCategoryId', params.subCategoryId.join(','));
        } else {
          queryParams = queryParams.set('subCategoryId', params.subCategoryId.toString());
        }
      }
      if (params.currencyId !== undefined) {
        queryParams = queryParams.set('currencyId', params.currencyId.toString());
      }
      if (params.productId !== undefined) {
        if (Array.isArray(params.productId)) {
          queryParams = queryParams.set('productId', params.productId.join(','));
        } else {
          queryParams = queryParams.set('productId', params.productId.toString());
        }
      }
      if (params.resultBy !== undefined) {
        queryParams = queryParams.set('resultBy', params.resultBy.toString());
      }
    }
    return this.http.get<any>(`${this.SURVEY_SERVICE_URL}/sellOut/top10ProductSurveyDashboardByDate`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getTop10ProductSurveyDashboardByWeek(params: {
    countryId?: number | number[],
    weekFrom?: number,
    weekTo?: number,
    yearFrom?: number,
    yearTo?: number,
    categoryId?: number | number[],
    subCategoryId?: number | number[],
    currencyId?: number,
    productId?: number | number[],
    resultBy?: number
  }): Observable<any> {
    let queryParams = new HttpParams();
    if (params) {
      if (params.countryId !== undefined) {
        if (Array.isArray(params.countryId)) {
          queryParams = queryParams.set('countryId', params.countryId.join(','));
        } else {
          queryParams = queryParams.set('countryId', params.countryId.toString());
        }
      }
      if (params.weekFrom !== undefined) {
        queryParams = queryParams.set('weekFrom', params.weekFrom.toString());
      }
      if (params.weekTo !== undefined) {
        queryParams = queryParams.set('weekTo', params.weekTo.toString());
      }
      queryParams = queryParams.set('yearFrom', (params.yearFrom ?? 0).toString());
      queryParams = queryParams.set('yearTo', (params.yearTo ?? 0).toString());
      if (params.categoryId !== undefined) {
        if (Array.isArray(params.categoryId)) {
          queryParams = queryParams.set('categoryId', params.categoryId.join(','));
        } else {
          queryParams = queryParams.set('categoryId', params.categoryId.toString());
        }
      }
      if (params.subCategoryId !== undefined) {
        if (Array.isArray(params.subCategoryId)) {
          queryParams = queryParams.set('subCategoryId', params.subCategoryId.join(','));
        } else {
          queryParams = queryParams.set('subCategoryId', params.subCategoryId.toString());
        }
      }
      if (params.currencyId !== undefined) {
        queryParams = queryParams.set('currencyId', params.currencyId.toString());
      }
      if (params.productId !== undefined) {
        if (Array.isArray(params.productId)) {
          queryParams = queryParams.set('productId', params.productId.join(','));
        } else {
          queryParams = queryParams.set('productId', params.productId.toString());
        }
      }
      if (params.resultBy !== undefined) {
        queryParams = queryParams.set('resultBy', params.resultBy.toString());
      }
    }
    return this.http.get<any>(`${this.SURVEY_SERVICE_URL}/sellOut/top10ProductSurveyDashboardByWeek`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getTop10ProductSurveyDashboardByMonth(params: {
    countryId?: number | number[],
    monthFrom?: number,
    monthTo?: number,
    yearFrom?: number,
    yearTo?: number,
    categoryId?: number | number[],
    subCategoryId?: number | number[],
    currencyId?: number,
    productId?: number | number[],
    resultBy?: number
  }): Observable<any> {
    let queryParams = new HttpParams();
    if (params) {
      if (params.countryId !== undefined) {
        if (Array.isArray(params.countryId)) {
          queryParams = queryParams.set('countryId', params.countryId.join(','));
        } else {
          queryParams = queryParams.set('countryId', params.countryId.toString());
        }
      }
      if (params.monthFrom !== undefined) {
        queryParams = queryParams.set('monthFrom', params.monthFrom.toString());
      }
      if (params.monthTo !== undefined) {
        queryParams = queryParams.set('monthTo', params.monthTo.toString());
      }
      queryParams = queryParams.set('yearFrom', (params.yearFrom ?? 0).toString());
      queryParams = queryParams.set('yearTo', (params.yearTo ?? 0).toString());
      if (params.categoryId !== undefined) {
        if (Array.isArray(params.categoryId)) {
          queryParams = queryParams.set('categoryId', params.categoryId.join(','));
        } else {
          queryParams = queryParams.set('categoryId', params.categoryId.toString());
        }
      }
      if (params.subCategoryId !== undefined) {
        if (Array.isArray(params.subCategoryId)) {
          queryParams = queryParams.set('subCategoryId', params.subCategoryId.join(','));
        } else {
          queryParams = queryParams.set('subCategoryId', params.subCategoryId.toString());
        }
      }
      if (params.currencyId !== undefined) {
        queryParams = queryParams.set('currencyId', params.currencyId.toString());
      }
      if (params.productId !== undefined) {
        if (Array.isArray(params.productId)) {
          queryParams = queryParams.set('productId', params.productId.join(','));
        } else {
          queryParams = queryParams.set('productId', params.productId.toString());
        }
      }
      if (params.resultBy !== undefined) {
        queryParams = queryParams.set('resultBy', params.resultBy.toString());
      }
    }
    return this.http.get<any>(`${this.SURVEY_SERVICE_URL}/sellOut/top10ProductSurveyDashboardByMonth`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }


  public getDisplayMatrixSummary(params: {
    accountId: number[], channelId: number[], countryId: number[], storeId: number[], areaId?: number[], stateId?: number[], year?: number, startDate?: string, endDate?: string, brandId?: number[]
  }): Observable<any> {
    let queryParams = new HttpParams();
    // if (params) {
    if (params.countryId.length > 0) {
      queryParams = queryParams.set('countryId', params.countryId.join(','));
    }
    if (params.accountId.length > 0) {
      queryParams = queryParams.set('accountId', params.accountId.join(','));
    }
    if (params.channelId.length > 0) {
      queryParams = queryParams.set('channelId', params.channelId.join(','));
    }
    if (params.storeId.length > 0) {
      queryParams = queryParams.set('storeId', params.storeId.join(','));
    }
    if (params.areaId && params.areaId.length > 0) {
      queryParams = queryParams.set('areaId', params.areaId.join(','));
    }
    if (params.stateId && params.stateId.length > 0) {
      queryParams = queryParams.set('stateId', params.stateId.join(','));
    }
    if (params.brandId && params.brandId.length > 0) {
      queryParams = queryParams.set('brandId', params.brandId.join(','));
    }
    if (params.year !== undefined) {
      queryParams = queryParams.set('year', params.year.toString());
    }
    if (params.startDate) {
      queryParams = queryParams.set('startDate', params.startDate);
    }
    if (params.endDate) {
      queryParams = queryParams.set('endDate', params.endDate);
    }
    // }
    return this.http.get<any>(`${this.SURVEY_SERVICE_URL}/displayMatrix/displayMatrixSummary`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getNoPosmGuidelineReason(): Observable<any> {
    return this.http.get<any>(`${this.SURVEY_SERVICE_URL}/displayMatrix/getNoPosmGuidelineReason`)
      .pipe(catchError(this.errorHandler.handleError));
  }


  public getNoDisplayReason(): Observable<any> {
    return this.http.get<any>(`${this.SURVEY_SERVICE_URL}/displayMatrix/getNoDisplayReason`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get price tracking data with pagination and filters
   * @param params - Filter parameters for price tracking data
   * @returns Observable of price tracking data
   */
  // Get user list for price tracking filter
  public getUserListPriceTracking(countryId?: number[]): Observable<any> {
    let queryParams = new HttpParams();
    if (countryId && countryId.length > 0) {
      queryParams = queryParams.set('countryId', countryId.join(','));
    }
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/survey/getPriceTrackingData/userList`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }



  public getPriceTrackingData(params: {
    pageIndex?: number;
    pageSize?: number;
    searchFilter?: string;
    countryIds?: number[];
    channelIds?: number[];
    accountIds?: number[];
    brandIds?: number[];
    productIds?: number[];
    coreRangeIds?: number[];
    categoryIds?: number[];
    subCategoryIds?: number[];
    startDate?: string;
    endDate?: string;
    isExecuted?: boolean;
    reasonForIsDisplayedPriceTrickingId?: number;
    isPromotionActiveWhileLeaving?: boolean;
    areaId?: number[];
    stateId?: number[];
    userId?: string | number | (string | number)[];
    focusStore?: boolean[],
    projectIds?: string[],
    starProduct?: string,
    channelType?: string
  }): Observable<any> {
    const queryParams: any = {};

    if (params.searchFilter) {
      queryParams.searchFilter = params.searchFilter;
    }
    if (params.pageIndex) {
      queryParams.pageIndex = params.pageIndex;
    }
    if (params.pageSize) {
      queryParams.pageSize = params.pageSize;
    }
    if (params.countryIds && params.countryIds.length > 0) {
      queryParams.countryIds = params.countryIds.join(',');
    }
    if (params.channelIds && params.channelIds.length > 0) {
      queryParams.channelIds = params.channelIds.join(',');
    }
    if (params.accountIds && params.accountIds.length > 0) {
      queryParams.accountIds = params.accountIds.join(',');
    }
    if (params.brandIds && params.brandIds.length > 0) {
      queryParams.brandIds = params.brandIds.join(',');
    }
    if (params.productIds && params.productIds.length > 0) {
      queryParams.productIds = params.productIds.join(',');
    }
    if (params.coreRangeIds && params.coreRangeIds.length > 0) {
      queryParams.coreRangeIds = params.coreRangeIds.join(',');
    }
    if (params.categoryIds && params.categoryIds.length > 0) {
      queryParams.categoryIds = params.categoryIds.join(',');
    }
    if (params.subCategoryIds && params.subCategoryIds.length > 0) {
      queryParams.subCategoryIds = params.subCategoryIds.join(',');
    }
    if (params.startDate) {
      queryParams.startDate = params.startDate;
    }
    if (params.endDate) {
      queryParams.endDate = params.endDate;
    }
    if (params.isExecuted !== undefined) {
      queryParams.isExecuted = params.isExecuted;
    }
    if (params.reasonForIsDisplayedPriceTrickingId !== undefined) {
      queryParams.reason_for_isdisplayed_price_tricking_id =
        params.reasonForIsDisplayedPriceTrickingId;
    }
    if (params.isPromotionActiveWhileLeaving !== undefined) {
      // to be handled by backend as promotion active while leaving filter
      queryParams.is_promotionactivewhileleaving = params.isPromotionActiveWhileLeaving;
    }
    if (params.areaId && params.areaId.length > 0) {
      queryParams.areaId = params.areaId.join(',');
    }
    if (params.stateId && params.stateId.length > 0) {
      queryParams.stateId = params.stateId.join(',');
    }
    if (params.userId) {
      let userIdValue = params.userId;
      if (Array.isArray(userIdValue)) {
        userIdValue = userIdValue.join(',');
      }
      queryParams.userId = userIdValue.toString();
    }
    if (params.focusStore && params.focusStore.length > 0) {
      queryParams.focusStore = params.focusStore.join(',');
    }
    if (params.projectIds && params.projectIds.length > 0) {
      queryParams.projectIds = params.projectIds.join(',');
    }
    if (params.starProduct) {
      queryParams.starProduct = params.starProduct;
    }
    if (params.channelType) {
      queryParams.channelType = params.channelType;
    }
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/survey/getPriceTrackingData`, { params: queryParams })
      .pipe(
        catchError((error) => this.errorHandler.handleError(error))
      );
  }

  /**
   * Get unique models from price tracking data based on filters
   * @param params - Filter parameters for price tracking data
   * @returns Observable of unique models
   */
  public getPriceTrackingModels(params: {
    countryIds?: number[];
    channelIds?: number[];
    accountIds?: number[];
    brandIds?: number[];
    categoryIds?: number[];
    subCategoryIds?: number[];
    startDate?: string;
    endDate?: string;
    isExecuted?: boolean;
    reasonForIsDisplayedPriceTrickingId?: number;
    isPromotionActiveWhileLeaving?: boolean;
    areaId?: number[];
    stateId?: number[];
    userId?: string | number | (string | number)[];
    focusStore?: boolean[];
    projectIds?: string[];
    starProduct?: string;
    channelType?: string;
  }): Observable<any> {
    const queryParams: any = {};

    if (params.countryIds && params.countryIds.length > 0) {
      queryParams.countryIds = params.countryIds.join(',');
    }
    if (params.channelIds && params.channelIds.length > 0) {
      queryParams.channelIds = params.channelIds.join(',');
    }
    if (params.accountIds && params.accountIds.length > 0) {
      queryParams.accountIds = params.accountIds.join(',');
    }
    if (params.brandIds && params.brandIds.length > 0) {
      queryParams.brandIds = params.brandIds.join(',');
    }
    if (params.categoryIds && params.categoryIds.length > 0) {
      queryParams.categoryIds = params.categoryIds.join(',');
    }
    if (params.subCategoryIds && params.subCategoryIds.length > 0) {
      queryParams.subCategoryIds = params.subCategoryIds.join(',');
    }
    if (params.startDate) {
      queryParams.startDate = params.startDate;
    }
    if (params.endDate) {
      queryParams.endDate = params.endDate;
    }
    if (params.isExecuted !== undefined) {
      queryParams.isExecuted = params.isExecuted;
    }
    if (params.reasonForIsDisplayedPriceTrickingId !== undefined) {
      queryParams.reason_for_isdisplayed_price_tricking_id =
        params.reasonForIsDisplayedPriceTrickingId;
    }
    if (params.isPromotionActiveWhileLeaving !== undefined) {
      queryParams.is_promotionactivewhileleaving = params.isPromotionActiveWhileLeaving;
    }
    if (params.areaId && params.areaId.length > 0) {
      queryParams.areaId = params.areaId.join(',');
    }
    if (params.stateId && params.stateId.length > 0) {
      queryParams.stateId = params.stateId.join(',');
    }
    if (params.userId) {
      if (Array.isArray(params.userId)) {
        queryParams.userId = params.userId.join(',');
      } else {
        queryParams.userId = params.userId;
      }
    }

    if (params.focusStore && params.focusStore.length > 0) {
      queryParams.focusStore = params.focusStore.join(',');
    }
    if (params.projectIds && params.projectIds.length > 0) {
      queryParams.projectIds = params.projectIds.join(',');
    }
    if (params.starProduct) {
      queryParams.starProduct = params.starProduct;
    }
    if (params.channelType) {
      queryParams.channelType = params.channelType;
    }

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/survey/getPriceTrackingModels`, { params: queryParams })
      .pipe(
        catchError((error) => this.errorHandler.handleError(error))
      );
  }

  /**
   * Get stock tracker data with pagination and filters
   * @param params - Filter parameters for stock tracker data
   * @returns Observable of stock tracker data
   */
  public getStockTrackerData(params: {
    pageIndex?: number;
    pageSize?: number;
    searchFilter?: string;
    countryIds?: number[];
    channelIds?: number[];
    accountIds?: number[];
    categoryIds?: number[];
    subCategoryIds?: number[];
    brandIds?: number[];
    productIds?: number[];
    startDate?: string;
    endDate?: string;
    isExecuted?: string;
    areaId?: number[];
    stateId?: number[];
    userId?: string | number | (string | number)[];
    storeIds?: number[];
    focusStore?: string;
    projectNameId?: number[];
    starProduct?: string;
    channelType?: string;
  }): Observable<any> {
    const queryParams: any = {};

    if (params.searchFilter) {
      queryParams.searchValue = params.searchFilter;
    }
    if (params.pageIndex) {
      queryParams.pageIndex = params.pageIndex;
    }
    if (params.pageSize) {
      queryParams.pageSize = params.pageSize;
    }
    if (params.countryIds && params.countryIds.length > 0) {
      queryParams.countryIds = params.countryIds.join(',');
    }
    if (params.channelIds && params.channelIds.length > 0) {
      queryParams.channelIds = params.channelIds.join(',');
    }
    if (params.accountIds && params.accountIds.length > 0) {
      queryParams.accountIds = params.accountIds.join(',');
    }
    if (params.categoryIds && params.categoryIds.length > 0) {
      queryParams.categoryIds = params.categoryIds.join(',');
    }
    if (params.subCategoryIds && params.subCategoryIds.length > 0) {
      queryParams.subCategoryIds = params.subCategoryIds.join(',');
    }
    if (params.brandIds && params.brandIds.length > 0) {
      queryParams.brandIds = params.brandIds.join(',');
    }
    if (params.productIds && params.productIds.length > 0) {
      queryParams.productIds = params.productIds.join(',');
    }
    if (params.startDate) {
      queryParams.startDate = params.startDate;
    }
    if (params.endDate) {
      queryParams.endDate = params.endDate;
    }
    if (params.isExecuted !== undefined) {
      queryParams.isExecuted = params.isExecuted;
    }
    if (params.areaId && params.areaId.length > 0) {
      queryParams.areaIds = params.areaId.join(',');
    }
    if (params.stateId && params.stateId.length > 0) {
      queryParams.stateIds = params.stateId.join(',');
    }
    if (params.userId) {
      let userIdValue = params.userId;
      if (Array.isArray(userIdValue)) {
        userIdValue = userIdValue.join(',');
      }
      queryParams.userIds = userIdValue.toString();
    }
    if (params.storeIds && params.storeIds.length > 0) {
      queryParams.storeIds = params.storeIds.join(',');
    }
    if (params.focusStore) {
      queryParams.focusStore = params.focusStore;
    }
    if (params.projectNameId && params.projectNameId.length > 0) {
      queryParams.projectNameId = params.projectNameId.join(',');
    }
    if (params.starProduct) {
      queryParams.starProduct = params.starProduct;
    }
    if (params.channelType) {
      queryParams.channelType = params.channelType;
    }
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/stockManagement/getStockTrackerData`, { params: queryParams })
      .pipe(
        catchError((error) => this.errorHandler.handleError(error))
      );
  }

  /**
   * Get unique models from stock tracker data based on filters
   * @param params - Filter parameters for stock tracker data
   * @returns Observable of unique models
   */
  public getStockTrackerModels(params: {
    countryIds?: number[];
    channelIds?: number[];
    accountIds?: number[];
    brandIds?: number[];
    categoryIds?: number[];
    subCategoryIds?: number[];
    startDate?: string;
    endDate?: string;
    isExecuted?: string;
    areaId?: number[];
    stateId?: number[];
    userId?: string | number | (string | number)[];
    storeIds?: number[];
    focusStore?: string;
    projectNameId?: number[];
  }): Observable<any> {
    const queryParams: any = {};

    if (params.countryIds && params.countryIds.length > 0) {
      queryParams.countryIds = params.countryIds.join(',');
    }
    if (params.channelIds && params.channelIds.length > 0) {
      queryParams.channelIds = params.channelIds.join(',');
    }
    if (params.accountIds && params.accountIds.length > 0) {
      queryParams.accountIds = params.accountIds.join(',');
    }
    if (params.brandIds && params.brandIds.length > 0) {
      queryParams.brandIds = params.brandIds.join(',');
    }
    if (params.categoryIds && params.categoryIds.length > 0) {
      queryParams.categoryIds = params.categoryIds.join(',');
    }
    if (params.subCategoryIds && params.subCategoryIds.length > 0) {
      queryParams.subCategoryIds = params.subCategoryIds.join(',');
    }
    if (params.startDate) {
      queryParams.startDate = params.startDate;
    }
    if (params.endDate) {
      queryParams.endDate = params.endDate;
    }
    if (params.isExecuted !== undefined) {
      queryParams.isExecuted = params.isExecuted;
    }
    if (params.areaId && params.areaId.length > 0) {
      queryParams.areaIds = params.areaId.join(',');
    }
    if (params.stateId && params.stateId.length > 0) {
      queryParams.stateIds = params.stateId.join(',');
    }
    if (params.userId) {
      if (Array.isArray(params.userId)) {
        queryParams.userIds = params.userId.join(',');
      } else {
        queryParams.userIds = params.userId;
      }
    }
    if (params.storeIds && params.storeIds.length > 0) {
      queryParams.storeIds = params.storeIds.join(',');
    }
    if (params.focusStore) {
      queryParams.focusStore = params.focusStore;
    }
    if (params.projectNameId && params.projectNameId.length > 0) {
      queryParams.projectNameId = params.projectNameId.join(',');
    }

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/stockManagement/getStockTrackerModels`, { params: queryParams })
      .pipe(
        catchError((error) => this.errorHandler.handleError(error))
      );
  }


  public getStockUserList(countryId?: number[]): Observable<any> {
    let queryParams = new HttpParams();
    if (countryId && countryId.length > 0) {
      queryParams = queryParams.set('countryId', countryId.join(','));
    }
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/stockManagement/getStockUserList`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  // Get user list for stock tracker filter
  public getUserListStockTracker(countryId?: number[]): Observable<any> {
    let queryParams = new HttpParams();
    if (countryId && countryId.length > 0) {
      queryParams = queryParams.set('countryId', countryId.join(','));
    }
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/stockManagement/getStockTrackerData/userList`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  // Get stock PDF data based on store selection
  public getStockPDF(params: {
    countryIds?: number[];
    storeIds: number[];
    brandIds?: number[];
    startDate?: string;
    endDate?: string;
    includeAll?: boolean;
  }): Observable<any> {
    let queryParams = new HttpParams();

    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }
    if (params.storeIds && params.storeIds.length > 0) {
      queryParams = queryParams.set('storeIds', params.storeIds.join(','));
    }
    if (params.brandIds && params.brandIds.length > 0) {
      queryParams = queryParams.set('brandIds', params.brandIds.join(','));
    }
    if (params.startDate) {
      queryParams = queryParams.set('startDate', params.startDate);
    }
    if (params.endDate) {
      queryParams = queryParams.set('endDate', params.endDate);
    }
    if (params.includeAll !== undefined) {
      queryParams = queryParams.set('includeAll', params.includeAll.toString());
    }

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/stockManagement/getStockPDF`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  // Export stock tracker data to Excel
  public exportStockTrackerData(params: {
    searchFilter?: string;
    countryIds?: number[];
    channelIds?: number[];
    accountIds?: number[];
    categoryIds?: number[];
    subCategoryIds?: number[];
    brandIds?: number[];
    productIds?: number[];
    startDate?: string;
    endDate?: string;
    isExecuted?: string;
    areaId?: number[];
    stateId?: number[];
    userId?: string | number | (string | number)[];
    storeIds?: number[];
    focusStore?: string;
    projectNameId?: number[];
    starProduct?: string;
    channelType?: string;
  }): Observable<Blob> {
    const queryParams: any = {};

    if (params.searchFilter) {
      queryParams.searchValue = params.searchFilter;
    }
    if (params.countryIds && params.countryIds.length > 0) {
      queryParams.countryIds = params.countryIds.join(',');
    }
    if (params.channelIds && params.channelIds.length > 0) {
      queryParams.channelIds = params.channelIds.join(',');
    }
    if (params.accountIds && params.accountIds.length > 0) {
      queryParams.accountIds = params.accountIds.join(',');
    }
    if (params.categoryIds && params.categoryIds.length > 0) {
      queryParams.categoryIds = params.categoryIds.join(',');
    }
    if (params.subCategoryIds && params.subCategoryIds.length > 0) {
      queryParams.subCategoryIds = params.subCategoryIds.join(',');
    }
    if (params.brandIds && params.brandIds.length > 0) {
      queryParams.brandIds = params.brandIds.join(',');
    }
    if (params.productIds && params.productIds.length > 0) {
      queryParams.productIds = params.productIds.join(',');
    }
    if (params.startDate) {
      queryParams.startDate = params.startDate;
    }
    if (params.endDate) {
      queryParams.endDate = params.endDate;
    }
    if (params.isExecuted !== undefined) {
      queryParams.isExecuted = params.isExecuted;
    }
    if (params.areaId && params.areaId.length > 0) {
      queryParams.areaIds = params.areaId.join(',');
    }
    if (params.stateId && params.stateId.length > 0) {
      queryParams.stateIds = params.stateId.join(',');
    }
    if (params.userId) {
      let userIdValue = params.userId;
      if (Array.isArray(userIdValue)) {
        userIdValue = userIdValue.join(',');
      }
      queryParams.userIds = userIdValue.toString();
    }
    if (params.storeIds && params.storeIds.length > 0) {
      queryParams.storeIds = params.storeIds.join(',');
    }
    if (params.focusStore) {
      queryParams.focusStore = params.focusStore;
    }
    if (params.projectNameId && params.projectNameId.length > 0) {
      queryParams.projectNameId = params.projectNameId.join(',');
    }
    if (params.starProduct) {
      queryParams.starProduct = params.starProduct;
    }
    if (params.channelType) {
      queryParams.channelType = params.channelType;
    }
    return this.http
      .post(`${this.SURVEY_SERVICE_URL}/stockManagement/downloadExcelReport`, {}, {
        params: queryParams,
        responseType: 'blob'
      })
      .pipe(
        catchError((error) => this.errorHandler.handleError(error))
      );
  }



  public exportPriceTrackingData(params: {
    searchFilter?: string;
    countryIds?: number[];
    channelIds?: number[];
    accountIds?: number[];
    brandIds?: number[];
    productIds?: number[];
    coreRangeIds?: number[];
    categoryIds?: number[];
    subCategoryIds?: number[];
    startDate?: string;
    endDate?: string;
    isExecuted?: boolean;
    reasonForIsDisplayedPriceTrickingId?: number;
    isPromotionActiveWhileLeaving?: boolean;
    exportImageType?: number;
    areaId?: number[];
    stateId?: number[];
    userId?: string | number | (string | number)[];
    focusStore?: boolean[],
    projectIds?: string[],
    starProduct?: string,
    channelType?: string;
  }): Observable<Blob> {
    const queryParams: any = {};

    if (params.searchFilter) {
      queryParams.searchFilter = params.searchFilter;
    }
    if (params.countryIds && params.countryIds.length > 0) {
      queryParams.countryIds = params.countryIds.join(',');
    }
    if (params.channelIds && params.channelIds.length > 0) {
      queryParams.channelIds = params.channelIds.join(',');
    }
    if (params.accountIds && params.accountIds.length > 0) {
      queryParams.accountIds = params.accountIds.join(',');
    }
    if (params.brandIds && params.brandIds.length > 0) {
      queryParams.brandIds = params.brandIds.join(',');
    }
    if (params.productIds && params.productIds.length > 0) {
      queryParams.productIds = params.productIds.join(',');
    }
    if (params.coreRangeIds && params.coreRangeIds.length > 0) {
      queryParams.coreRangeIds = params.coreRangeIds.join(',');
    }
    if (params.categoryIds && params.categoryIds.length > 0) {
      queryParams.categoryIds = params.categoryIds.join(',');
    }
    if (params.subCategoryIds && params.subCategoryIds.length > 0) {
      queryParams.subCategoryIds = params.subCategoryIds.join(',');
    }
    if (params.startDate) {
      queryParams.startDate = params.startDate;
    }
    if (params.endDate) {
      queryParams.endDate = params.endDate;
    }
    if (params.isExecuted !== undefined) {
      queryParams.isExecuted = params.isExecuted;
    }
    if (params.reasonForIsDisplayedPriceTrickingId !== undefined) {
      queryParams.reason_for_isdisplayed_price_tricking_id =
        params.reasonForIsDisplayedPriceTrickingId;
    }
    if (params.isPromotionActiveWhileLeaving !== undefined) {
      queryParams.is_promotionactivewhileleaving = params.isPromotionActiveWhileLeaving;
    }
    if (params.exportImageType !== undefined) {
      queryParams.exportImageType = params.exportImageType;
    }
    if (params.areaId && params.areaId.length > 0) {
      queryParams.areaId = params.areaId.join(',');
    }
    if (params.stateId && params.stateId.length > 0) {
      queryParams.stateId = params.stateId.join(',');
    }
    if (params.userId) {
      let userIdValue = params.userId;
      if (Array.isArray(userIdValue)) {
        userIdValue = userIdValue.join(',');
      }
      queryParams.userId = userIdValue.toString();
    }
    if (params.focusStore && params.focusStore.length > 0) {
      queryParams.focusStore = params.focusStore.join(',');
    }
    if (params.projectIds && params.projectIds.length > 0) {
      queryParams.projectIds = params.projectIds.join(',');
    }
    if (params.starProduct) {
      queryParams.starProduct = params.starProduct;
    }
    if (params.channelType) {
      queryParams.channelType = params.channelType;
    }
    return this.http
      .post(`${this.SURVEY_SERVICE_URL}/survey/priceTracking/downloadExcelReport`, {}, {
        params: queryParams,
        responseType: 'blob'
      })
      .pipe(
        catchError((error) => this.errorHandler.handleError(error))
      );
  }

  public downloadPriceTrackingMediaStream(
    id: string,
    fileIndex: string,
    kind: string
  ): Observable<HttpResponse<Blob>> {
    const queryParams = new HttpParams()
      .set('id', id)
      .set('fileIndex', fileIndex || '0')
      .set('kind', kind || 'price-tracking-image');
    const headers = new HttpHeaders().set('isBlob', 'true');

    return this.http
      .get(`${this.SURVEY_SERVICE_URL}/survey/priceTracking/media/file`, {
        params: queryParams,
        observe: 'response',
        responseType: 'blob',
        headers,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get all product go price mappings with pagination and search functionality
   * @param params - Parameters for pagination and filtering
   * @returns Observable of product go price mappings with pagination
   */
  public getAllPriceMappingUpload(params: {
    pageIndex?: number;
    pageSize?: number;
    searchValue?: string;
    accountIds?: number[];
    countryIds?: number[];
    productId?: number;
  }): Observable<any> {
    let queryParams = new HttpParams();

    if (params.pageIndex) {
      queryParams = queryParams.set('pageIndex', params.pageIndex.toString());
    }
    if (params.pageSize) {
      queryParams = queryParams.set('pageSize', params.pageSize.toString());
    }
    if (params.searchValue) {
      queryParams = queryParams.set('searchValue', params.searchValue);
    }
    if (params.accountIds && params.accountIds.length > 0) {
      queryParams = queryParams.set('accountIds', params.accountIds.join(','));
    }
    if (params.productId) {
      queryParams = queryParams.set('productId', params.productId.toString());
    }
    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/productGoPrice/getAllPriceMappingUpload`, { params: queryParams })
      .pipe(
        catchError((error) => this.errorHandler.handleError(error))
      );
  }


  public updateDisplayMatrix(id: number, formData: FormData): Observable<any> {

    return this.http.post<any>(`${this.SURVEY_SERVICE_URL}/displayMatrix/updateDisplayMatrix`, formData)
      .pipe(catchError(this.errorHandler.handleError));
  }


  public deleteDisplayMatrix(id: number): Observable<any> {
    let queryParams = new HttpParams();
    return this.http.delete<any>(`${this.SURVEY_SERVICE_URL}/displayMatrix/deleteDisplayMatrix/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  private buildDisplayComplianceListParams(params: {
    pageIndex: any;
    pageSize: any;
    searchFilter?: string;
    country?: string[];
    store?: string[];
    product?: string[];
    startDate?: string;
    endDate?: string;
    channel?: string[];
    account?: string[];
    category?: string[];
    subCategory?: string[];
    displayType?: string[];
    segmentId?: number[];
    isPosm?: string;
    isPosmAsPerGuidline?: string;
    areaId?: number[];
    stateId?: number[];
    userId?: string | number | (string | number)[];
    brandId?: number[];
    model?: string[];
    starProduct?: string | number | number[];
    channelType?: string | number | number[];
    focusStore?: string;
    projectNameId?: number[];
  }): HttpParams {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize);
    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }
    if (params.country?.length) {
      queryParams = queryParams.set('country', params.country.join(','));
    }
    if (params.product?.length) {
      queryParams = queryParams.set('product', params.product.join(','));
    }
    if (params.category?.length) {
      queryParams = queryParams.set('category', params.category.join(','));
    }
    if (params.brandId?.length) {
      queryParams = queryParams.set('brandId', params.brandId.join(','));
    }
    if (params.model?.length) {
      queryParams = queryParams.set('model', params.model.join(','));
    }
    if (params.subCategory?.length) {
      queryParams = queryParams.set('subCategory', params.subCategory.join(','));
    }
    if (params.startDate) {
      queryParams = queryParams.set('startDate', params.startDate);
    }
    if (params.endDate) {
      queryParams = queryParams.set('endDate', params.endDate);
    }
    if (params.account?.length) {
      queryParams = queryParams.set('account', params.account.join(','));
    }
    if (params.channel?.length) {
      queryParams = queryParams.set('channel', params.channel.join(','));
    }
    if (params.store?.length) {
      queryParams = queryParams.set('store', params.store.join(','));
    }
    if (params.displayType?.length) {
      queryParams = queryParams.set('displayType', params.displayType.join(','));
    }
    if (params.segmentId?.length) {
      queryParams = queryParams.set('segmentId', params.segmentId.join(','));
    }
    if (params.isPosm) {
      queryParams = queryParams.set('isPosm', params.isPosm);
    }
    if (params.isPosmAsPerGuidline) {
      queryParams = queryParams.set('isPosmAsPerGuidline', params.isPosmAsPerGuidline);
    }
    if (params.areaId?.length) {
      queryParams = queryParams.set('areaId', params.areaId.join(','));
    }
    if (params.stateId?.length) {
      queryParams = queryParams.set('stateId', params.stateId.join(','));
    }
    if (params.userId) {
      const userIdValue = Array.isArray(params.userId)
        ? params.userId.join(',')
        : params.userId.toString();
      queryParams = queryParams.set('userId', userIdValue);
    }
    if (params.starProduct) {
      const starProduct = Array.isArray(params.starProduct)
        ? params.starProduct.join(',')
        : params.starProduct;
      queryParams = queryParams.set('starProduct', starProduct);
    }
    if (params.channelType) {
      const channelType = Array.isArray(params.channelType)
        ? params.channelType.join(',')
        : params.channelType;
      queryParams = queryParams.set('channelType', channelType);
    }
    if (params.focusStore) {
      queryParams = queryParams.set('focusStore', params.focusStore);
    }
    if (params.projectNameId?.length) {
      queryParams = queryParams.set('projectNameId', params.projectNameId.join(','));
    }
    return queryParams;
  }

  public getUserListDisplayCompliance(countryId?: number[]): Observable<any> {
    let queryParams = new HttpParams();
    if (countryId?.length) {
      queryParams = queryParams.set('countryId', countryId.join(','));
    }
    return this.http
      .get<any>(
        `${this.SURVEY_SERVICE_URL}/displayCompliance/getAllDisplayMatrixDetails/userList`,
        { params: queryParams },
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getDisplayComplianceSyncStatus(): Observable<{
    lastSyncTime: string | null;
    syncedBy: number | null;
  }> {
    return this.http
      .get<{ result?: { lastSyncTime: string | null; syncedBy: number | null } }>(
        `${this.SURVEY_SERVICE_URL}/displayCompliance/syncStatus`,
      )
      .pipe(
        map((r) => r?.result ?? { lastSyncTime: null, syncedBy: null }),
        catchError(this.errorHandler.handleError),
      );
  }

  public refreshDisplayComplianceData(): Observable<{
    accepted: boolean;
    inProgress: boolean;
    lastSyncTime: string | null;
  }> {
    return this.http
      .post<{
        result?: {
          accepted: boolean;
          inProgress: boolean;
          lastSyncTime: string | null;
        };
      }>(`${this.SURVEY_SERVICE_URL}/displayCompliance/refreshData`, {})
      .pipe(
        map(
          (r) =>
            r?.result ?? {
              accepted: false,
              inProgress: false,
              lastSyncTime: null,
            },
        ),
        catchError(this.errorHandler.handleError),
      );
  }

  public getAllDisplayCompliance(params: {
    pageIndex: any;
    pageSize: any;
    searchFilter?: string;
    country?: string[];
    store?: string[];
    product?: string[];
    startDate?: string;
    endDate?: string;
    channel?: string[];
    account?: string[];
    category?: string[];
    subCategory?: string[];
    displayType?: string[];
    segmentId?: number[];
    isPosm?: string;
    isPosmAsPerGuidline?: string;
    areaId?: number[];
    stateId?: number[];
    userId?: string | number | (string | number)[];
    brandId?: number[];
    model?: string[];
    starProduct?: string | number | number[];
    channelType?: string | number | number[];
    focusStore?: string;
    projectNameId?: number[];
  }): Observable<any> {
    const queryParams = this.buildDisplayComplianceListParams(params);
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/displayCompliance/getAllDisplayMatrixDetails`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  private appendDisplayHealthFilters(
    queryParams: HttpParams,
    params: {
      month?: number;
      year?: number;
      productTier?: number[];
      projectNameId?: number[];
      fixtureShare?: 'yes' | 'no';
    },
  ): HttpParams {
    if (params.month != null) {
      queryParams = queryParams.set('month', String(params.month));
    }
    if (params.year != null) {
      queryParams = queryParams.set('year', String(params.year));
    }
    if (params.productTier?.length) {
      queryParams = queryParams.set('productTier', params.productTier.join(','));
    }
    if (params.projectNameId?.length) {
      queryParams = queryParams.set('projectNameId', params.projectNameId.join(','));
    }
    if (params.fixtureShare === 'yes' || params.fixtureShare === 'no') {
      queryParams = queryParams.set('fixtureShare', params.fixtureShare);
    }
    return queryParams;
  }

  public getDisplayHealthKpis(params: {
    country?: Array<string | number>;
    month?: number;
    year?: number;
    productTier?: number[];
    projectNameId?: number[];
    fixtureShare?: 'yes' | 'no';
  }): Observable<any> {
    let queryParams = new HttpParams();
    if (params.country?.length) {
      queryParams = queryParams.set('country', params.country.join(','));
    }
    queryParams = this.appendDisplayHealthFilters(queryParams, params);
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/displayCompliance/displayHealthKpis`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getDisplayHealthChart(params: {
    tab?: string;
    country?: Array<string | number>;
    month?: number;
    year?: number;
    productTier?: number[];
    projectNameId?: number[];
    fixtureShare?: 'yes' | 'no';
  }): Observable<any> {
    let queryParams = new HttpParams();
    if (params.tab) {
      queryParams = queryParams.set('tab', params.tab);
    }
    if (params.country?.length) {
      queryParams = queryParams.set('country', params.country.join(','));
    }
    queryParams = this.appendDisplayHealthFilters(queryParams, params);
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/displayCompliance/displayHealthChart`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getDisplayHealthScoreTable(params: {
    country?: Array<string | number>;
    month?: number;
    year?: number;
    productTier?: number[];
    projectNameId?: number[];
    fixtureShare?: 'yes' | 'no';
  }): Observable<any> {
    let queryParams = new HttpParams();
    if (params.country?.length) {
      queryParams = queryParams.set('country', params.country.join(','));
    }
    queryParams = this.appendDisplayHealthFilters(queryParams, params);
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/displayCompliance/displayHealthScoreTable`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getDisplayHealthStoreTable(params: {
    country?: Array<string | number>;
    month?: number;
    year?: number;
    productTier?: number[];
    projectNameId?: number[];
    fixtureShare?: 'yes' | 'no';
  }): Observable<any> {
    let queryParams = new HttpParams();
    if (params.country?.length) {
      queryParams = queryParams.set('country', params.country.join(','));
    }
    queryParams = this.appendDisplayHealthFilters(queryParams, params);
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/displayCompliance/displayHealthStoreTable`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getDisplayHealthScoreModels(params: {
    country?: Array<string | number>;
    month?: number;
    year?: number;
    productTier?: number[];
    projectNameId?: number[];
    fixtureShare?: 'yes' | 'no';
    storeId?: number;
  }): Observable<any> {
    let queryParams = new HttpParams();
    if (params.country?.length) {
      queryParams = queryParams.set('country', params.country.join(','));
    }
    queryParams = this.appendDisplayHealthFilters(queryParams, params);
    if (params.storeId != null) {
      queryParams = queryParams.set('storeId', String(params.storeId));
    }
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/displayCompliance/displayHealthScoreModels`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getDisplayComplianceModels(params: {
    pageIndex: number;
    pageSize: number;
    searchFilter?: string;
    country?: number[];
    startDate: string;
    endDate: string;
    category?: number[];
    subCategory?: number[];
    brandId?: number[];
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize)
      .set('startDate', params.startDate)
      .set('endDate', params.endDate);
    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }
    if (params.country?.length) {
      queryParams = queryParams.set('country', params.country.join(','));
    }
    if (params.category?.length) {
      queryParams = queryParams.set('category', params.category.join(','));
    }
    if (params.subCategory?.length) {
      queryParams = queryParams.set('subCategory', params.subCategory.join(','));
    }
    if (params.brandId?.length) {
      queryParams = queryParams.set('brandId', params.brandId.join(','));
    }
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/displayCompliance/getModels`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public exportDisplayCompliance(params: {
    pageIndex: any;
    pageSize: any;
    exportImageType: number;
    searchFilter?: string;
    country?: number[];
    storeName?: string[];
    product?: string[];
    startDate?: string;
    endDate?: string;
    channel?: string[];
    account?: string[];
    category?: string[];
    subCategory?: string[];
    displayType?: number[];
    segmentId?: number[];
    isPosmAsPerGuidline?: string;
    noPosmGuidlinesReasonName?: string;
    isPosm?: string;
    areaId?: number[];
    stateId?: number[];
    userId?: string | number | (string | number)[];
    brandId?: number[];
    model?: string[];
    starProduct?: string | number[];
    channelType?: string | number[];
    focusStore?: string;
    projectNameId?: number[];
  }): Observable<Blob> {
    const columns: string[] = [
      'Country',
      'Store Code',
      'Store',
      'Focus Store',
      'City',
      'State',
      'Area Group',
      'Channel',
      'Account',
      'Date',
      'Category',
      'Sub Category',
      'Segment',
      'Brand',
      'Project Name',
      'MDA/SDA/O',
      'Model',
      'PNC',
      'Display Type',
      'Quantity',
      'Is POSM available',
      'No Posm Guidlines Reason',
      'Is POSM as per guidline',
      'Reason for not as per guideline',
      'Before Image 1',
      'After Image 1',
      'Uploaded By',
      'Created By',
      'Created Date & Time',
      'Updated Date & Time',
    ];
    return this.http
      .post<Blob>(
        `${this.SURVEY_SERVICE_URL}/displayCompliance/downloadExcelReport`,
        { columns, ...params },
        { responseType: 'blob' as 'json' },
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public buildDisplayComplianceExportStartBody(
    params: Record<string, unknown>,
  ): Record<string, unknown> {
    return {
      columns: this.displayComplianceExportColumns(),
      ...params,
      pageIndex: params['pageIndex'] ?? 1,
      pageSize: params['pageSize'] ?? 50,
    };
  }

  private displayComplianceExportColumns(): string[] {
    return [
      'Country',
      'Store Code',
      'Store',
      'Focus Store',
      'City',
      'State',
      'Area Group',
      'Channel',
      'Account',
      'Date',
      'Category',
      'Sub Category',
      'Segment',
      'Brand',
      'Project Name',
      'Model',
      'PNC',
      'Display Type',
      'Quantity',
      'Is POSM available',
      'No Posm Guidlines Reason',
      'Is POSM as per guidline',
      'Reason for not as per guideline',
      'Before Image 1',
      'After Image 1',
      'Uploaded By',
      'Created By',
      'Created Date & Time',
      'Updated Date & Time',
    ];
  }

  public startDisplayComplianceExportJob(
    params: Record<string, unknown>,
  ): Observable<{ jobId: string }> {
    const skipDup = new HttpContext().set(SKIP_DUPLICATE_CHECK, true);
    return this.http
      .post<any>(`${this.DISPLAY_COMPLIANCE_EXPORT_URL}/async-export/start`, {
        columns: this.displayComplianceExportColumns(),
        pageIndex: 1,
        pageSize: 50,
        ...params,
      }, { context: skipDup })
      .pipe(
        map((r: any) => r?.result ?? r),
        map((inner: any) => {
          const jobId = inner?.jobId;
          if (!jobId) {
            throw new Error('No job id returned');
          }
          return { jobId: String(jobId) };
        }),
        catchError(this.errorHandler.handleError),
      );
  }

  public getDisplayComplianceExportJobStatus(jobId: string): Observable<any> {
    const skipDup = new HttpContext().set(SKIP_DUPLICATE_CHECK, true);
    return this.http
      .get<any>(
        `${this.DISPLAY_COMPLIANCE_EXPORT_URL}/async-export/jobs/${jobId}`,
        { context: skipDup },
      )
      .pipe(
        map((r: any) => r?.result ?? r),
        catchError(this.errorHandler.handleError),
      );
  }

  public listDisplayComplianceExportJobs(): Observable<any[]> {
    const skipDup = new HttpContext().set(SKIP_DUPLICATE_CHECK, true);
    return this.http
      .get<any>(`${this.DISPLAY_COMPLIANCE_EXPORT_URL}/async-export/jobs`, {
        context: skipDup,
      })
      .pipe(
        map((r: any) => {
          const list = r?.result ?? r;
          return Array.isArray(list) ? list : [];
        }),
        catchError(this.errorHandler.handleError),
      );
  }

  public markDisplayComplianceExportDownloaded(jobId: string): Observable<any> {
    const skipDup = new HttpContext().set(SKIP_DUPLICATE_CHECK, true);
    return this.http
      .post<any>(
        `${this.DISPLAY_COMPLIANCE_EXPORT_URL}/async-export/jobs/${jobId}/downloaded`,
        {},
        { context: skipDup },
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public exportDisplayComplianceAsync(
    params: Record<string, unknown>,
    progress: ExcelExportProgressSlot,
  ): Observable<void> {
    return this.asyncExportService
      .runAsyncExport(
        this.displayComplianceExportApi,
        params,
        progress,
        (body) => this.buildDisplayComplianceExportStartBody(body),
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public cancelDisplayComplianceExportJob(jobId: string): Observable<unknown> {
    return this.asyncExportService
      .cancelJob(this.displayComplianceExportApi.apiBaseUrl, jobId)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public downloadDisplayComplianceMediaStream(
    id: string,
    fileIndex: string,
    kind: string,
  ): Observable<HttpResponse<Blob>> {
    const params = new HttpParams()
      .set('id', id)
      .set('fileIndex', fileIndex || '0')
      .set('kind', kind || 'display-compliance-before');
    const headers = new HttpHeaders().set('isBlob', 'true');
    return this.http
      .get(`${this.SURVEY_SERVICE_URL}/displayCompliance/media/file`, {
        params,
        observe: 'response',
        responseType: 'blob',
        headers,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getDisplayComplianceSummary(params: {
    accountId: number[];
    channelId: number[];
    countryId: number[];
    storeId: number[];
    areaId?: number[];
    stateId?: number[];
    year?: number;
    startDate?: string;
    endDate?: string;
    brandId?: number[];
    categoryId?: number[];
    subCategoryId?: number[];
  }): Observable<any> {
    let queryParams = new HttpParams();
    if (params.countryId.length > 0) {
      queryParams = queryParams.set('countryId', params.countryId.join(','));
    }
    if (params.accountId.length > 0) {
      queryParams = queryParams.set('accountId', params.accountId.join(','));
    }
    if (params.channelId.length > 0) {
      queryParams = queryParams.set('channelId', params.channelId.join(','));
    }
    if (params.storeId.length > 0) {
      queryParams = queryParams.set('storeId', params.storeId.join(','));
    }
    if (params.areaId?.length) {
      queryParams = queryParams.set('areaId', params.areaId.join(','));
    }
    if (params.stateId?.length) {
      queryParams = queryParams.set('stateId', params.stateId.join(','));
    }
    if (params.year !== undefined) {
      queryParams = queryParams.set('year', params.year.toString());
    }
    if (params.startDate) {
      queryParams = queryParams.set('startDate', params.startDate);
    }
    if (params.endDate) {
      queryParams = queryParams.set('endDate', params.endDate);
    }
    if (params.brandId && params.brandId.length > 0) {
      queryParams = queryParams.set('brandId', params.brandId.join(','));
    }
    if (params.categoryId && params.categoryId.length > 0) {
      queryParams = queryParams.set('categoryId', params.categoryId.join(','));
    }
    if (params.subCategoryId && params.subCategoryId.length > 0) {
      queryParams = queryParams.set('subCategoryId', params.subCategoryId.join(','));
    }
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/displayCompliance/displayMatrixSummary`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getNoDisplayReasonForCompliance(): Observable<any> {
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/displayCompliance/getNoDisplayReason`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getNoPosmGuidelineReasonForCompliance(): Observable<any> {
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/displayCompliance/getNoPosmGuidelineReason`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateDisplayCompliance(_id: number, formData: FormData): Observable<any> {
    return this.http
      .post<any>(`${this.SURVEY_SERVICE_URL}/displayCompliance/updateDisplayMatrix`, formData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteDisplayCompliance(id: number): Observable<any> {
    return this.http
      .delete<any>(`${this.SURVEY_SERVICE_URL}/displayCompliance/deleteDisplayMatrix/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateDisplayPlanogram(
    id: number,
    body: {
      quantity?: number;
      category_ids?: number[];
      sub_category_ids?: number[];
      planogram_type_id?: number;
      fixture_display_location_id?: number;
    }
  ): Observable<any> {
    return this.http
      .put<any>(`${this.SURVEY_SERVICE_URL}/displayPlanogram/update/${id}`, body)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getFixtureDisplayLocationsByCountry(): Observable<any> {
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/fixtureDisplayLocation/getByCountryId`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteDisplayPlanogram(id: number): Observable<any> {
    let queryParams = new HttpParams();
    return this.http.delete<any>(`${this.SURVEY_SERVICE_URL}/displayPlanogram/delete/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateDisplayShare(id: number, quantity: number): Observable<any> {
    let queryParams: any = {};
    if (quantity) {
      queryParams.quantity = quantity;
    }
    return this.http.put<any>(`${this.SURVEY_SERVICE_URL}/displayShare/update/${id}`, queryParams)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteDisplayShare(id: number): Observable<any> {
    let queryParams = new HttpParams();
    return this.http.delete<any>(`${this.SURVEY_SERVICE_URL}/displayShare/delete/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getBrandMapByCountryId(countryId: number, pageIndex?: number, pageSize?: number): Observable<any> {
    let params = new HttpParams();
    if (pageIndex !== undefined && pageSize !== undefined) {
      params = params.set('pageIndex', pageIndex.toString());
      params = params.set('pageSize', pageSize.toString());
    }
    return this.http.get<any>(`${this.SURVEY_SERVICE_URL}/displayShare/brandMap/${countryId}`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public downloadDisplayShareBrandMappingTemplate(countryId: number): Observable<Blob> {
    return this.http.get<Blob>(`${this.SURVEY_SERVICE_URL}/displayShare/brandMap/downloadExcelTemplate`, {
      params: new HttpParams().set('countryId', countryId.toString()),
      responseType: 'blob' as 'json'
    }).pipe(catchError(this.errorHandler.handleError));
  }

  public validateDisplayShareBrandMapping(data: any[], countryId: number): Observable<any> {
    return this.http.post<any>(`${this.SURVEY_SERVICE_URL}/displayShare/brandMap/validateData`, data, {
      params: new HttpParams().set('countryId', countryId.toString())
    }).pipe(catchError(this.errorHandler.handleError));
  }

  public importDisplayShareBrandMapping(data: any[], countryId: number): Observable<any> {
    return this.http.post<any>(`${this.SURVEY_SERVICE_URL}/displayShare/brandMap/importData`, data, {
      params: new HttpParams().set('countryId', countryId.toString())
    }).pipe(catchError(this.errorHandler.handleError));
  }

  public exportDisplayShareBrandMapping(countryId: number): Observable<Blob> {
    return this.http.post<Blob>(`${this.SURVEY_SERVICE_URL}/displayShare/brandMap/downloadExcelReport`, {}, {
      params: new HttpParams().set('countryId', countryId.toString()),
      responseType: 'blob' as 'json'
    }).pipe(catchError(this.errorHandler.handleError));
  }

  /** Store 360 Display Share Analysis — v_displaysharelist for one store and one month. */
  public getDisplayShareListByStoreId(
    storeId: number,
    year?: number,
    month?: number
  ): Observable<any> {
    let params = new HttpParams();
    if (year) {
      params = params.set('year', String(year));
    }
    if (month) {
      params = params.set('month', String(month));
    }
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/displayShare/listByStoreId/${storeId}`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /** Account-level Display Share by Category — same v_displaysharelist logic as Store 360, all stores. */
  public getDisplayShareCategoryByAccount(
    accountId: number,
    year?: number,
    month?: number,
    countryId?: number,
  ): Observable<any> {
    let params = new HttpParams();
    if (year) {
      params = params.set('year', String(year));
    }
    if (month) {
      params = params.set('month', String(month));
    }
    if (countryId) {
      params = params.set('countryId', String(countryId));
    }
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/displayShare/categoryShareByAccount/${accountId}`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /** Account-level Display Share by Brand — same v_displaysharelist logic, grouped by brand. */
  public getDisplayShareBrandByAccount(
    accountId: number,
    year?: number,
    month?: number,
    countryId?: number,
  ): Observable<any> {
    let params = new HttpParams();
    if (year) {
      params = params.set('year', String(year));
    }
    if (month) {
      params = params.set('month', String(month));
    }
    if (countryId) {
      params = params.set('countryId', String(countryId));
    }
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/displayShare/brandShareByAccount/${accountId}`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getPromoterShareByStoreId(storeId: number): Observable<any[]> {
    return this.http
      .get<any[]>(`${this.SURVEY_SERVICE_URL}/PromoterShare/getByStoreId/${storeId}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /** Account-level Promoter Share analysis (current calendar month). */
  public getPromoterShareByAccountId(accountId: number): Observable<any[]> {
    return this.http
      .get<any[]>(`${this.SURVEY_SERVICE_URL}/PromoterShare/getByAccountId/${accountId}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updatePromoterShare(id: number, quantity: number): Observable<any> {
    let queryParams: any = {};
    if (quantity) {
      queryParams.quantity = quantity;
    }
    return this.http.put<any>(`${this.SURVEY_SERVICE_URL}/PromoterShare/update/${id}`, queryParams)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deletePromoterShare(id: number): Observable<any> {
    let queryParams = new HttpParams();
    return this.http.delete<any>(`${this.SURVEY_SERVICE_URL}/PromoterShare/delete/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Upload product go price data in bulk
   * @param data - Array of product go price data objects
   * @returns Observable of the upload result
   */
  public uploadProductGoPrice(data: any[]): Observable<any> {
    return this.http
      .post<any>(`${this.SURVEY_SERVICE_URL}/productGoPrice/excelBulkSave`, data)
      .pipe(
        catchError((error) => this.errorHandler.handleError(error))
      );
  }

  /**
   * Upload weekly price mapping data
   * @param data - Array of weekly price mapping data from Excel
   * @returns Observable of upload response
   */
  public uploadWeeklyPriceMapping(data: any[]): Observable<any> {
    return this.http
      .post<any>(`${this.SURVEY_SERVICE_URL}/productGoPrice/uploadWeeklyPriceMapping`, data)
      .pipe(
        catchError((error) => this.errorHandler.handleError(error))
      );
  }

  /**
   * Get comprehensive dashboard data
   * @param params - Dashboard filter parameters
   * @returns Observable of dashboard data
   */
  public getDashboardOverallProject360Data(params: {
    regionIds?: number[];
    countryIds?: number[];
    month?: number;
    year?: number;
  }): Observable<any> {
    let queryParams = new HttpParams();

    if (params.regionIds && params.regionIds.length > 0) {
      queryParams = queryParams.set('regionIds', params.regionIds.join(','));
    }
    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }
    if (params.month) {
      queryParams = queryParams.set('month', params.month.toString());
    }
    if (params.year) {
      queryParams = queryParams.set('year', params.year.toString());
    }

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/survey/getDashboardOverallProject360Data`, { params: queryParams })
      .pipe(
        catchError((error) => this.errorHandler.handleError(error))
      );
  }

  /**
   * Get dashboard sellout trend data
   * @param params - Dashboard filter parameters
   * @returns Observable of sellout trend data
   */
  public getDashboardSelloutTrend(params: {
    regionIds?: number[];
    countryIds?: number[];
    month?: number;
    year?: number;
    type?: number
  }): Observable<any> {
    let queryParams = new HttpParams();

    if (params.regionIds && params.regionIds.length > 0) {
      queryParams = queryParams.set('regionIds', params.regionIds.join(','));
    }
    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }
    if (params.month) {
      queryParams = queryParams.set('month', params.month.toString());
    }
    if (params.year) {
      queryParams = queryParams.set('year', params.year.toString());
    }
    if (params.type) {
      queryParams = queryParams.set('type', params.type.toString());
    }

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/survey/getDashboardSelloutTrend`, { params: queryParams })
      .pipe(
        catchError((error) => this.errorHandler.handleError(error))
      );
  }

  /**
   * Get dashboard target achievement trend data
   * @param params - Dashboard filter parameters
   * @returns Observable of target achievement trend data
   */
  public getDashboardTargetAchievementTrend(params: {
    regionIds?: number[];
    countryIds?: number[];
    month?: number;
    year?: number;
  }): Observable<any> {
    let queryParams = new HttpParams();

    if (params.regionIds && params.regionIds.length > 0) {
      queryParams = queryParams.set('regionIds', params.regionIds.join(','));
    }
    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }
    if (params.month) {
      queryParams = queryParams.set('month', params.month.toString());
    }
    if (params.year) {
      queryParams = queryParams.set('year', params.year.toString());
    }


    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/survey/getDashboardTargetAchievementTrend`, { params: queryParams })
      .pipe(
        catchError((error) => this.errorHandler.handleError(error))
      );
  }

  /**
   * Get dashboard efficiency trend data
   * @param params - Dashboard filter parameters
   * @returns Observable of efficiency trend data
   */
  public getDashboardEfficiencyTrend(params: {
    regionIds?: number[];
    countryIds?: number[];
    month?: number;
    year?: number;
  }): Observable<any> {
    let queryParams = new HttpParams();

    if (params.regionIds && params.regionIds.length > 0) {
      queryParams = queryParams.set('regionIds', params.regionIds.join(','));
    }
    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }
    if (params.month) {
      queryParams = queryParams.set('month', params.month.toString());
    }
    if (params.year) {
      queryParams = queryParams.set('year', params.year.toString());
    }

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/survey/getDashboardEfficiencyTrend`, { params: queryParams })
      .pipe(
        catchError((error) => this.errorHandler.handleError(error))
      );
  }

  /**
   * Get top 10 promoter ranking data
   * @param params - Dashboard filter parameters
   * @returns Observable of top 10 promoter ranking data
   */
  public getTop10PromoterRanking(params: {
    regionIds?: number[];
    countryIds?: number[];
    month?: number;
    year?: number;
    pageIndex?: number;
    pageSize?: number;
    searchValue?: string;
  }): Observable<any> {

    let queryParams = new HttpParams();

    if (params.regionIds && params.regionIds.length > 0) {
      queryParams = queryParams.set('regionIds', params.regionIds.join(','));
    }
    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }
    if (params.month) {
      queryParams = queryParams.set('month', params.month.toString());
    }
    if (params.year) {
      queryParams = queryParams.set('year', params.year.toString());
    }
    if (params.pageIndex) {
      queryParams = queryParams.set('pageIndex', params.pageIndex.toString());
    }
    if (params.pageSize) {
      queryParams = queryParams.set('pageSize', params.pageSize.toString());
    }

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/survey/getTop10PromoterRanking`, { params: queryParams })
      .pipe(
        catchError((error) => this.errorHandler.handleError(error))
      );
  }


  //get dashboard trade-dev and display header table data
  public getDashboardTradeDevHeaderTableData(params: { regionId: number[], countryId: number[], month: number, year: number }): Observable<any> {
    let queryParams = new HttpParams();
    if (params.regionId && params.regionId.length > 0) {
      queryParams = queryParams.set('regionId', params.regionId.join(','));
    }
    if (params.countryId && params.countryId.length > 0) {
      queryParams = queryParams.set('countryId', params.countryId.join(','));
    }
    if (params.month) {
      queryParams = queryParams.set('month', params.month.toString());
    }
    if (params.year) {
      queryParams = queryParams.set('year', params.year.toString());
    }
    return this.http.get<any>(`${this.SURVEY_SERVICE_URL}/survey/getDashboardTradeDevHeaderTableData`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }


  /**
* Get dashboard display share trend data
* @param params - Dashboard filter parameters
* @returns Observable of sellout trend data
*/
  public getDashboardDisplayShareTrend(params: {
    regionId?: number[];
    countryId?: number[];
    month?: number;
    year?: number;
  }): Observable<any> {
    let queryParams = new HttpParams();

    if (params.regionId && params.regionId.length > 0) {
      queryParams = queryParams.set('regionIds', params.regionId.join(','));
    }
    if (params.countryId && params.countryId.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryId.join(','));
    }
    if (params.month) {
      queryParams = queryParams.set('month', params.month.toString());
    }
    if (params.year) {
      queryParams = queryParams.set('year', params.year.toString());
    }

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/survey/getDashboardDisplayShareTrend`, { params: queryParams })
      .pipe(
        catchError((error) => this.errorHandler.handleError(error))
      );
  }


  /**
* Get dashboard fixture share trend data
* @param params - Dashboard filter parameters
* @returns Observable of sellout trend data
*/
  public getDashboardFixtureShareTrend(params: {
    regionId?: number[];
    countryId?: number[];
    month?: number;
    year?: number;
  }): Observable<any> {
    let queryParams = new HttpParams();

    if (params.regionId && params.regionId.length > 0) {
      queryParams = queryParams.set('regionIds', params.regionId.join(','));
    }
    if (params.countryId && params.countryId.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryId.join(','));
    }
    if (params.month) {
      queryParams = queryParams.set('month', params.month.toString());
    }
    if (params.year) {
      queryParams = queryParams.set('year', params.year.toString());
    }

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/survey/getDashboardFixtureShareTrend`, { params: queryParams })
      .pipe(
        catchError((error) => this.errorHandler.handleError(error))
      );
  }

  // SellOut isDelete list for web
  public getSelloutIsDeletedData(params: {
    pageIndex: any,
    pageSize: any,
    searchFilter?: string,
    countryId?: number | string,
    accountId?: number | string,
    channelId?: number | string,
    startDate?: string,
    endDate?: string,
    productId?: number,
    categoryId?: number | string,
    subCategoryId?: number | string,
    storeId?: number | string,
    areaId?: number | string,
    stateId?: number | string,
    userId?: number | string,
    isBundleOffer?: boolean,
    starProduct?: string,
    mdaSda?: string,
    focusStore?: string,
    projectNameId?: string,
    onlyZeroPrice?: boolean,
    mdaSdaName?: string,
    channelType?: string
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize);

    if (params.isBundleOffer !== undefined && params.isBundleOffer !== null) {
      queryParams = queryParams.set('isBundleOffer', params.isBundleOffer.toString());
    }

    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }
    if (params.countryId) {
      queryParams = queryParams.set('countryId', params.countryId.toString());
    }
    if (params.accountId) {
      const accountIds = Array.isArray(params.accountId) ? params.accountId.join(',') : params.accountId;
      queryParams = queryParams.set('accountId', accountIds);
    }
    if (params.channelId) {
      // If array, join as comma-separated string
      const channelIds = Array.isArray(params.channelId) ? params.channelId.join(',') : params.channelId;
      queryParams = queryParams.set('channelId', channelIds);
    }
    if (params.productId) {
      queryParams = queryParams.set('productId', params.productId);
    }
    if (params.categoryId) {
      // If array, join as comma-separated string
      const catIds = Array.isArray(params.categoryId) ? params.categoryId.join(',') : params.categoryId;
      queryParams = queryParams.set('categoryId', catIds);
    }
    if (params.subCategoryId) {
      // If array, join as comma-separated string
      const subCatIds = Array.isArray(params.subCategoryId) ? params.subCategoryId.join(',') : params.subCategoryId;
      queryParams = queryParams.set('subCategoryId', subCatIds);
    }
    if (params.storeId) {
      const storeIds = Array.isArray(params.storeId) ? params.storeId.join(',') : params.storeId;
      queryParams = queryParams.set('storeId', storeIds);
    }
    if (params.startDate) {
      queryParams = queryParams.set('fromDate', params.startDate);
    }
    if (params.endDate) {
      queryParams = queryParams.set('toDate', params.endDate);
    }
    if (params.areaId) {
      const areaIds = Array.isArray(params.areaId) ? params.areaId.join(',') : params.areaId;
      queryParams = queryParams.set('areaId', areaIds);
    }
    if (params.stateId) {
      const stateIds = Array.isArray(params.stateId) ? params.stateId.join(',') : params.stateId;
      queryParams = queryParams.set('stateId', stateIds);
    }
    if (params.userId) {
      const userIds = Array.isArray(params.userId) ? params.userId.join(',') : params.userId;
      queryParams = queryParams.set('userId', userIds);
    }
    if (params.starProduct) {
      const starProduct = Array.isArray(params.starProduct)
        ? params.starProduct.join(',')
        : params.starProduct;
      queryParams = queryParams.set('starProduct', starProduct);
    }
    if (params.channelType) {
      const channelType = Array.isArray(params.channelType)
        ? params.channelType.join(',')
        : params.channelType;
      queryParams = queryParams.set('channelType', channelType);
    }
    if (params.mdaSda) {
      queryParams = queryParams.set('mdaSda', params.mdaSda);
    }
    if (params.focusStore) {
      queryParams = queryParams.set('focusStore', params.focusStore);
    }
    if (params.projectNameId) {
      queryParams = queryParams.set('projectNameId', params.projectNameId);
    }
    if (params.onlyZeroPrice !== undefined && params.onlyZeroPrice !== null) {
      queryParams = queryParams.set('onlyZeroPrice', params.onlyZeroPrice.toString());
    }
    if (params.mdaSdaName) {
      queryParams = queryParams.set('mdaSdaName', params.mdaSdaName);
    }

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/sellOutNew/selloutIsDelete`, { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public selloutDeletedUserList(countryId?: number | number[]): Observable<any> {
    let queryParams = new HttpParams();

    if (countryId) {
      // Handle both single country ID and array of country IDs
      if (Array.isArray(countryId)) {
        queryParams = queryParams.set('countryId', countryId.join(','));
      } else {
        queryParams = queryParams.set('countryId', countryId.toString());
      }
    }

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/sellOutNew/selloutIsDelete/userList`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getMdaSdaCountryList(countryIds?: number[]): Observable<any> {
    let queryParams = new HttpParams();
    if (countryIds && countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', countryIds.join(','));
    }
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/sellOutNew/mdaSdaCountry/list`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getWeeklyPriceMapping(
    params: {
      pageIndex: number,
      pageSize: number,
      searchFilter?: string,
      countryIds?: number[],
      brandIds?: number[],
      categoryIds?: number[],
      subCategoryIds?: number[],
      year?: number,
      productTierIds?: number[],
      starProduct?: string
    }
  ): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize);

    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }

    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }

    if (params.brandIds && params.brandIds.length > 0) {
      queryParams = queryParams.set('brandIds', params.brandIds.join(','));
    }

    if (params.categoryIds && params.categoryIds.length > 0) {
      queryParams = queryParams.set('categoryIds', params.categoryIds.join(','));
    }

    if (params.subCategoryIds && params.subCategoryIds.length > 0) {
      queryParams = queryParams.set('subCategoryIds', params.subCategoryIds.join(','));
    }

    if (params.year) {
      queryParams = queryParams.set('year', params.year.toString());
    }

    if (params.productTierIds && params.productTierIds.length > 0) {
      queryParams = queryParams.set('productTierIds', params.productTierIds.join(','));
      queryParams = queryParams.set('starProduct', params.productTierIds.join(','));
    } else if (params.starProduct) {
      queryParams = queryParams.set('starProduct', params.starProduct);
      queryParams = queryParams.set('productTierIds', params.starProduct);
    }

    return this.http
      .get<any>(
        `${this.SURVEY_SERVICE_URL}/productGoPrice/weekly-price-mapping`,
        { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public downloadWeeklyPriceMappingTemplate(countryIds?: number[], year?: number, weeks?: number[]): Observable<any> {
    let queryParams = new HttpParams();

    if (countryIds && countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', countryIds.join(','));
    }

    if (year) {
      queryParams = queryParams.set('year', year.toString());
    }

    if (weeks && weeks.length > 0) {
      queryParams = queryParams.set('weeks', weeks.join(','));
    }

    return this.http
      .get(
        `${this.SURVEY_SERVICE_URL}/productGoPrice/downloadExcelTemplate`,
        {
          params: queryParams,
          responseType: 'blob' as 'json'
        }
      )
      .pipe(
        catchError(this.errorHandler.handleError)
      );
  }


  public getImagePOsition(countryId?: number): Observable<any> {
    let queryParams = new HttpParams();
    if (countryId != null) {
      queryParams = queryParams.set('countryId', countryId.toString());
    }
    return this.http
      .get(
        `${this.SURVEY_SERVICE_URL}/image-position/getImagePosition`,
        { params: queryParams }
      )
      .pipe(
        catchError(this.errorHandler.handleError)
      );
  }

  public createStore360(data: any): Observable<any> {
    return this.http
      .post<any>(`${this.SURVEY_SERVICE_URL}/store360/createStore360`, data)
      .pipe(
        catchError(this.errorHandler.handleError)
      );
  }

  /** Save Store360 with blob URLs already obtained from blob upload (containerName=store360) */
  public uploadStore360(
    data: {
      imagePositionId: number;
      urls?: string[];
      video_url?: string;
      description?: string;
    },
    countryId: number,
    storeId: number,
  ): Observable<any> {
    let queryParams = new HttpParams()
      .set('countryId', countryId.toString())
      .set('storeId', storeId.toString());
    return this.http
      .post<any>(
        `${this.SURVEY_SERVICE_URL}/store360/upload/store-image`,
        data,
        { params: queryParams },
      )
      .pipe(
        catchError(this.errorHandler.handleError)
      );
  }

  public updateStore360(store360: any): Observable<any> {
    return this.http
      .put<any>(
        `${this.SURVEY_SERVICE_URL}/store360/updateStore360/${store360.id}`,
        store360
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  /** PATCH Store360 media entry with imagePositionId, urls, video_url, description */
  public patchStore360(
    id: number,
    data: {
      imagePositionId?: number;
      urls?: string[];
      video_url?: string;
      description?: string;
      countryId?: number;
      storeId?: number;
    },
    countryId?: number,
    storeId?: number,
  ): Observable<any> {
    let queryParams = new HttpParams();
    if (countryId != null) queryParams = queryParams.set('countryId', countryId.toString());
    if (storeId != null) queryParams = queryParams.set('storeId', storeId.toString());
    return this.http
      .patch<any>(
        `${this.SURVEY_SERVICE_URL}/store360/upload/store-image/${id}`,
        data,
        { params: queryParams },
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  /** DELETE Store360 entry by ID */
  public deleteStore360(id: number): Observable<any> {
    return this.http
      .delete<any>(`${this.SURVEY_SERVICE_URL}/store360/store-image/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }



  public getAllStore360(pageIndex: number, pageSize: number): Observable<any> {
    let queryParams = new HttpParams();
    queryParams = queryParams.set('pageIndex', pageIndex.toString());
    queryParams = queryParams.set('pageSize', pageSize.toString());
    return this.http
      .get(
        `${this.SURVEY_SERVICE_URL}/store360/getAllStore360`, { params: queryParams }
      )
      .pipe(
        catchError(this.errorHandler.handleError)
      );
  }

  public getStore360ImagesByStoreId(storeId: number): Observable<any> {
    let queryParams = new HttpParams().set('storeId', storeId.toString());
    return this.http
      .get(`${this.SURVEY_SERVICE_URL}/store360/store-images`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllProductRRPriceList(
    params: {
      pageIndex: number,
      pageSize: number,
      searchFilter?: string,
      countryIds?: number[],
      brandIds?: number[],
      categoryIds?: number[],
      subCategoryIds?: number[],
      year?: number
    }
  ): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize);

    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }

    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }

    if (params.brandIds && params.brandIds.length > 0) {
      queryParams = queryParams.set('brandIds', params.brandIds.join(','));
    }

    if (params.categoryIds && params.categoryIds.length > 0) {
      queryParams = queryParams.set('categoryIds', params.categoryIds.join(','));
    }

    if (params.subCategoryIds && params.subCategoryIds.length > 0) {
      queryParams = queryParams.set('subCategoryIds', params.subCategoryIds.join(','));
    }

    if (params.year) {
      queryParams = queryParams.set('year', params.year.toString());
    }

    return this.http
      .get<any>(
        `${this.SURVEY_SERVICE_URL}/productGoPrice/rrprice-list`,
        { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }


  public downloadRRPriceMappingTemplate(
    params: {
      pageIndex: number,
      pageSize: number,
      searchFilter?: string,
      countryIds?: number[],
      brandIds?: number[],
      categoryIds?: number[],
      subCategoryIds?: number[],
      year?: number
    }
  ): Observable<any> {
    let queryParams = new HttpParams()


    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }

    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }

    if (params.brandIds && params.brandIds.length > 0) {
      queryParams = queryParams.set('brandIds', params.brandIds.join(','));
    }

    if (params.categoryIds && params.categoryIds.length > 0) {
      queryParams = queryParams.set('categoryIds', params.categoryIds.join(','));
    }

    if (params.subCategoryIds && params.subCategoryIds.length > 0) {
      queryParams = queryParams.set('subCategoryIds', params.subCategoryIds.join(','));
    }

    if (params.year) {
      queryParams = queryParams.set('year', params.year.toString());
    }

    return this.http
      .post<any>(
        `${this.SURVEY_SERVICE_URL}/productGoPrice/productRRPrice/downloadExcelReport`,
        {},
        {
          params: queryParams,
          responseType: 'blob' as 'json'
        }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public importRRPriceMapping(data: Record<string, any>): Observable<any[]> {
    return this.http
      .post<any[]>(
        `${this.SURVEY_SERVICE_URL}/productGoPrice/updateRRPrices`,
        data
      )
      .pipe(catchError(this.errorHandler.handleError));
  }


  //incentive scheme setup page card data
  public getCardData(): Observable<any> {
    return this.http
      .get<any>(
        `${this.SURVEY_SERVICE_URL}/incentiveScheme/getCardData`
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public saveIncentiveSchemeSetup(data: any): Observable<any> {
    return this.http
      .post<any>(`${this.SURVEY_SERVICE_URL}/incentiveScheme/saveIncentiveSchemeSetup`, data)
      .pipe(
        catchError(this.errorHandler.handleError)
      );
  }

  public existingSchemeTypeCheck(countryId: number, startDate: string, endDate: string): Observable<any> {
    return this.http.get<any>(`${this.SURVEY_SERVICE_URL}/incentiveScheme/existingSchemeTypeCheck`, {
      params: new HttpParams()
        .set('countryId', countryId.toString())
        .set('startDate', startDate)
        .set('endDate', endDate)
    })
      .pipe(
        catchError(this.errorHandler.handleError)
      );
  }

  public getIncentiveSchemeTypesWithCountries(): Observable<any> {
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/incentiveScheme/schemeTypesWithCountries`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public createIncentiveSchemeType(body: { name: string }): Observable<any> {
    return this.http
      .post<any>(`${this.SURVEY_SERVICE_URL}/incentiveScheme/schemeType`, body)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public setIncentiveSchemeTypeCountries(schemeTypeId: number, body: { countryIds: number[] }): Observable<any> {
    return this.http
      .put<any>(
        `${this.SURVEY_SERVICE_URL}/incentiveScheme/schemeType/${schemeTypeId}/countries`,
        body,
      )
      .pipe(catchError(this.errorHandler.handleError));
  }


  //to save the price promotion
  public updatePricePromotion(data: any[]): Observable<any> {
    return this.http.post<any>(`${this.SURVEY_SERVICE_URL}/pricePromotion/update`, data)
      .pipe(catchError(this.errorHandler.handleError))
  }

  public getBrandAndModelList(params: {
    subCategoryId?: number,
    brandId?: number,
    searchValue?: string
  }): Observable<any> {
    let queryParams = new HttpParams()
    if (params?.subCategoryId) {
      queryParams = queryParams.set('subCategoryId', params?.subCategoryId?.toString());
    }
    if (params?.brandId) {
      queryParams = queryParams.set('brandId', params?.brandId?.toString());
    }
    if (params?.searchValue) {
      queryParams = queryParams.set('searchValue', params?.searchValue);
    }

    return this.http.get<any>(`${this.SURVEY_SERVICE_URL}/pricePromotion/getBrandAndModelList`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError))
  }


  //To get the price promotion data with filters
  public getPricePromotionData(params: {
    pageIndex: number,
    pageSize: number,
    countryIds?: number[],
    areaGroupIds?: number[],
    stateIds?: number[],
    channelIds?: number[],
    accountIds?: number[],
    storeIds?: number[],
    userIds?: number[],
    categoryIds?: number[],
    subCategoryIds?: number[],
    brandIds?: number[],
    searchValue?: string,
    startDate?: string,
    endDate?: string,
    focusStore?: string,
    starProduct?: string,
    channelType?: string
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize);
    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }
    if (params.areaGroupIds && params.areaGroupIds.length > 0) {
      queryParams = queryParams.set('areaGroupIds', params.areaGroupIds.join(','));
    }
    if (params.stateIds && params.stateIds.length > 0) {
      queryParams = queryParams.set('stateIds', params.stateIds.join(','));
    }
    if (params.channelIds && params.channelIds.length > 0) {
      queryParams = queryParams.set('channelIds', params.channelIds.join(','));
    }
    if (params.accountIds && params.accountIds.length > 0) {
      queryParams = queryParams.set('accountIds', params.accountIds.join(','));
    }
    if (params.storeIds && params.storeIds.length > 0) {
      queryParams = queryParams.set('storeIds', params.storeIds.join(','));
    }
    if (params.userIds && params.userIds.length > 0) {
      queryParams = queryParams.set('userIds', params.userIds.join(','));
    }
    if (params.categoryIds && params.categoryIds.length > 0) {
      queryParams = queryParams.set('categoryIds', params.categoryIds.join(','));
    }
    if (params.subCategoryIds && params.subCategoryIds.length > 0) {
      queryParams = queryParams.set('subCategoryIds', params.subCategoryIds.join(','));
    }
    if (params.brandIds && params.brandIds.length > 0) {
      queryParams = queryParams.set('brandIds', params.brandIds.join(','));
    }
    if (params.searchValue && params.searchValue.trim() !== '') {
      queryParams = queryParams.set('searchValue', params.searchValue);
    }
    if (params.startDate) {
      queryParams = queryParams.set('startDate', params.startDate);
    }
    if (params.endDate) {
      queryParams = queryParams.set('endDate', params.endDate);
    }
    if (params.focusStore && params.focusStore !== 'All') {
      queryParams = queryParams.set('focusStore', params.focusStore);
    }
    if (params.starProduct) {
      queryParams = queryParams.set('starProduct', params.starProduct);
    }
    if (params.channelType) {
      queryParams = queryParams.set('channelType', params.channelType);
    }
    return this.http.get<any>(`${this.SURVEY_SERVICE_URL}/pricePromotion/getPricePromotionData`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError))
  }

  // Get user list from price promotion table based on country ids
  public getPricePromotionUserList(countryId?: number[]): Observable<any> {
    let queryParams = new HttpParams();
    if (countryId && countryId.length > 0) {
      queryParams = queryParams.set('countryId', countryId.join(','));
    }
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/pricePromotion/getPricePromotionUserList`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  // Export price promotion data to Excel
  public exportPricePromotionData(params: {
    pageIndex?: number,
    pageSize?: number,
    countryIds?: number[],
    areaGroupIds?: number[],
    stateIds?: number[],
    channelIds?: number[],
    accountIds?: number[],
    storeIds?: number[],
    userIds?: number[],
    categoryIds?: number[],
    subCategoryIds?: number[],
    brandIds?: number[],
    searchValue?: string,
    startDate?: string,
    endDate?: string,
    frontendBaseUrl?: string,
    /** When true, embed images in Excel; when false, only image URL in cell */
    includeImages?: boolean,
    focusStore?: string,
    starProduct?: string,
    channelType?: string
  }): Observable<Blob> {
    let queryParams = new HttpParams();
    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }
    if (params.areaGroupIds && params.areaGroupIds.length > 0) {
      queryParams = queryParams.set('areaGroupIds', params.areaGroupIds.join(','));
    }
    if (params.stateIds && params.stateIds.length > 0) {
      queryParams = queryParams.set('stateIds', params.stateIds.join(','));
    }
    if (params.channelIds && params.channelIds.length > 0) {
      queryParams = queryParams.set('channelIds', params.channelIds.join(','));
    }
    if (params.accountIds && params.accountIds.length > 0) {
      queryParams = queryParams.set('accountIds', params.accountIds.join(','));
    }
    if (params.storeIds && params.storeIds.length > 0) {
      queryParams = queryParams.set('storeIds', params.storeIds.join(','));
    }
    if (params.userIds && params.userIds.length > 0) {
      queryParams = queryParams.set('userIds', params.userIds.join(','));
    }
    if (params.categoryIds && params.categoryIds.length > 0) {
      queryParams = queryParams.set('categoryIds', params.categoryIds.join(','));
    }
    if (params.subCategoryIds && params.subCategoryIds.length > 0) {
      queryParams = queryParams.set('subCategoryIds', params.subCategoryIds.join(','));
    }
    if (params.brandIds && params.brandIds.length > 0) {
      queryParams = queryParams.set('brandIds', params.brandIds.join(','));
    }
    if (params.searchValue && params.searchValue.trim() !== '') {
      queryParams = queryParams.set('searchValue', params.searchValue);
    }
    if (params.startDate) {
      queryParams = queryParams.set('startDate', params.startDate);
    }
    if (params.endDate) {
      queryParams = queryParams.set('endDate', params.endDate);
    }
    if (params.frontendBaseUrl) {
      queryParams = queryParams.set('frontendBaseUrl', params.frontendBaseUrl);
    }
    if (params.includeImages !== undefined) {
      queryParams = queryParams.set('includeImages', params.includeImages ? 'true' : 'false');
    }
    if (params.focusStore && params.focusStore !== 'All') {
      queryParams = queryParams.set('focusStore', params.focusStore);
    }
    if (params.starProduct) {
      queryParams = queryParams.set('starProduct', params.starProduct);
    }
    if (params.channelType) {
      queryParams = queryParams.set('channelType', params.channelType);
    }
    return this.http
      .get(`${this.SURVEY_SERVICE_URL}/pricePromotion/downloadExcelReport`, {
        params: queryParams,
        responseType: 'blob'
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public buildPricePromotionExportStartBody(
    params: Record<string, unknown>,
  ): Record<string, unknown> {
    return {
      ...params,
      includeImages: false,
    };
  }

  public buildPriceTrackingExportStartBody(
    params: Record<string, unknown>,
  ): Record<string, unknown> {
    return {
      ...params,
      exportImageType: 1,
    };
  }

  public buildCheckInOutExportStartBody(
    params: Record<string, unknown>,
  ): Record<string, unknown> {
    return {
      ...params,
      exportImageType: 1,
    };
  }

  public downloadPricePromotionMediaStream(
    id: string,
    fileIndex: string,
    kind: string
  ): Observable<HttpResponse<Blob>> {
    const params = new HttpParams()
      .set('id', id)
      .set('fileIndex', fileIndex || '0')
      .set('kind', kind || 'price-promotion-image');
    const headers = new HttpHeaders().set('isBlob', 'true');
    return this.http
      .get(`${this.SURVEY_SERVICE_URL}/pricePromotion/media/file`, {
        params,
        observe: 'response',
        responseType: 'blob',
        headers,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAegSurveyCreatedByFilterOptions(params: {
    countryIds?: number[];
    storeIds?: number[];
    channelIds?: number[];
    accountIds?: number[];
    startDate?: string | null;
    endDate?: string | null;
    search?: string;
  }): Observable<{ result: Array<{ id: number | string; name: string }> }> {
    let queryParams = new HttpParams();
    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }
    if (params.storeIds && params.storeIds.length > 0) {
      queryParams = queryParams.set('storeIds', params.storeIds.join(','));
    }
    if (params.channelIds && params.channelIds.length > 0) {
      queryParams = queryParams.set('channelIds', params.channelIds.join(','));
    }
    if (params.accountIds && params.accountIds.length > 0) {
      queryParams = queryParams.set('accountIds', params.accountIds.join(','));
    }
    if (params.startDate) {
      queryParams = queryParams.set('startDate', params.startDate);
    }
    if (params.endDate) {
      queryParams = queryParams.set('endDate', params.endDate);
    }
    if (params.search && params.search.trim() !== '') {
      queryParams = queryParams.set('search', params.search.trim());
    }
    return this.http
      .get<{ result: Array<{ id: number | string; name: string }> }>(
        `${this.SURVEY_SERVICE_URL}/aegSurvey/createdByFilterOptions`,
        { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAegSurveyFromView(
    params?: {
      pageIndex?: number;
      pageSize?: number;
      countryIds?: number[];
      storeIds?: number[];
      channelIds?: number[];
      accountIds?: number[];
      createdByIds?: (number | string)[];
      startDate?: string | null;
      endDate?: string | null;
      search?: string;
      focusStore?: string;
    }
  ): Observable<any> {
    if (!params) {
      return this.http
        .get<any>(`${this.SURVEY_SERVICE_URL}/aegSurvey/getAllFromView`)
        .pipe(catchError(this.errorHandler.handleError));
    }

    let queryParams = new HttpParams();

    if (typeof params.pageIndex === 'number' && !Number.isNaN(params.pageIndex)) {
      queryParams = queryParams.set('pageIndex', String(params.pageIndex));
    }
    if (typeof params.pageSize === 'number' && !Number.isNaN(params.pageSize)) {
      queryParams = queryParams.set('pageSize', String(params.pageSize));
    }
    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }
    if (params.storeIds && params.storeIds.length > 0) {
      queryParams = queryParams.set('storeIds', params.storeIds.join(','));
    }
    if (params.channelIds && params.channelIds.length > 0) {
      queryParams = queryParams.set('channelIds', params.channelIds.join(','));
    }
    if (params.accountIds && params.accountIds.length > 0) {
      queryParams = queryParams.set('accountIds', params.accountIds.join(','));
    }
    if (params.createdByIds && params.createdByIds.length > 0) {
      queryParams = queryParams.set(
        'createdByIds',
        params.createdByIds.map((id) => String(id)).join(',')
      );
    }
    if (params.startDate) {
      queryParams = queryParams.set('startDate', params.startDate);
    }
    if (params.endDate) {
      queryParams = queryParams.set('endDate', params.endDate);
    }
    if (params.search && params.search.trim() !== '') {
      queryParams = queryParams.set('search', params.search.trim());
    }
    if (params.focusStore && params.focusStore.trim() !== '') {
      queryParams = queryParams.set('focusStore', params.focusStore.trim());
    }

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/aegSurvey/getAllFromView`, {
        params: queryParams
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAegSurveyRawFromView(
    params?: {
      pageIndex?: number;
      pageSize?: number;
      countryIds?: number[];
      storeIds?: number[];
      channelIds?: number[];
      accountIds?: number[];
      createdByIds?: (number | string)[];
      startDate?: string | null;
      endDate?: string | null;
      search?: string;
      focusStore?: string;
    }
  ): Observable<any> {
    if (!params) {
      return this.http
        .get<any>(`${this.SURVEY_SERVICE_URL}/aegSurvey/getAllFromRawView`)
        .pipe(catchError(this.errorHandler.handleError));
    }

    let queryParams = new HttpParams();

    if (typeof params.pageIndex === 'number' && !Number.isNaN(params.pageIndex)) {
      queryParams = queryParams.set('pageIndex', String(params.pageIndex));
    }
    if (typeof params.pageSize === 'number' && !Number.isNaN(params.pageSize)) {
      queryParams = queryParams.set('pageSize', String(params.pageSize));
    }
    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }
    if (params.storeIds && params.storeIds.length > 0) {
      queryParams = queryParams.set('storeIds', params.storeIds.join(','));
    }
    if (params.channelIds && params.channelIds.length > 0) {
      queryParams = queryParams.set('channelIds', params.channelIds.join(','));
    }
    if (params.accountIds && params.accountIds.length > 0) {
      queryParams = queryParams.set('accountIds', params.accountIds.join(','));
    }
    if (params.createdByIds && params.createdByIds.length > 0) {
      queryParams = queryParams.set(
        'createdByIds',
        params.createdByIds.map((id) => String(id)).join(',')
      );
    }
    if (params.startDate) {
      queryParams = queryParams.set('startDate', params.startDate);
    }
    if (params.endDate) {
      queryParams = queryParams.set('endDate', params.endDate);
    }
    if (params.search && params.search.trim() !== '') {
      queryParams = queryParams.set('search', params.search.trim());
    }
    if (params.focusStore && params.focusStore.trim() !== '') {
      queryParams = queryParams.set('focusStore', params.focusStore.trim());
    }

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/aegSurvey/getAllFromRawView`, {
        params: queryParams
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public exportAegSurveySummary(params: {
    countryIds?: number[];
    storeIds?: number[];
    channelIds?: number[];
    accountIds?: number[];
    createdByIds?: (number | string)[];
    startDate?: string | null;
    endDate?: string | null;
    search?: string;
    focusStore?: string;
  }): Observable<Blob> {
    let queryParams = new HttpParams();
    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }
    if (params.storeIds && params.storeIds.length > 0) {
      queryParams = queryParams.set('storeIds', params.storeIds.join(','));
    }
    if (params.channelIds && params.channelIds.length > 0) {
      queryParams = queryParams.set('channelIds', params.channelIds.join(','));
    }
    if (params.accountIds && params.accountIds.length > 0) {
      queryParams = queryParams.set('accountIds', params.accountIds.join(','));
    }
    if (params.createdByIds && params.createdByIds.length > 0) {
      queryParams = queryParams.set(
        'createdByIds',
        params.createdByIds.map((id) => String(id)).join(',')
      );
    }
    if (params.startDate) {
      queryParams = queryParams.set('startDate', params.startDate);
    }
    if (params.endDate) {
      queryParams = queryParams.set('endDate', params.endDate);
    }
    if (params.search && params.search.trim() !== '') {
      queryParams = queryParams.set('search', params.search.trim());
    }
    if (params.focusStore && params.focusStore.trim() !== '') {
      queryParams = queryParams.set('focusStore', params.focusStore.trim());
    }

    return this.http
      .get<Blob>(`${this.SURVEY_SERVICE_URL}/aegSurvey/exportSummary/downloadExcelReport`, {
        params: queryParams,
        responseType: 'blob' as 'json',
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public exportAegSurveyRaw(params: {
    countryIds?: number[];
    storeIds?: number[];
    channelIds?: number[];
    accountIds?: number[];
    createdByIds?: (number | string)[];
    startDate?: string | null;
    endDate?: string | null;
    search?: string;
    focusStore?: string;
  }): Observable<Blob> {
    let queryParams = new HttpParams();
    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }
    if (params.storeIds && params.storeIds.length > 0) {
      queryParams = queryParams.set('storeIds', params.storeIds.join(','));
    }
    if (params.channelIds && params.channelIds.length > 0) {
      queryParams = queryParams.set('channelIds', params.channelIds.join(','));
    }
    if (params.accountIds && params.accountIds.length > 0) {
      queryParams = queryParams.set('accountIds', params.accountIds.join(','));
    }
    if (params.createdByIds && params.createdByIds.length > 0) {
      queryParams = queryParams.set(
        'createdByIds',
        params.createdByIds.map((id) => String(id)).join(',')
      );
    }
    if (params.startDate) {
      queryParams = queryParams.set('startDate', params.startDate);
    }
    if (params.endDate) {
      queryParams = queryParams.set('endDate', params.endDate);
    }
    if (params.search && params.search.trim() !== '') {
      queryParams = queryParams.set('search', params.search.trim());
    }
    if (params.focusStore && params.focusStore.trim() !== '') {
      queryParams = queryParams.set('focusStore', params.focusStore.trim());
    }

    return this.http
      .get<Blob>(`${this.SURVEY_SERVICE_URL}/aegSurvey/exportRaw/downloadExcelReport`, {
        params: queryParams,
        responseType: 'blob' as 'json',
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public exportAegSurveyFormat(params: {
    countryIds?: number[];
    storeIds?: number[];
    channelIds?: number[];
    accountIds?: number[];
    createdByIds?: (number | string)[];
    startDate?: string | null;
    endDate?: string | null;
    search?: string;
    focusStore?: string;
  }): Observable<Blob> {
    let queryParams = new HttpParams();
    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }
    if (params.storeIds && params.storeIds.length > 0) {
      queryParams = queryParams.set('storeIds', params.storeIds.join(','));
    }
    if (params.channelIds && params.channelIds.length > 0) {
      queryParams = queryParams.set('channelIds', params.channelIds.join(','));
    }
    if (params.accountIds && params.accountIds.length > 0) {
      queryParams = queryParams.set('accountIds', params.accountIds.join(','));
    }
    if (params.createdByIds && params.createdByIds.length > 0) {
      queryParams = queryParams.set(
        'createdByIds',
        params.createdByIds.map((id) => String(id)).join(',')
      );
    }
    if (params.startDate) {
      queryParams = queryParams.set('startDate', params.startDate);
    }
    if (params.endDate) {
      queryParams = queryParams.set('endDate', params.endDate);
    }
    if (params.search && params.search.trim() !== '') {
      queryParams = queryParams.set('search', params.search.trim());
    }
    if (params.focusStore && params.focusStore.trim() !== '') {
      queryParams = queryParams.set('focusStore', params.focusStore.trim());
    }

    return this.http
      .get<Blob>(`${this.SURVEY_SERVICE_URL}/aegSurvey/exportAegFormat/downloadExcelReport`, {
        params: queryParams,
        responseType: 'blob' as 'json',
      })
      .pipe(catchError(this.errorHandler.handleError));
  }


  getSnagTickets(params: {
    statusId?: number[];
    priority?: string[];
    resolvedBy?: number[];
    countryId?: number[];
    issueTypeId?: number[];
    storeId?: number[];
    search?: string;
    pageIndex?: number;
    pageSize?: number;
    focusStore?: string;
  }): Observable<any> {
    let queryParams = new HttpParams();
    if (params.statusId && params.statusId.length > 0) {
      queryParams = queryParams.set('statusId', params.statusId.join(','));
    }
    if (params.priority && params.priority.length > 0) {
      queryParams = queryParams.set('priority', params.priority.join(','));
    }
    if (params.resolvedBy && params.resolvedBy.length > 0) {
      queryParams = queryParams.set('resolvedBy', params.resolvedBy.join(','));
    }
    if (params.countryId && params.countryId.length > 0) {
      queryParams = queryParams.set('countryId', params.countryId.join(','));
    }
    if (params.issueTypeId && params.issueTypeId.length > 0) {
      queryParams = queryParams.set('issueTypeId', params.issueTypeId.join(','));
    }
    if (params.storeId && params.storeId.length > 0) {
      queryParams = queryParams.set('storeId', params.storeId.join(','));
    }
    if (params.search && params.search.trim() !== '') {
      queryParams = queryParams.set('search', params.search.trim());
    }
    if (typeof params.pageIndex === 'number' && !Number.isNaN(params.pageIndex)) {
      queryParams = queryParams.set('pageIndex', String(params.pageIndex));
    }
    if (typeof params.pageSize === 'number' && !Number.isNaN(params.pageSize)) {
      queryParams = queryParams.set('pageSize', String(params.pageSize));
    }
    if (params.focusStore) {
      queryParams = queryParams.set('focusStore', params.focusStore);
    }
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/snagreport/ticketReports`, {
        params: queryParams
      })
      .pipe(catchError(this.errorHandler.handleError));
  }


  countsSnagTicket(params: {
    statusId?: number[];
    priority?: string[];
    resolvedBy?: number[];
    countryId?: number[];
    issueTypeId?: number[];
    storeId?: number[];
    search?: string;
    focusStore?: string;
  }): Observable<any> {
    let queryParams = new HttpParams();
    if (params.statusId && params.statusId.length > 0) {
      queryParams = queryParams.set('statusId', params.statusId.join(','));
    }
    if (params.priority && params.priority.length > 0) {
      queryParams = queryParams.set('priority', params.priority.join(','));
    }
    if (params.resolvedBy && params.resolvedBy.length > 0) {
      queryParams = queryParams.set('resolvedBy', params.resolvedBy.join(','));
    }
    if (params.countryId && params.countryId.length > 0) {
      queryParams = queryParams.set('countryId', params.countryId.join(','));
    }
    if (params.issueTypeId && params.issueTypeId.length > 0) {
      queryParams = queryParams.set('issueTypeId', params.issueTypeId.join(','));
    }
    if (params.storeId && params.storeId.length > 0) {
      queryParams = queryParams.set('storeId', params.storeId.join(','));
    }
    if (params.search && params.search.trim() !== '') {
      queryParams = queryParams.set('search', params.search.trim());
    }
    if (params.focusStore) {
      queryParams = queryParams.set('focusStore', params.focusStore);
    }
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/snagreport/tickets/counts`, {
        params: queryParams
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  resolveSnagTicket(formData: FormData): Observable<any> {
    return this.http
      .post<any>(`${this.SURVEY_SERVICE_URL}/snagreport/resolve-ticket`, formData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  reopenSnagTicket(formData: FormData): Observable<any> {

    return this.http
      .post<any>(`${this.SURVEY_SERVICE_URL}/snagreport/reopen-ticket`, formData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  getAllSnagTicketStatus(): Observable<any> {
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/snagreport/ticket-statuses`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  getAllSnagTicketType(): Observable<any> {
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/snagreport/ticket-types`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  deleteSnagTicket(id: number): Observable<any> {
    return this.http
      .delete<any>(`${this.SURVEY_SERVICE_URL}/snagreport/deleteTicket/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  exportSnagReport(params: {
    statusId?: number[];
    priority?: string[];
    resolvedBy?: number[];
    countryId?: number[];
    issueTypeId?: number[];
    storeId?: number[];
    search?: string;
    focusStore?: string;
  }): Observable<any> {
    let queryParams = new HttpParams();
    if (params.statusId && params.statusId.length > 0) {
      queryParams = queryParams.set('statusId', params.statusId.join(','));
    }
    if (params.priority && params.priority.length > 0) {
      queryParams = queryParams.set('priority', params.priority.join(','));
    }
    if (params.resolvedBy && params.resolvedBy.length > 0) {
      queryParams = queryParams.set('resolvedBy', params.resolvedBy.join(','));
    }
    if (params.countryId && params.countryId.length > 0) {
      queryParams = queryParams.set('countryId', params.countryId.join(','));
    }
    if (params.issueTypeId && params.issueTypeId.length > 0) {
      queryParams = queryParams.set('issueTypeId', params.issueTypeId.join(','));
    }
    if (params.storeId && params.storeId.length > 0) {
      queryParams = queryParams.set('storeId', params.storeId.join(','));
    }
    if (params.search && params.search.trim() !== '') {
      queryParams = queryParams.set('search', params.search.trim());
    }
    if (params.focusStore) {
      queryParams = queryParams.set('focusStore', params.focusStore);
    }
    return this.http.get<Blob>(`${this.SURVEY_SERVICE_URL}/snagreport/downloadExcelTemplate`, {
      params: queryParams,
      responseType: 'blob' as 'json'
    })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getSurveyControlMobileRoles(): Observable<any> {
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/surveyControl/mobileRoles`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getSurveyControlList(countryId: number, roleId: number): Observable<any> {
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/surveyControl/list`, {
        params: new HttpParams()
          .set('countryId', countryId.toString())
          .set('roleId', roleId.toString()),
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateSurveyWithoutCheckinToggle(payload: {
    countryId: number;
    roleId: number;
    surveyId: number;
    isSurveyWithoutCheckin: boolean;
  }): Observable<any> {
    return this.http
      .put<any>(`${this.SURVEY_SERVICE_URL}/surveyControl/toggle`, payload)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getSurveyRoleMapList(countryId: number, roleId: number): Observable<any> {
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/surveyRoleMap/list`, {
        params: new HttpParams()
          .set('countryId', countryId.toString())
          .set('roleId', roleId.toString()),
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateSurveyRoleMapToggle(payload: {
    countryId: number;
    roleId: number;
    surveyId: number;
    isMapped: boolean;
  }): Observable<any> {
    return this.http
      .put<any>(`${this.SURVEY_SERVICE_URL}/surveyRoleMap/toggle`, payload)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getMobileSurveysWithoutCheckin(): Observable<{
    result: {
      surveyId: number;
      name: string;
      desc: string;
      surveyTypeId: number;
      surveyTypeName: string;
      roleId: number;
      order: number | null;
      isSurveyWithoutCheckin: boolean;
    }[];
  }> {
    return this.http
      .get<{
        result: {
          surveyId: number;
          name: string;
          desc: string;
          surveyTypeId: number;
          surveyTypeName: string;
          roleId: number;
          order: number | null;
          isSurveyWithoutCheckin: boolean;
        }[];
      }>(`${this.SURVEY_SERVICE_URL}/surveyControl/mobile/enabled`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getDailySurveyPromoterTypes(): Observable<any> {
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/dailySurvey/promoterTypes`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getDailySurveyPromoterCategories(): Observable<any> {
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/dailySurvey/promoterCategories`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getDailySurveyList(params: {
    pageIndex: number;
    pageSize: number;
    searchFilter?: string;
    startDate?: string;
    endDate?: string;
    regionIds?: number[];
    countryIds?: number[];
    channelIds?: number[];
    accountIds?: number[];
    storeIds?: number[];
    promoterTypeIds?: number[];
    promoterCategoryIds?: number[];
    mdaSda?: string;
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex.toString())
      .set('pageSize', params.pageSize.toString());

    const appendParam = (key: string, value?: string | number[]) => {
      if (value === undefined || value === null) {
        return;
      }
      if (Array.isArray(value)) {
        if (value.length > 0) {
          queryParams = queryParams.set(key, value.join(','));
        }
        return;
      }
      const trimmed = String(value).trim();
      if (trimmed !== '') {
        queryParams = queryParams.set(key, trimmed);
      }
    };

    appendParam('searchFilter', params.searchFilter);
    appendParam('startDate', params.startDate);
    appendParam('endDate', params.endDate);
    appendParam('regionIds', params.regionIds);
    appendParam('countryIds', params.countryIds);
    appendParam('channelIds', params.channelIds);
    appendParam('accountIds', params.accountIds);
    appendParam('storeIds', params.storeIds);
    appendParam('promoterTypeIds', params.promoterTypeIds);
    appendParam('promoterCategoryIds', params.promoterCategoryIds);
    appendParam('mdaSda', params.mdaSda);

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/dailySurvey/list`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getDailySurveySummaryList(params: {
    pageIndex: number;
    pageSize: number;
    searchFilter?: string;
    startDate?: string;
    endDate?: string;
    regionIds?: number[];
    countryIds?: number[];
    channelIds?: number[];
    accountIds?: number[];
    storeIds?: number[];
    promoterTypeIds?: number[];
    promoterCategoryIds?: number[];
    mdaSda?: string;
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex.toString())
      .set('pageSize', params.pageSize.toString());

    const appendParam = (key: string, value?: string | number[]) => {
      if (value === undefined || value === null) {
        return;
      }
      if (Array.isArray(value)) {
        if (value.length > 0) {
          queryParams = queryParams.set(key, value.join(','));
        }
        return;
      }
      const trimmed = String(value).trim();
      if (trimmed !== '') {
        queryParams = queryParams.set(key, trimmed);
      }
    };

    appendParam('searchFilter', params.searchFilter);
    appendParam('startDate', params.startDate);
    appendParam('endDate', params.endDate);
    appendParam('regionIds', params.regionIds);
    appendParam('countryIds', params.countryIds);
    appendParam('channelIds', params.channelIds);
    appendParam('accountIds', params.accountIds);
    appendParam('storeIds', params.storeIds);
    appendParam('promoterTypeIds', params.promoterTypeIds);
    appendParam('promoterCategoryIds', params.promoterCategoryIds);
    appendParam('mdaSda', params.mdaSda);

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/dailySurvey/summary`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getDailySurveyBatchDetails(batchId: string): Observable<any> {
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/dailySurvey/batchDetails`, {
        params: new HttpParams().set('batchId', batchId),
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getDailySurveyDetailsList(params: {
    pageIndex: number;
    pageSize: number;
    searchFilter?: string;
    startDate?: string;
    endDate?: string;
    regionIds?: number[];
    countryIds?: number[];
    channelIds?: number[];
    accountIds?: number[];
    storeIds?: number[];
    promoterTypeIds?: number[];
    promoterCategoryIds?: number[];
    mdaSda?: string;
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex.toString())
      .set('pageSize', params.pageSize.toString());

    const appendParam = (key: string, value?: string | number[]) => {
      if (value === undefined || value === null) {
        return;
      }
      if (Array.isArray(value)) {
        if (value.length > 0) {
          queryParams = queryParams.set(key, value.join(','));
        }
        return;
      }
      const trimmed = String(value).trim();
      if (trimmed !== '') {
        queryParams = queryParams.set(key, trimmed);
      }
    };

    appendParam('searchFilter', params.searchFilter);
    appendParam('startDate', params.startDate);
    appendParam('endDate', params.endDate);
    appendParam('regionIds', params.regionIds);
    appendParam('countryIds', params.countryIds);
    appendParam('channelIds', params.channelIds);
    appendParam('accountIds', params.accountIds);
    appendParam('storeIds', params.storeIds);
    appendParam('promoterTypeIds', params.promoterTypeIds);
    appendParam('promoterCategoryIds', params.promoterCategoryIds);
    appendParam('mdaSda', params.mdaSda);

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/dailySurvey/details`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public exportDailySurveySummary(params: {
    searchFilter?: string;
    startDate?: string;
    endDate?: string;
    regionIds?: number[];
    countryIds?: number[];
    channelIds?: number[];
    accountIds?: number[];
    storeIds?: number[];
    promoterTypeIds?: number[];
    promoterCategoryIds?: number[];
    mdaSda?: string;
  } = {}): Observable<Blob> {
    let queryParams = new HttpParams();

    const appendParam = (key: string, value?: string | number[]) => {
      if (value === undefined || value === null) {
        return;
      }
      if (Array.isArray(value)) {
        if (value.length > 0) {
          queryParams = queryParams.set(key, value.join(','));
        }
        return;
      }
      const trimmed = String(value).trim();
      if (trimmed !== '') {
        queryParams = queryParams.set(key, trimmed);
      }
    };

    appendParam('searchFilter', params.searchFilter);
    appendParam('startDate', params.startDate);
    appendParam('endDate', params.endDate);
    appendParam('regionIds', params.regionIds);
    appendParam('countryIds', params.countryIds);
    appendParam('channelIds', params.channelIds);
    appendParam('accountIds', params.accountIds);
    appendParam('storeIds', params.storeIds);
    appendParam('promoterTypeIds', params.promoterTypeIds);
    appendParam('promoterCategoryIds', params.promoterCategoryIds);
    appendParam('mdaSda', params.mdaSda);

    return this.http
      .get(`${this.SURVEY_SERVICE_URL}/dailySurvey/summary/downloadExcelReport`, {
        params: queryParams,
        responseType: 'blob',
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getDailySurveyCategories(): Observable<any> {
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/dailySurvey/categories`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public exportDailySurvey(params: {
    searchFilter?: string;
    startDate?: string;
    endDate?: string;
    regionIds?: number[];
    countryIds?: number[];
    channelIds?: number[];
    accountIds?: number[];
    storeIds?: number[];
    promoterTypeIds?: number[];
    promoterCategoryIds?: number[];
    mdaSda?: string;
  } = {}): Observable<Blob> {
    let queryParams = new HttpParams();

    const appendParam = (key: string, value?: string | number[]) => {
      if (value === undefined || value === null) {
        return;
      }
      if (Array.isArray(value)) {
        if (value.length > 0) {
          queryParams = queryParams.set(key, value.join(','));
        }
        return;
      }
      const trimmed = String(value).trim();
      if (trimmed !== '') {
        queryParams = queryParams.set(key, trimmed);
      }
    };

    appendParam('searchFilter', params.searchFilter);
    appendParam('startDate', params.startDate);
    appendParam('endDate', params.endDate);
    appendParam('regionIds', params.regionIds);
    appendParam('countryIds', params.countryIds);
    appendParam('channelIds', params.channelIds);
    appendParam('accountIds', params.accountIds);
    appendParam('storeIds', params.storeIds);
    appendParam('promoterTypeIds', params.promoterTypeIds);
    appendParam('promoterCategoryIds', params.promoterCategoryIds);
    appendParam('mdaSda', params.mdaSda);

    return this.http
      .get(`${this.SURVEY_SERVICE_URL}/dailySurvey/downloadExcelReport`, {
        params: queryParams,
        responseType: 'blob',
      })
      .pipe(catchError(this.errorHandler.handleError));
  }


  //to update the price promotion (RRP, Promo, Start Date, End Date)
  public pricePromotionPromoRRPUpdate(data: {
    id: number;
    rrp: number;
    promo: number;
    startDate?: string | null;
    endDate?: string | null;
  }): Observable<any> {
    return this.http.post<any>(`${this.SURVEY_SERVICE_URL}/pricePromotion/updatePricePromotionPromoRRP`, data)
      .pipe(catchError(this.errorHandler.handleError))
  }

  public downloadNotMovingStockTemplate(
    countryId?: number,
    accountId?: number,
    month?: string,
    year?: number,
  ): Observable<Blob> {
    let params = new HttpParams();
    if (countryId) {
      params = params.set('countryId', countryId.toString());
    }
    if (accountId) {
      params = params.set('accountId', accountId.toString());
    }
    if (month) {
      params = params.set('month', month);
    }
    if (year) {
      params = params.set('year', year.toString());
    }
    return this.http
      .get<Blob>(
        `${this.RAWDATA_URL}/notMovingStockExport/downloadExcelTemplate`,
        { params, responseType: 'blob' as 'json' }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public exportNotMovingStockExcel(
    countryId?: any,
    regionId?: any,
    search?: string,
    columns?: string[],
    account?: any,
    month?: string,
    year?: number,
    storeId?: any,
    model?: any,
    status?: any,
  ): Observable<Blob> {
    let params = new HttpParams();

    if (countryId) {
      const val = Array.isArray(countryId) ? countryId.join(',') : countryId.toString();
      if (val) {
        params = params.set('countryId', val);
      }
    }

    if (regionId) {
      const val = Array.isArray(regionId) ? regionId.join(',') : regionId.toString();
      if (val) {
        params = params.set('regionId', val);
      }
    }

    if (search) {
      params = params.set('search', search);
    }

    if (account) {
      const val = Array.isArray(account) ? account.join(',') : account.toString();
      if (val) {
        params = params.set('account', val);
      }
    }

    if (month) {
      params = params.set('month', month);
    }

    if (year) {
      params = params.set('year', year.toString());
    }

    if (storeId) {
      const val = Array.isArray(storeId) ? storeId.join(',') : storeId.toString();
      if (val) {
        params = params.set('storeId', val);
      }
    }

    if (model) {
      const val = Array.isArray(model) ? model.join(',') : model.toString();
      if (val) {
        params = params.set('model', val);
      }
    }

    if (status) {
      const val = Array.isArray(status) ? status.join(',') : status.toString();
      if (val) {
        params = params.set('status', val);
      }
    }

    return this.http
      .post<Blob>(
        `${this.RAWDATA_URL}/notMovingStockExport/downloadExcelReport`,
        { columns },
        {
          params,
          responseType: 'blob' as 'json',
        }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public exportStockAuditExcel(
    countryId?: any,
    regionId?: any,
    search?: string,
    columns?: string[],
    account?: any,
    month?: string,
    year?: number,
    storeId?: any,
    status?: any,
    activeTab?: string,
  ): Observable<Blob> {
    let params = new HttpParams();

    if (countryId) {
      const val = Array.isArray(countryId) ? countryId.join(',') : countryId.toString();
      if (val) {
        params = params.set('countryId', val);
      }
    }

    if (regionId) {
      const val = Array.isArray(regionId) ? regionId.join(',') : regionId.toString();
      if (val) {
        params = params.set('regionId', val);
      }
    }

    if (search) {
      params = params.set('search', search);
    }

    if (account) {
      const val = Array.isArray(account) ? account.join(',') : account.toString();
      if (val) {
        params = params.set('account', val);
      }
    }

    if (month) {
      params = params.set('month', month);
    }

    if (year) {
      params = params.set('year', year.toString());
    }

    if (storeId) {
      const val = Array.isArray(storeId) ? storeId.join(',') : storeId.toString();
      if (val) {
        params = params.set('storeId', val);
      }
    }

    if (status) {
      const val = Array.isArray(status) ? status.join(',') : status.toString();
      if (val) {
        params = params.set('status', val);
      }
    }

    if (activeTab) {
      params = params.set('activeTab', activeTab);
    }

    return this.http
      .post<Blob>(
        `${this.RAWDATA_URL}/stockAuditExport/downloadExcelReport`,
        { columns },
        {
          params,
          responseType: 'blob' as 'json',
        }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateNotMovingStockRecord(payload: any): Observable<any> {
    return this.http
      .post(`${this.SURVEY_SERVICE_URL}/notMovingStock/updateRecord`, payload)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteNotMovingStockRecord(id: number): Observable<any> {
    return this.http
      .post(`${this.SURVEY_SERVICE_URL}/notMovingStock/deleteRecord`, { id })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteNotMovingStockSummaryData(payload: {
    countryId?: number[] | number;
    accountId?: number[] | number;
    month?: string;
    year?: number;
  }): Observable<any> {
    return this.http
      .post(`${this.SURVEY_SERVICE_URL}/notMovingStock/deleteSummaryData`, payload)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public validateNotMovingStock(
    data: any[],
    countryId?: number,
    accountId?: number,
    month?: string,
    year?: number,
  ): Observable<any> {
    let params = new HttpParams();
    if (countryId) {
      params = params.set('countryId', countryId.toString());
    }
    if (accountId) {
      params = params.set('accountId', accountId.toString());
    }
    if (month) {
      params = params.set('month', month);
    }
    if (year) {
      params = params.set('year', year.toString());
    }
    return this.http
      .post<any>(`${this.SURVEY_SERVICE_URL}/notMovingStock/validate`, data, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public bulkUploadNotMovingStock(
    data: any[],
    countryId?: number,
    accountId?: number,
    month?: string,
    year?: number,
  ): Observable<any> {
    let params = new HttpParams();
    if (countryId) {
      params = params.set('countryId', countryId.toString());
    }
    if (accountId) {
      params = params.set('accountId', accountId.toString());
    }
    if (month) {
      params = params.set('month', month);
    }
    if (year) {
      params = params.set('year', year.toString());
    }
    return this.http
      .post<any>(`${this.SURVEY_SERVICE_URL}/notMovingStock/bulkUpload`, data, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public validateStockAudit(
    data: any[],
    countryId?: number,
    accountId?: number,
    month?: string,
    year?: number,
  ): Observable<any> {
    let params = new HttpParams();
    if (countryId) {
      params = params.set('countryId', countryId.toString());
    }
    if (accountId) {
      params = params.set('accountId', accountId.toString());
    }
    if (month) {
      params = params.set('month', month);
    }
    if (year) {
      params = params.set('year', year.toString());
    }
    return this.http
      .post<any>(`${this.SURVEY_SERVICE_URL}/stockAudit/validate`, data, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public bulkUploadStockAudit(
    data: any[],
    countryId?: number,
    accountId?: number,
    month?: string,
    year?: number,
  ): Observable<any> {
    let params = new HttpParams();
    if (countryId) {
      params = params.set('countryId', countryId.toString());
    }
    if (accountId) {
      params = params.set('accountId', accountId.toString());
    }
    if (month) {
      params = params.set('month', month.toString());
    }
    if (year) {
      params = params.set('year', year.toString());
    }
    return this.http
      .post<any>(`${this.SURVEY_SERVICE_URL}/stockAudit/bulkUpload`, data, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateStockAuditStatus(data: any): Observable<any> {
    return this.http
      .post<any>(`${this.SURVEY_SERVICE_URL}/stockAudit/updateStatus`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllStockAudit(
    pageIndex: number = 1,
    pageSize: number = 10,
    countryId?: any,
    regionId?: any,
    search?: string,
    account?: any,
    month?: string,
    year?: number,
    storeId?: any,
    status?: any,
    model?: any,
  ): Observable<any> {
    let params = new HttpParams()
      .set('pageIndex', pageIndex.toString())
      .set('pageSize', pageSize.toString());

    if (countryId) {
      const val = Array.isArray(countryId) ? countryId.join(',') : countryId.toString();
      if (val) {
        params = params.set('countryId', val);
      }
    }

    if (regionId) {
      const val = Array.isArray(regionId) ? regionId.join(',') : regionId.toString();
      if (val) {
        params = params.set('regionId', val);
      }
    }

    if (account) {
      const val = Array.isArray(account) ? account.join(',') : account.toString();
      if (val) {
        params = params.set('account', val);
      }
    }

    if (month) {
      params = params.set('month', month);
    }

    if (year) {
      params = params.set('year', year.toString());
    }

    if (storeId) {
      const val = Array.isArray(storeId) ? storeId.join(',') : storeId.toString();
      if (val) {
        params = params.set('storeId', val);
      }
    }

    if (status !== undefined && status !== null && status !== '') {
      params = params.set('status', status.toString());
    }

    if (model) {
      const val = Array.isArray(model) ? model.join(',') : model.toString();
      if (val) {
        params = params.set('model', val);
      }
    }

    if (search && search.trim() !== '') {
      params = params.set('search', search.trim());
    }

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/stockAudit/getAllModelResult`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getStockAuditKpiCards(filters: {
    countryId?: any,
    regionId?: any,
    search?: string,
    account?: any,
    month?: string,
    year?: number,
    storeId?: any,
    status?: any,
    model?: any,
  }): Observable<any> {
    let params = new HttpParams();

    if (filters.countryId) {
      const val = Array.isArray(filters.countryId) ? filters.countryId.join(',') : filters.countryId.toString();
      if (val) {
        params = params.set('countryId', val);
      }
    }

    if (filters.regionId) {
      const val = Array.isArray(filters.regionId) ? filters.regionId.join(',') : filters.regionId.toString();
      if (val) {
        params = params.set('regionId', val);
      }
    }

    if (filters.account) {
      const val = Array.isArray(filters.account) ? filters.account.join(',') : filters.account.toString();
      if (val) {
        params = params.set('account', val);
      }
    }

    if (filters.month) {
      params = params.set('month', filters.month);
    }

    if (filters.year) {
      params = params.set('year', filters.year.toString());
    }

    if (filters.storeId) {
      const val = Array.isArray(filters.storeId) ? filters.storeId.join(',') : filters.storeId.toString();
      if (val) {
        params = params.set('storeId', val);
      }
    }

    if (filters.status !== undefined && filters.status !== null && filters.status !== '') {
      params = params.set('status', filters.status.toString());
    }

    if (filters.model) {
      const val = Array.isArray(filters.model) ? filters.model.join(',') : filters.model.toString();
      if (val) {
        params = params.set('model', val);
      }
    }

    if (filters.search && filters.search.trim() !== '') {
      params = params.set('search', filters.search.trim());
    }

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/stockAudit/kpiCards`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllNotMovingStock(
    pageIndex: number = 1,
    pageSize: number = 10,
    countryId?: any,
    regionId?: any,
    search?: string,
    account?: any,
    month?: string,
    year?: number,
    storeId?: any,
    status?: any,
    model?: any,
  ): Observable<any> {
    let params = new HttpParams()
      .set('pageIndex', pageIndex.toString())
      .set('pageSize', pageSize.toString());

    if (countryId) {
      const val = Array.isArray(countryId) ? countryId.join(',') : countryId.toString();
      if (val) {
        params = params.set('countryId', val);
      }
    }

    if (regionId) {
      const val = Array.isArray(regionId) ? regionId.join(',') : regionId.toString();
      if (val) {
        params = params.set('regionId', val);
      }
    }

    if (search) {
      params = params.set('search', search);
    }

    if (account) {
      const val = Array.isArray(account) ? account.join(',') : account.toString();
      if (val) {
        params = params.set('account', val);
      }
    }

    if (month) {
      params = params.set('month', month);
    }

    if (year) {
      params = params.set('year', year.toString());
    }

    if (storeId) {
      const val = Array.isArray(storeId) ? storeId.join(',') : storeId.toString();
      if (val) {
        params = params.set('storeId', val);
      }
    }

    if (status !== undefined && status !== null && status !== 'ALL') {
      const val = Array.isArray(status)
        ? status.filter((s: any) => s !== undefined && s !== null && s !== '' && s !== 'ALL').join(',')
        : status.toString();
      if (val && val !== 'ALL') {
        params = params.set('status', val);
      }
    }

    if (model) {
      const val = Array.isArray(model) ? model.join(',') : model.toString();
      if (val) {
        params = params.set('model', val);
      }
    }

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/notMovingStock/getAllQualityResult`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getNotMovingStockStatusSummary(
    countryId?: any,
    regionId?: any,
    search?: string,
    storeId?: any,
    month?: string,
    year?: number,
    account?: any,
    model?: any,
  ): Observable<any> {
    let params = new HttpParams();

    if (countryId) {
      const val = Array.isArray(countryId) ? countryId.join(',') : countryId.toString();
      if (val) {
        params = params.set('countryId', val);
      }
    }

    if (regionId) {
      const val = Array.isArray(regionId) ? regionId.join(',') : regionId.toString();
      if (val) {
        params = params.set('regionId', val);
      }
    }

    if (search) {
      params = params.set('search', search);
    }

    if (storeId) {
      const val = Array.isArray(storeId) ? storeId.join(',') : storeId.toString();
      if (val) {
        params = params.set('storeId', val);
      }
    }

    if (account) {
      const val = Array.isArray(account) ? account.join(',') : account.toString();
      if (val) {
        params = params.set('account', val);
      }
    }

    if (month) {
      params = params.set('month', month);
    }

    if (year) {
      params = params.set('year', year.toString());
    }

    if (model) {
      const val = Array.isArray(model) ? model.join(',') : model.toString();
      if (val) {
        params = params.set('model', val);
      }
    }

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/notMovingStock/getStatusSummary`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getSummaryKpiData(
    countryId?: any,
    regionId?: any,
    storeId?: any,
    month?: string,
    year?: number,
    account?: any,
    model?: any,
  ): Observable<any> {
    let params = new HttpParams();

    if (countryId) {
      const val = Array.isArray(countryId) ? countryId.join(',') : countryId.toString();
      if (val) {
        params = params.set('countryId', val);
      }
    }

    if (regionId) {
      const val = Array.isArray(regionId) ? regionId.join(',') : regionId.toString();
      if (val) {
        params = params.set('regionId', val);
      }
    }

    if (storeId) {
      const val = Array.isArray(storeId) ? storeId.join(',') : storeId.toString();
      if (val) {
        params = params.set('storeId', val);
      }
    }

    if (month) {
      params = params.set('month', month);
    }

    if (year) {
      params = params.set('year', year.toString());
    }

    if (account) {
      const val = Array.isArray(account) ? account.join(',') : account.toString();
      if (val) {
        params = params.set('account', val);
      }
    }

    if (model) {
      const val = Array.isArray(model) ? model.join(',') : model.toString();
      if (val) {
        params = params.set('model', val);
      }
    }

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/notMovingStock/SummaryKpiData`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getSubmissionPageKpidata(
    countryId?: any,
    regionId?: any,
    storeId?: any,
    month?: string,
    year?: number,
    account?: any,
    model?: any,
    status?: any,
  ): Observable<any> {
    let params = new HttpParams();

    if (countryId) {
      const val = Array.isArray(countryId) ? countryId.join(',') : countryId.toString();
      if (val) {
        params = params.set('countryId', val);
      }
    }

    if (regionId) {
      const val = Array.isArray(regionId) ? regionId.join(',') : regionId.toString();
      if (val) {
        params = params.set('regionId', val);
      }
    }

    if (storeId) {
      const val = Array.isArray(storeId) ? storeId.join(',') : storeId.toString();
      if (val) {
        params = params.set('storeId', val);
      }
    }

    if (month) {
      params = params.set('month', month);
    }

    if (year) {
      params = params.set('year', year.toString());
    }

    if (account) {
      const val = Array.isArray(account) ? account.join(',') : account.toString();
      if (val) {
        params = params.set('account', val);
      }
    }

    if (model) {
      const val = Array.isArray(model) ? model.join(',') : model.toString();
      if (val) {
        params = params.set('model', val);
      }
    }

    if (status) {
      const val = Array.isArray(status) ? status.join(',') : status.toString();
      if (val) {
        params = params.set('status', val);
      }
    }

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/notMovingStock/getSubmissionPageKpidata`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAccountsNotUploaded(
    pageIndex: number = 1,
    pageSize: number = 10,
    countryId?: any,
    regionId?: any,
    search?: string,
    month?: string,
    year?: number,
  ): Observable<any> {
    let params = new HttpParams()
      .set('pageIndex', pageIndex.toString())
      .set('pageSize', pageSize.toString());

    if (countryId) {
      const val = Array.isArray(countryId) ? countryId.join(',') : countryId.toString();
      if (val) {
        params = params.set('countryId', val);
      }
    }

    if (regionId) {
      const val = Array.isArray(regionId) ? regionId.join(',') : regionId.toString();
      if (val) {
        params = params.set('regionId', val);
      }
    }

    if (search) {
      params = params.set('search', search);
    }

    if (month) {
      params = params.set('month', month);
    }

    if (year) {
      params = params.set('year', year.toString());
    }

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/notMovingStock/getAccountsNotUploaded`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getNonMovingStockBasedonMonthCountry(
    pageIndex: number = 1,
    pageSize: number = 10,
    countryId?: any,
    regionId?: any,
    search?: string,
    month?: string,
    year?: number,
    storeId?: any,
    account?: any,
    model?: any
  ): Observable<any> {
    let params = new HttpParams()
      .set('pageIndex', pageIndex.toString())
      .set('pageSize', pageSize.toString());

    if (countryId) {
      const val = Array.isArray(countryId) ? countryId.join(',') : countryId.toString();
      if (val) {
        params = params.set('countryId', val);
      }
    }

    if (regionId) {
      const val = Array.isArray(regionId) ? regionId.join(',') : regionId.toString();
      if (val) {
        params = params.set('regionId', val);
      }
    }

    if (search) {
      params = params.set('search', search);
    }

    if (account) {
      const val = Array.isArray(account) ? account.join(',') : account.toString();
      if (val) {
        params = params.set('account', val);
      }
    }

    if (month) {
      params = params.set('month', month);
    }

    if (year) {
      params = params.set('year', year.toString());
    }

    if (storeId) {
      const val = Array.isArray(storeId) ? storeId.join(',') : storeId.toString();
      if (val) {
        params = params.set('storeId', val);
      }
    }

    if (model) {
      const val = Array.isArray(model) ? model.join(',') : model.toString();
      if (val) {
        params = params.set('model', val);
      }
    }

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/notMovingStock/getNonMovingStockBasedonMonthCountry`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }



  public downloadStockAuditTemplate(
    countryId?: number,
    accountId?: number,
    month?: string,
    year?: number,
  ): Observable<Blob> {
    let params = new HttpParams();
    if (countryId) {
      params = params.set('countryId', countryId.toString());
    }
    if (accountId) {
      params = params.set('accountId', accountId.toString());
    }
    if (month) {
      params = params.set('month', month);
    }
    if (year) {
      params = params.set('year', year.toString());
    }
    return this.http
      .get(`${this.RAWDATA_URL}/stockAuditExport/downloadExcelTemplate`, {
        params,
        responseType: 'blob',
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public addStockAuditReason(reason: string): Observable<any> {
    return this.http
      .post<any>(`${this.SURVEY_SERVICE_URL}/stockAudit/addReason`, { reason })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllStockAuditReasons(): Observable<any> {
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/stockAudit/getAllStockReasons`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getStockAuditUploadsGroupedByCountry(
    pageIndex: number = 1,
    pageSize: number = 10,
    countryId?: any,
    regionId?: any,
    account?: any,
    month?: string,
    year?: number,
    search?: string,
  ): Observable<any> {
    let params = new HttpParams()
      .set('pageIndex', pageIndex.toString())
      .set('pageSize', pageSize.toString());

    if (countryId) {
      const val = Array.isArray(countryId) ? countryId.join(',') : countryId.toString();
      if (val) {
        params = params.set('countryId', val);
      }
    }

    if (regionId) {
      const val = Array.isArray(regionId) ? regionId.join(',') : regionId.toString();
      if (val) {
        params = params.set('regionId', val);
      }
    }

    if (account) {
      const val = Array.isArray(account) ? account.join(',') : account.toString();
      if (val) {
        params = params.set('account', val);
      }
    }

    if (month) {
      params = params.set('month', month);
    }

    if (year) {
      params = params.set('year', year.toString());
    }

    if (search && search.trim() !== '') {
      params = params.set('search', search.trim());
    }

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/stockAudit/getUploadsGroupedByCountry`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getStockAuditbasedOnStores(
    pageIndex: number = 1,
    pageSize: number = 10,
    countryId?: any,
    regionId?: any,
    account?: any,
    storeId?: any,
    month?: string,
    year?: number,
    search?: string,
  ): Observable<any> {
    let params = new HttpParams()
      .set('pageIndex', pageIndex.toString())
      .set('pageSize', pageSize.toString());

    if (countryId) {
      const val = Array.isArray(countryId) ? countryId.join(',') : countryId.toString();
      if (val) {
        params = params.set('countryId', val);
      }
    }

    if (regionId) {
      const val = Array.isArray(regionId) ? regionId.join(',') : regionId.toString();
      if (val) {
        params = params.set('regionId', val);
      }
    }

    if (account) {
      const val = Array.isArray(account) ? account.join(',') : account.toString();
      if (val) {
        params = params.set('account', val);
      }
    }

    if (storeId) {
      const val = Array.isArray(storeId) ? storeId.join(',') : storeId.toString();
      if (val) {
        params = params.set('storeId', val);
      }
    }

    if (month) {
      params = params.set('month', month);
    }

    if (year) {
      params = params.set('year', year.toString());
    }

    if (search && search.trim() !== '') {
      params = params.set('search', search.trim());
    }

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/stockAudit/getStockAuditbasedOnStores`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getStockAuditByAccountSummary(
    pageIndex: number = 1,
    pageSize: number = 10,
    countryId?: any,
    regionId?: any,
    account?: any,
    storeId?: any,
    month?: string,
    year?: number,
    search?: string,
  ): Observable<any> {
    let params = new HttpParams()
      .set('pageIndex', pageIndex.toString())
      .set('pageSize', pageSize.toString());

    if (countryId) {
      const val = Array.isArray(countryId) ? countryId.join(',') : countryId.toString();
      if (val) {
        params = params.set('countryId', val);
      }
    }

    if (regionId) {
      const val = Array.isArray(regionId) ? regionId.join(',') : regionId.toString();
      if (val) {
        params = params.set('regionId', val);
      }
    }

    if (account) {
      const val = Array.isArray(account) ? account.join(',') : account.toString();
      if (val) {
        params = params.set('account', val);
      }
    }

    if (storeId) {
      const val = Array.isArray(storeId) ? storeId.join(',') : storeId.toString();
      if (val) {
        params = params.set('storeId', val);
      }
    }

    if (month) {
      params = params.set('month', month);
    }

    if (year) {
      params = params.set('year', year.toString());
    }

    if (search && search.trim() !== '') {
      params = params.set('search', search.trim());
    }

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/stockAudit/getByAccountSummary`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getStockAuditByTds(
    pageIndex: number = 1,
    pageSize: number = 10,
    countryId?: any,
    regionId?: any,
    account?: any,
    storeId?: any,
    month?: string,
    year?: number,
    search?: string,
    tdsId?: any,
  ): Observable<any> {
    let params = new HttpParams()
      .set('pageIndex', pageIndex.toString())
      .set('pageSize', pageSize.toString());

    if (countryId) {
      const val = Array.isArray(countryId) ? countryId.join(',') : countryId.toString();
      if (val) {
        params = params.set('countryId', val);
      }
    }

    if (regionId) {
      const val = Array.isArray(regionId) ? regionId.join(',') : regionId.toString();
      if (val) {
        params = params.set('regionId', val);
      }
    }

    if (account) {
      const val = Array.isArray(account) ? account.join(',') : account.toString();
      if (val) {
        params = params.set('account', val);
      }
    }

    if (storeId) {
      const val = Array.isArray(storeId) ? storeId.join(',') : storeId.toString();
      if (val) {
        params = params.set('storeId', val);
      }
    }

    if (tdsId) {
      const val = Array.isArray(tdsId) ? tdsId.join(',') : tdsId.toString();
      if (val) {
        params = params.set('tdsId', val);
      }
    }

    if (month) {
      params = params.set('month', month);
    }

    if (year) {
      params = params.set('year', year.toString());
    }

    if (search && search.trim() !== '') {
      params = params.set('search', search.trim());
    }

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/stockAudit/stockAuditByTds`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllTdsStoresNotInList(
    pageIndex: number = 1,
    pageSize: number = 100,
    countryId?: any,
    regionId?: any,
    account?: any,
    storeId?: any,
    month?: string,
    year?: number,
    search?: string,
    tdsId?: any,
  ): Observable<any> {
    let params = new HttpParams()
      .set('pageIndex', pageIndex.toString())
      .set('pageSize', pageSize.toString());

    if (countryId) {
      const val = Array.isArray(countryId) ? countryId.join(',') : countryId.toString();
      if (val) {
        params = params.set('countryId', val);
      }
    }

    if (regionId) {
      const val = Array.isArray(regionId) ? regionId.join(',') : regionId.toString();
      if (val) {
        params = params.set('regionId', val);
      }
    }

    if (account) {
      const val = Array.isArray(account) ? account.join(',') : account.toString();
      if (val) {
        params = params.set('account', val);
      }
    }

    if (storeId) {
      const val = Array.isArray(storeId) ? storeId.join(',') : storeId.toString();
      if (val) {
        params = params.set('storeId', val);
      }
    }

    if (tdsId) {
      const val = Array.isArray(tdsId) ? tdsId.join(',') : tdsId.toString();
      if (val) {
        params = params.set('tdsId', val);
      }
    }

    if (month) {
      params = params.set('month', month);
    }

    if (year) {
      params = params.set('year', year.toString());
    }

    if (search && search.trim() !== '') {
      params = params.set('search', search.trim());
    }

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/stockAudit/getAllTdsStoresNotInList`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getTdsStoresNotInListById(
    pageIndex: number = 1,
    pageSize: number = 10,
    countryId?: any,
    regionId?: any,
    account?: any,
    storeId?: any,
    month?: string,
    year?: number,
    search?: string,
    tdsId?: any,
  ): Observable<any> {
    let params = new HttpParams()
      .set('pageIndex', pageIndex.toString())
      .set('pageSize', pageSize.toString());

    if (countryId) {
      const val = Array.isArray(countryId) ? countryId.join(',') : countryId.toString();
      if (val) {
        params = params.set('countryId', val);
      }
    }

    if (regionId) {
      const val = Array.isArray(regionId) ? regionId.join(',') : regionId.toString();
      if (val) {
        params = params.set('regionId', val);
      }
    }

    if (account) {
      const val = Array.isArray(account) ? account.join(',') : account.toString();
      if (val) {
        params = params.set('account', val);
      }
    }

    if (storeId) {
      const val = Array.isArray(storeId) ? storeId.join(',') : storeId.toString();
      if (val) {
        params = params.set('storeId', val);
      }
    }

    if (tdsId) {
      const val = Array.isArray(tdsId) ? tdsId.join(',') : tdsId.toString();
      if (val) {
        params = params.set('tdsId', val);
      }
    }

    if (month) {
      params = params.set('month', month);
    }

    if (year) {
      params = params.set('year', year.toString());
    }

    if (search && search.trim() !== '') {
      params = params.set('search', search.trim());
    }

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/stockAudit/getTdsStoresNotInListById`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getDashboardStockById(
    pageIndex: number = 1,
    pageSize: number = 10,
    countryId?: any,
    regionId?: any,
    account?: any,
    storeId?: any,
    month?: string,
    year?: number,
    search?: string,
    tdsId?: any,
  ): Observable<any> {
    let params = new HttpParams()
      .set('pageIndex', pageIndex.toString())
      .set('pageSize', pageSize.toString());

    if (countryId) {
      const val = Array.isArray(countryId) ? countryId.join(',') : countryId.toString();
      if (val) {
        params = params.set('countryId', val);
      }
    }

    if (regionId) {
      const val = Array.isArray(regionId) ? regionId.join(',') : regionId.toString();
      if (val) {
        params = params.set('regionId', val);
      }
    }

    if (account) {
      const val = Array.isArray(account) ? account.join(',') : account.toString();
      if (val) {
        params = params.set('account', val);
      }
    }

    if (storeId) {
      const val = Array.isArray(storeId) ? storeId.join(',') : storeId.toString();
      if (val) {
        params = params.set('storeId', val);
      }
    }

    if (tdsId) {
      const val = Array.isArray(tdsId) ? tdsId.join(',') : tdsId.toString();
      if (val) {
        params = params.set('tdsId', val);
      }
    }

    if (month) {
      params = params.set('month', month);
    }

    if (year) {
      params = params.set('year', year.toString());
    }

    if (search && search.trim() !== '') {
      params = params.set('search', search.trim());
    }

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/stockAudit/getDashboardStockById`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }


  public getStockAuditDashboardKpiData(
    countryId?: any,
    regionId?: any,
    account?: any,
    storeId?: any,
    month?: string,
    year?: number,
    search?: string,
    tdsId?: any,
  ): Observable<any> {
    let params = new HttpParams();

    if (countryId) {
      const val = Array.isArray(countryId) ? countryId.join(',') : countryId.toString();
      if (val) {
        params = params.set('countryId', val);
      }
    }

    if (regionId) {
      const val = Array.isArray(regionId) ? regionId.join(',') : regionId.toString();
      if (val) {
        params = params.set('regionId', val);
      }
    }

    if (account) {
      const val = Array.isArray(account) ? account.join(',') : account.toString();
      if (val) {
        params = params.set('account', val);
      }
    }

    if (storeId) {
      const val = Array.isArray(storeId) ? storeId.join(',') : storeId.toString();
      if (val) {
        params = params.set('storeId', val);
      }
    }

    if (tdsId) {
      const val = Array.isArray(tdsId) ? tdsId.join(',') : tdsId.toString();
      if (val) {
        params = params.set('tdsId', val);
      }
    }

    if (month) {
      params = params.set('month', month);
    }

    if (year) {
      params = params.set('year', year.toString());
    }

    if (search && search.trim() !== '') {
      params = params.set('search', search.trim());
    }

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/stockAudit/getDashboardKpidata`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getStockAuditReasons(): Observable<any> {
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/stockAudit/getAllStockReasons`)
      .pipe(catchError(this.errorHandler.handleError));
  }



  public getExecDashboardValueTypes(): Observable<any[]> {
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/execDashboardUpload/valueTypes`)
      .pipe(
        map((res: any) => (Array.isArray(res) ? res : res?.result ?? [])),
        catchError(this.errorHandler.handleError),
      );
  }

  public getExecDashboardGlobalProductLines(): Observable<string[]> {
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/execDashboardUpload/globalProductLines`)
      .pipe(
        map((res: any) => {
          const list = Array.isArray(res) ? res : res?.result ?? [];
          return (list as unknown[])
            .map((value) => String(value ?? '').trim())
            .filter((value) => !!value);
        }),
        catchError(this.errorHandler.handleError),
      );
  }

  public getExecDashboardUploads(params: {
    valueTypeId: number;
    pageIndex: number;
    pageSize: number;
    year?: number[] | null;
    bc?: number[] | null;
  }): Observable<{ data: any[]; paginatorValue: { totalData: number; from: number; to: number } }> {
    const queryParams: Record<string, string> = {
      valueTypeId: String(params.valueTypeId),
      pageIndex: String(params.pageIndex),
      pageSize: String(params.pageSize),
    };
    if (params.year?.length) {
      queryParams['year'] = params.year.join(',');
    }
    if (params.bc?.length) {
      queryParams['bc'] = params.bc.join(',');
    }

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/execDashboardUpload/getAll`, {
        params: queryParams,
      })
      .pipe(
        map((res: any) => {
          const result = res?.result ?? res;
          return {
            data: Array.isArray(result?.data) ? result.data : [],
            paginatorValue: result?.paginatorValue ?? {
              totalData: 0,
              from: 0,
              to: 0,
            },
          };
        }),
        catchError(this.errorHandler.handleError),
      );
  }

  public getExecDashboardFilterOptions(params: {
    valueTypeId: number;
    year?: number[] | null;
    bc?: number[] | null;
  }): Observable<{ years: number[]; bcs: number[] }> {
    const queryParams: Record<string, string> = {
      valueTypeId: String(params.valueTypeId),
    };
    if (params.year?.length) {
      queryParams['year'] = params.year.join(',');
    }
    if (params.bc?.length) {
      queryParams['bc'] = params.bc.join(',');
    }

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/execDashboardUpload/filterOptions`, {
        params: queryParams,
      })
      .pipe(
        map((res: any) => {
          const result = res?.result ?? res;
          return {
            years: Array.isArray(result?.years) ? result.years.map(Number) : [],
            bcs: Array.isArray(result?.bcs) ? result.bcs.map(Number) : [],
          };
        }),
        catchError(this.errorHandler.handleError),
      );
  }

  public getExecDashboardUploadByPeriod(
    year: number,
    bc: number,
    valueTypeId: number,
  ): Observable<any | null> {
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/execDashboardUpload/getByPeriod`, {
        params: {
          year: String(year),
          bc: String(bc),
          valueTypeId: String(valueTypeId),
        },
      })
      .pipe(
        map((res: any) => {
          if (res && typeof res === 'object' && 'result' in res) {
            return res.result ?? null;
          }
          return res ?? null;
        }),
        catchError(this.errorHandler.handleError),
      );
  }

  public uploadExecDashboardFile(
    year: number,
    bc: number,
    valueTypeId: number,
    file: File,
  ): Observable<any> {
    const formData = new FormData();
    formData.append('year', String(year));
    formData.append('bc', String(bc));
    formData.append('valueTypeId', String(valueTypeId));
    formData.append('file', file);
    return this.http
      .post<any>(`${this.SURVEY_SERVICE_URL}/execDashboardUpload/upload`, formData)
      .pipe(
        map((res: any) => res?.result ?? res),
        catchError(this.errorHandler.handleError),
      );
  }

  public downloadExecDashboardFile(id: number): Observable<Blob> {
    const headers = new HttpHeaders().set('isBlob', 'true');
    return this.http
      .get(`${this.SURVEY_SERVICE_URL}/execDashboardUpload/download/${id}`, {
        headers,
        responseType: 'blob',
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  // ===================== EXEC DASHBOARD KPI APIs =====================

  public getExecDashboardKpis(params: {
    pageIndex: number;
    pageSize: number;
    year?: number[] | null;
    bcNo?: number[] | null;
    /** Months 1-12 and/or 'FY' */
    period?: (number | 'FY')[] | null;
    region?: string[] | null;
    country?: string[] | null;
    valueTypeId?: number[] | null;
  }): Observable<{ data: any[]; paginatorValue: { totalData: number; from: number; to: number } }> {
    const queryParams: Record<string, string> = {
      pageIndex: String(params.pageIndex),
      pageSize: String(params.pageSize),
    };
    if (params.year?.length) queryParams['year'] = params.year.join(',');
    if (params.bcNo?.length) queryParams['bcNo'] = params.bcNo.join(',');
    if (params.period?.length) queryParams['period'] = params.period.join(',');
    if (params.region?.length) queryParams['region'] = params.region.join(',');
    if (params.country?.length) queryParams['country'] = params.country.join(',');
    if (params.valueTypeId?.length) queryParams['valueTypeId'] = params.valueTypeId.join(',');

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/execDashboardKpi/getAll`, { params: queryParams })
      .pipe(
        map((res: any) => {
          const result = res?.result ?? res;
          return {
            data: Array.isArray(result?.data) ? result.data : [],
            paginatorValue: result?.paginatorValue ?? { totalData: 0, from: 0, to: 0 },
          };
        }),
        catchError(this.errorHandler.handleError),
      );
  }

  /** All saved months + full year of one country / Business Call */
  public getExecDashboardKpiEntry(year: number, bcNo: number, country: string): Observable<any[]> {
    const params = { year: String(year), bcNo: String(bcNo), country };
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/execDashboardKpi/entry`, { params })
      .pipe(
        map((res: any) => {
          const result = res?.result ?? res;
          return Array.isArray(result) ? result : [];
        }),
        catchError(this.errorHandler.handleError),
      );
  }

  /** Saves one month + the full-year forecast; a KPI with no values is removed */
  public saveExecDashboardKpiEntry(payload: any): Observable<{ saved: number; deleted: number }> {
    return this.http
      .put<any>(`${this.SURVEY_SERVICE_URL}/execDashboardKpi/save`, payload)
      .pipe(
        map((res: any) => res?.result ?? res),
        catchError(this.errorHandler.handleError),
      );
  }

  public bulkUploadExecDashboardKpis(payload: any): Observable<{
    saved: number;
    errorCount: number;
    errors: string[];
    touched: { year: number; bcNo: number; country: string }[];
  }> {
    return this.http
      .post<any>(`${this.SURVEY_SERVICE_URL}/execDashboardKpi/bulkUpload`, payload)
      .pipe(
        map((res: any) => res?.result ?? res),
        catchError(this.errorHandler.handleError),
      );
  }

  public deleteExecDashboardKpiMonth(year: number, bcNo: number, country: string, month: number): Observable<any> {
    const params = { year: String(year), bcNo: String(bcNo), country, month: String(month) };
    return this.http
      .delete<any>(`${this.SURVEY_SERVICE_URL}/execDashboardKpi/deleteMonth`, { params })
      .pipe(
        map((res: any) => res?.result ?? res),
        catchError(this.errorHandler.handleError),
      );
  }

  public deleteExecDashboardKpi(id: number): Observable<any> {
    return this.http
      .delete<any>(`${this.SURVEY_SERVICE_URL}/execDashboardKpi/delete/${id}`)
      .pipe(
        map((res: any) => res?.result ?? res),
        catchError(this.errorHandler.handleError),
      );
  }
  // ===================== STORE MAPPING APIs =====================

  public getRetailerIdsByAccount(accountId: number): Observable<any> {
    let params = new HttpParams().set('accountId', accountId.toString());
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/storeMapping/getRetailerIdsByAccount`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public validateStoreMapping(
    data: any[],
    countryId?: number,
    accountId?: number,
    retailerId?: string,
  ): Observable<any> {
    let params = new HttpParams();
    if (countryId) {
      params = params.set('countryId', countryId.toString());
    }
    if (accountId) {
      params = params.set('accountId', accountId.toString());
    }
    if (retailerId) {
      params = params.set('retailerId', retailerId.toString());
    }
    return this.http
      .post<any>(`${this.SURVEY_SERVICE_URL}/storeMapping/validate`, data, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public bulkUploadStoreMapping(
    data: any[],
    countryId?: number,
    accountId?: number,
    retailerId?: string,
  ): Observable<any> {
    let params = new HttpParams();
    if (countryId) {
      params = params.set('countryId', countryId.toString());
    }
    if (accountId) {
      params = params.set('accountId', accountId.toString());
    }
    if (retailerId) {
      params = params.set('retailerId', retailerId.toString());
    }
    return this.http
      .post<any>(`${this.SURVEY_SERVICE_URL}/storeMapping/bulkUpload`, data, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getStoreMappings(
    pageIndex: number = 0,
    pageSize: number = 10,
    countryId?: number[],
    regionId?: number[],
    account?: number[],
    search?: string,
    storeId?: number[],
  ): Observable<any> {
    let params = new HttpParams();
    if (pageIndex !== undefined) {
      params = params.set('pageIndex', pageIndex.toString());
    }
    if (pageSize !== undefined) {
      params = params.set('pageSize', pageSize.toString());
    }
    if (countryId && countryId.length > 0) {
      params = params.set('countryId', countryId.join(','));
    }
    if (regionId && regionId.length > 0) {
      params = params.set('regionId', regionId.join(','));
    }
    if (account && account.length > 0) {
      params = params.set('account', account.join(','));
    }
    if (storeId && storeId.length > 0) {
      params = params.set('storeId', storeId.join(','));
    }
    if (search && search.trim() !== '') {
      params = params.set('search', search.trim());
    }
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/storeMapping/getAll`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public downloadStoreMappingTemplate(
    countryId?: number,
    accountId?: number,
    retailerId?: string,
  ): Observable<Blob> {
    let params = new HttpParams();
    if (countryId) {
      params = params.set('countryId', countryId.toString());
    }
    if (accountId) {
      params = params.set('accountId', accountId.toString());
    }
    if (retailerId) {
      params = params.set('retailerId', retailerId.toString());
    }
    return this.http
      .get(`${this.SURVEY_SERVICE_URL}/storeMapping/downloadExcelTemplate`, {
        params,
        responseType: 'blob',
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteExecDashboardFile(id: number): Observable<void> {
    return this.http
      .delete<any>(`${this.SURVEY_SERVICE_URL}/execDashboardUpload/delete/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteStoreMapping(id: number): Observable<any> {
    return this.http
      .post<any>(`${this.SURVEY_SERVICE_URL}/storeMapping/deleteRecord`, { id })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public saveStoreMapping(data: any): Observable<any> {
    return this.http
      .post<any>(`${this.SURVEY_SERVICE_URL}/storeMapping/saveRecord`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  // ═════════════════════════════════════════════════════════════════
  // PRODUCT MAPPING
  // ═════════════════════════════════════════════════════════════════

  public validateProductMappingBulk(
    data: any[],
    countryId?: number,
    accountId?: number,
    retailerId?: string,
  ): Observable<any> {
    let params = new HttpParams();
    if (countryId) {
      params = params.set('countryId', countryId.toString());
    }
    if (accountId) {
      params = params.set('accountId', accountId.toString());
    }
    if (retailerId) {
      params = params.set('retailerId', retailerId.toString());
    }
    return this.http
      .post<any>(`${this.SURVEY_SERVICE_URL}/productMapping/validate`, data, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public bulkUploadProductMapping(
    data: any[],
    countryId?: number,
    accountId?: number,
    retailerId?: string,
  ): Observable<any> {
    let params = new HttpParams();
    if (countryId) {
      params = params.set('countryId', countryId.toString());
    }
    if (accountId) {
      params = params.set('accountId', accountId.toString());
    }
    if (retailerId) {
      params = params.set('retailerId', retailerId.toString());
    }
    return this.http
      .post<any>(`${this.SURVEY_SERVICE_URL}/productMapping/bulkUpload`, data, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getProductMappings(
    pageIndex: number = 0,
    pageSize: number = 10,
    countryId?: number[],
    regionId?: number[],
    account?: number[],
    search?: string,
    productId?: number[],
  ): Observable<any> {
    let params = new HttpParams();
    if (pageIndex !== undefined) {
      params = params.set('pageIndex', pageIndex.toString());
    }
    if (pageSize !== undefined) {
      params = params.set('pageSize', pageSize.toString());
    }
    if (countryId && countryId.length > 0) {
      params = params.set('countryId', countryId.join(','));
    }
    if (regionId && regionId.length > 0) {
      params = params.set('regionId', regionId.join(','));
    }
    if (account && account.length > 0) {
      params = params.set('account', account.join(','));
    }
    if (productId && productId.length > 0) {
      params = params.set('productId', productId.join(','));
    }
    if (search && search.trim() !== '') {
      params = params.set('search', search.trim());
    }
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/productMapping/getAll`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public downloadProductMappingTemplate(
    countryId?: number,
    accountId?: number,
    retailerId?: string,
  ): Observable<Blob> {
    let params = new HttpParams();
    if (countryId) {
      params = params.set('countryId', countryId.toString());
    }
    if (accountId) {
      params = params.set('accountId', accountId.toString());
    }
    if (retailerId) {
      params = params.set('retailerId', retailerId.toString());
    }
    return this.http
      .get(`${this.SURVEY_SERVICE_URL}/productMapping/downloadExcelTemplate`, {
        params,
        responseType: 'blob',
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteProductMapping(id: number): Observable<any> {
    return this.http
      .post<any>(`${this.SURVEY_SERVICE_URL}/productMapping/deleteRecord`, { id })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public saveProductMapping(data: any): Observable<any> {
    return this.http
      .post<any>(`${this.SURVEY_SERVICE_URL}/productMapping/saveRecord`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getProductsByCountry(countryId?: number): Observable<any> {
    let params = new HttpParams();
    if (countryId) {
      params = params.set('countryId', countryId.toString());
    }
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/productMapping/productsByCountry`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getChinaSurveyQuestions(surveyType?: string): Observable<any> {
    let params = new HttpParams();
    if (surveyType) {
      params = params.set('surveyType', surveyType);
    }
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/chinaSurvey/getAllQuestions`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getChinaSurveySubmissionDetails(filters: any): Observable<any> {
    let params = new HttpParams();
    if (filters?.pageIndex != null) params = params.set('pageIndex', filters.pageIndex.toString());
    if (filters?.pageSize != null) params = params.set('pageSize', filters.pageSize.toString());
    if (filters?.surveyType != null) params = params.set('surveyType', filters.surveyType.toString());
    if (filters?.status) params = params.set('status', filters.status);
    if (filters?.userId != null) params = params.set('userId', filters.userId.toString());
    if (filters?.submissionId != null) params = params.set('submissionId', filters.submissionId.toString());
    if (filters?.storeName) params = params.set('storeName', filters.storeName);
    if (filters?.storeId != null) params = params.set('storeId', filters.storeId.toString());
    if (filters?.storeIds) params = params.set('storeIds', Array.isArray(filters.storeIds) ? filters.storeIds.join(',') : filters.storeIds.toString());
    if (filters?.countryId != null) params = params.set('countryId', filters.countryId.toString());
    if (filters?.countryIds) params = params.set('countryIds', Array.isArray(filters.countryIds) ? filters.countryIds.join(',') : filters.countryIds.toString());
    if (filters?.regionId != null) params = params.set('regionId', filters.regionId.toString());
    if (filters?.regionIds) params = params.set('regionIds', Array.isArray(filters.regionIds) ? filters.regionIds.join(',') : filters.regionIds.toString());
    if (filters?.fromDate) params = params.set('fromDate', filters.fromDate);
    if (filters?.toDate) params = params.set('toDate', filters.toDate);
    if (filters?.groupBySubmission != null) {
      params = params.set('groupBySubmission', String(filters.groupBySubmission));
    }
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/chinaSurvey/getAllSubmissionDetails`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public downloadChinaSurveyExcel(filters: any): Observable<Blob> {
    let params = new HttpParams();
    if (filters?.surveyType != null) params = params.set('surveyType', filters.surveyType.toString());
    if (filters?.status) params = params.set('status', filters.status);
    if (filters?.userId != null) params = params.set('userId', filters.userId.toString());
    if (filters?.storeName) params = params.set('storeName', filters.storeName);
    if (filters?.storeId != null) params = params.set('storeId', filters.storeId.toString());
    if (filters?.storeIds) params = params.set('storeIds', Array.isArray(filters.storeIds) ? filters.storeIds.join(',') : filters.storeIds.toString());
    if (filters?.countryId != null) params = params.set('countryId', filters.countryId.toString());
    if (filters?.countryIds) params = params.set('countryIds', Array.isArray(filters.countryIds) ? filters.countryIds.join(',') : filters.countryIds.toString());
    if (filters?.regionId != null) params = params.set('regionId', filters.regionId.toString());
    if (filters?.regionIds) params = params.set('regionIds', Array.isArray(filters.regionIds) ? filters.regionIds.join(',') : filters.regionIds.toString());
    if (filters?.fromDate) params = params.set('fromDate', filters.fromDate);
    if (filters?.toDate) params = params.set('toDate', filters.toDate);
    return this.http
      .get(`${this.SURVEY_SERVICE_URL}/chinaSurvey/chinaSurveySubmission/downloadExcelReport`, {
        params,
        responseType: 'blob',
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public downloadChinaSurveyCrmExcel(): Observable<Blob> {
    return this.http
      .get(`${this.SURVEY_SERVICE_URL}/chinaSurvey/chinaSurveyCRMReports/downloadExcelReport`, {
        responseType: 'blob',
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getChinaSurveyStepOneDetail(storeName: string, date?: string): Observable<any> {
    let params = new HttpParams().set('storeName', storeName);
    if (date) {
      params = params.set('date', date);
    }
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/chinaSurvey/getStepOneDetail`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getChinaSurveyAllSubmissions(filters?: any): Observable<any> {
    let params = new HttpParams();
    if (filters?.currentDate) params = params.set('currentDate', filters.currentDate);
    if (filters?.date) params = params.set('date', filters.date);
    if (filters?.storeName) params = params.set('storeName', filters.storeName);
    if (filters?.storeId) params = params.set('storeId', String(filters.storeId));
    if (filters?.surveyType) params = params.set('surveyType', filters.surveyType);
    if (filters?.status) params = params.set('status', filters.status);
    if (filters?.userId != null) params = params.set('userId', filters.userId.toString());
    if (filters?.pageIndex != null) params = params.set('pageIndex', filters.pageIndex.toString());
    if (filters?.pageSize != null) params = params.set('pageSize', filters.pageSize.toString());
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/chinaSurvey/getAllSubmissions`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }
}

